/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Nav,
  NavItem,
  NavLink,
  Row,
  Col,
  Table,
  Input,
  FormGroup,
  Label,
  InputGroup,
  InputGroupAddon,
  Alert,
  Collapse
} from "reactstrap";
import Select from "react-select";
import TSUser from "components/Forms/TSUser";
import moment from "moment";
import "moment/locale/th";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import {
  isStayActive,
  isStayFuture,
  getTraineeTrainingPeriodStatus,
  parseThaiDateRange,
  formatThaiDateRangeText,
  countDaysBetween,
  calculateDormFeeByDays,
  getStayDateRange,
  checkDateRangesOverlap
} from "../Trainee/dormitoryFeeCalculator.js";
import { checkDormitoryMasterDataPermission } from "./dormitoryAuth.js";
import Dormitory3DBuildingView from "./Dormitory3DBuildingView.js";
import {
  ROOM_CATEGORIES,
  ROOM_CATEGORY_LIST,
  getRoomCategoryKey,
  getRoomCategoryInfo,
  applyRoomCategory
} from "./dormitoryRoomCategoryHelper.js";

// Icons
import HotelIcon from "@material-ui/icons/Hotel";
import MeetingRoomIcon from "@material-ui/icons/MeetingRoom";
import PeopleIcon from "@material-ui/icons/People";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import PrintIcon from "@material-ui/icons/Print";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import RefreshIcon from "@material-ui/icons/Refresh";
import StorageIcon from "@material-ui/icons/Storage";
import SearchIcon from "@material-ui/icons/Search";
import FilterListIcon from "@material-ui/icons/FilterList";
import BusinessIcon from "@material-ui/icons/Business";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

moment.locale("th");

const useStyles = makeStyles(() => ({
  roomCard: {
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "16px",
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: "260px",
    "&:hover": {
      boxShadow: "0 12px 28px -6px rgba(0,0,0,0.1), 0 8px 12px -6px rgba(0,0,0,0.04)",
      transform: "translateY(-3px)",
      borderColor: "#cbd5e1"
    }
  },
  floorHeader: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    padding: "10px 16px",
    borderRadius: "10px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "14px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  statCard: {
    backgroundColor: "#ffffff",
    borderRadius: "14px",
    padding: "16px 18px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
    cursor: "pointer",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 10px 25px -5px rgba(0,0,0,0.08), 0 8px 10px -6px rgba(0,0,0,0.03)"
    }
  }
}));

const THAI_MONTHS = [
  { value: 1, label: "มกราคม" },
  { value: 2, label: "กุมภาพันธ์" },
  { value: 3, label: "มีนาคม" },
  { value: 4, label: "เมษายน" },
  { value: 5, label: "พฤษภาคม" },
  { value: 6, label: "มิถุนายน" },
  { value: 7, label: "กรกฎาคม" },
  { value: 8, label: "สิงหาคม" },
  { value: 9, label: "กันยายน" },
  { value: 10, label: "ตุลาคม" },
  { value: 11, label: "พฤศจิกายน" },
  { value: 12, label: "ธันวาคม" }
];

const DEFAULT_BUILDING_LIST = [
  { id: 1, dormitoryID: 1, name: "ตึก 1 (หอพักเจ้าหน้าที่ นักศึกษาผู้หญิง)", shortName: "ตึก 1", buildingNo: "1", note: "ห้อง 111 - 134", totalFloors: 3 },
  { id: 2, dormitoryID: 2, name: "ตึก 2 (หอพักแพทย์)", shortName: "ตึก 2", buildingNo: "2", note: "ห้อง 211 - 234", totalFloors: 3 },
  { id: 3, dormitoryID: 3, name: "ตึก 3 (หอพักบุคลากร / นศ.หญิง)", shortName: "ตึก 3", buildingNo: "3", note: "ห้อง 311 - 334", totalFloors: 3 },
  { id: 4, dormitoryID: 4, name: "ตึก 4 (หอพักรับรองพิเศษ / นศ.ชาย)", shortName: "ตึก 4", buildingNo: "4", note: "ห้อง 411 - 434", totalFloors: 3 }
];

const DEFAULT_RATE_PER_UNIT = 8.0;

const getStoredElectricityRate = () => {
  try {
    const saved = localStorage.getItem("dorm_electricity_rate");
    if (saved && !isNaN(Number(saved)) && Number(saved) > 0) {
      return Number(saved);
    }
  } catch (e) {
    // ignore
  }
  return DEFAULT_RATE_PER_UNIT;
};

// ฟังก์ชันสร้างรายชื่อผู้เข้าพักที่เป็นไปได้จากข้อมูลแพทย์และนักศึกษา
const buildCandidateList = (apiDoctors = [], apiStudents = []) => {
  const list = [];
  const seenIds = new Set();

  // 1. แพทย์ฝึกอบรม / ดูงาน
  const docs = (apiDoctors && apiDoctors.length > 0) ? apiDoctors : [];
  docs.forEach((d) => {
    const rawId = d.id || d.doctorID || d.DoctorID || `doc-${d.fullNameTH || d.nameTH}`;
    const idKey = String(rawId);
    if (!seenIds.has(idKey)) {
      seenIds.add(idKey);
      list.push({
        id: idKey,
        type: "DOCTOR",
        name: d.nameTH || d.fullNameTH || d.FullNameTH || d.name || "",
        position: d.position || d.Position || d.doctorPositionType || d.doctorType || "นายแพทย์ปฏิบัติงาน",
        institution: d.institution || d.institutionName || d.InstitutionName || "สถาบันโรคผิวหนัง",
        department: d.department || d.departmentName || d.DepartmentName || "",
        phone: d.phone || d.Phone || "",
        startDate: (d.startDate || d.StartDate || "").toString().split("T")[0],
        endDate: (d.endDate || d.EndDate || "").toString().split("T")[0],
        dateRange: d.dateRange || d.dateRangeText || d.DateRangeText || "",
        fiscalYear: d.fiscalYear || d.FiscalYear || 2569,
        statusKey: d.statusKey ?? d.StatusKey,
        statusText: d.statusText || d.StatusText || d.status || "",
        requiresAccommodation: d.requiresAccommodation ?? d.RequiresAccommodation ?? false,
        dormFee: d.dormFee || (d.doctorPositionType && d.doctorPositionType.includes("elective") ? "วันละ 100 บาท" : "500 บาท"),
        roomNumber: d.roomNumber || d.assignedRoomNumber || d.AssignedRoomNumber || ""
      });
    }
  });

  // 2. นักศึกษาฝึกงาน
  const stus = (apiStudents && apiStudents.length > 0) ? apiStudents : [];
  stus.forEach((s) => {
    const rawId = s.id || s.studentID || s.StudentID || `std-${s.fullName || s.name}`;
    const idKey = String(rawId);
    if (!seenIds.has(idKey)) {
      seenIds.add(idKey);
      list.push({
        id: idKey,
        type: "STUDENT",
        name: s.name || s.fullName || s.FullName || "",
        position: s.role || s.level || s.positionLevel || s.traineeRole || "นศ.ฝึกงาน",
        institution: s.institution || s.institutionName || s.InstitutionName || "",
        department: s.department || s.departmentName || s.DepartmentName || "",
        phone: s.phone || s.Phone || "",
        startDate: (s.startDate || s.StartDate || "").toString().split("T")[0],
        endDate: (s.endDate || s.EndDate || "").toString().split("T")[0],
        dateRange: s.dateRange || s.dateRangeText || s.DateRangeText || "",
        fiscalYear: s.fiscalYear || s.FiscalYear || 2569,
        statusKey: s.statusKey ?? s.StatusKey,
        statusText: s.statusText || s.StatusText || s.status || "",
        requiresAccommodation: s.requiresAccommodation ?? s.RequiresAccommodation ?? false,
        dormFee: s.dormFee ? (s.dormFee.includes("บาท") ? s.dormFee : `${s.dormFee} บาท`) : "500 บาท",
        roomNumber: s.roomNumber || s.assignedRoomNumber || s.AssignedRoomNumber || ""
      });
    }
  });

  return list;
};

export default function DormitoryStaffManagement() {
  const classes = useStyles();
  const history = useHistory();
  const canManageMasterData = checkDormitoryMasterDataPermission();
  const [activeTab, setActiveTab] = useState(0); // 0 = ผังห้องพัก (Floor Grid), 1 = ทะเบียนผู้เข้าพัก, 2 = จดมิเตอร์ค่าไฟ

  // Data states
  const [buildings, setBuildings] = useState(DEFAULT_BUILDING_LIST);
  const [rooms, setRooms] = useState([]);
  const [stays, setStays] = useState([]);
  const [staffCandidates, setStaffCandidates] = useState([]);
  const [traineeCandidates, setTraineeCandidates] = useState([]);
  const [onlyActiveOrFuture, setOnlyActiveOrFuture] = useState(true);
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [electricityList, setElectricityList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ show: false, color: "success", message: "" });

  // View mode: '3D' (Default approved 3D Isometric View) | 'GRID' (Classic Floor Grid)
  const [viewMode, setViewMode] = useState("3D");

  // Filters - Floor Grid tab
  const [selectedBuilding, setSelectedBuilding] = useState("ALL"); // "ALL", "ตึก 1", "ตึก 2", "ตึก 3", "ตึก 4"
  const [floorFilter, setFloorFilter] = useState("ALL"); // "ALL", "3", "2", "1"
  const [roomTargetFilter, setRoomTargetFilter] = useState("ALL"); // "ALL", "STAFF", "STUDENT"
  const [roomOccupancyFilter, setRoomOccupancyFilter] = useState("ALL"); // "ALL", "ACTIVE", "VACANT"

  // Filters - Residents tab
  const [residentSearch, setResidentSearch] = useState("");
  const [residentTypeFilter, setResidentTypeFilter] = useState("ALL");
  const [residentStatusFilter, setResidentStatusFilter] = useState("ACTIVE"); // 'ACTIVE' | 'ALL' | 'CHECKED_OUT'

  // Electricity state
  const currentYearCE = new Date().getFullYear();
  const currentMonthNum = new Date().getMonth() + 1;
  const [billingYear, setBillingYear] = useState(currentYearCE);
  const [billingMonth, setBillingMonth] = useState(currentMonthNum);
  const [electricityBuildingFilter, setElectricityBuildingFilter] = useState(0);
  const [electricityRate, setElectricityRate] = useState(getStoredElectricityRate);
  const [electricityDataMap, setElectricityDataMap] = useState({});
  const [electricityOccupancyFilter, setElectricityOccupancyFilter] = useState("OCCUPIED_ONLY"); // "OCCUPIED_ONLY" or "ALL"
  const [batchSaving, setBatchSaving] = useState(false);

  // Modals
  const [editRoomModal, setEditRoomModal] = useState({ open: false, room: null });
  const [checkInModal, setCheckInModal] = useState({ open: false, defaultRoomId: 0 });
  const [checkOutModal, setCheckOutModal] = useState({ open: false, stay: null });
  const [editStayModal, setEditStayModal] = useState({
    open: false,
    stayId: "",
    stay: null,
    room: null,
    residentName: "",
    positionText: "",
    departmentName: "",
    roomNumber: "",
    buildingName: "",
    checkInDate: "",
    expectedCheckOutDate: "",
    phone: "",
    feeRateType: "MONTHLY",
    freeElectricityUnits: 0,
    remarks: "",
    coResidents: "",
    isSubmitting: false
  });
  const [billPrintModal, setBillPrintModal] = useState({ open: false, bill: null });

  // Inline TSUser selector collapse states
  const [staffSelectorOpen, setStaffSelectorOpen] = useState(true);
  const [coResidentSelectorOpen, setCoResidentSelectorOpen] = useState(false);

  // Form states - Check-in
  const [checkInForm, setCheckInForm] = useState({
    residentCategory: "STAFF", // 'STAFF' | 'STUDENT'
    selectedStaff: null,
    traineeName: "",
    traineeType: "DOCTOR",
    positionText: "",
    departmentName: "",
    phone: "",
    citizenID: "",
    staffCode: "",
    doctorID: null,
    studentID: null,
    selectedCandidate: null,
    building: "ตึก 1",
    floor: 1,
    roomNo: "111",
    roomId: 0,
    checkInDate: moment().format("YYYY-MM-DD"),
    expectedCheckOutDate: "",
    dateRange: "",
    feeRateType: "MONTHLY",
    feeAmount: 500,
    freeElectricityUnits: 0,
    paymentStatus: "PAID",
    remarks: "",
    isFamilyStay: false,
    coResidents: "",
    coResidentStaff: [],
    familyNotes: ""
  });

  // Form state - Check-out
  const [checkOutForm, setCheckOutForm] = useState({
    checkOutDate: moment().format("YYYY-MM-DD"),
    remarks: "สภาพห้องเรียบร้อย คืนกุญแจแล้ว"
  });

  const handleSelectStaffFromTSUser = (selectedList) => {
    if (!selectedList || selectedList.length === 0) return;
    const usr = selectedList[0];
    const fullName = `${usr.prefix || ""}${usr.firstName || ""} ${usr.lastName || ""}`.trim();
    const pos = usr.positionName || usr.position || "";
    const dept = usr.departmentName || usr.groupWorkName || usr.missionName || "";
    const phone = usr.phone || usr.tel || "";

    setCheckInForm((prev) => ({
      ...prev,
      selectedStaff: usr,
      traineeName: fullName,
      positionText: pos,
      departmentName: dept,
      phone: phone,
      citizenID: usr.citizenID || "",
      staffCode: usr.staffCode || usr.userName || ""
    }));
    setStaffSelectorOpen(false); // ซ่อนหลังจากเลือกคนแล้ว
    showAlert(`เลือกเจ้าหน้าที่: ${fullName} เรียบร้อยแล้ว`, "info");
  };

  const handleSelectCoResidentStaffFromTSUser = (selectedList) => {
    if (!selectedList || selectedList.length === 0) return;
    const usr = selectedList[0];
    const fullName = `${usr.prefix || ""}${usr.firstName || ""} ${usr.lastName || ""}`.trim();

    setCheckInForm((prev) => {
      const existing = prev.coResidentStaff || [];
      if (existing.some((s) => s.userName === usr.userName)) {
        return prev;
      }
      const updatedStaff = [...existing, usr];
      const staffNames = updatedStaff.map(
        (s) => `${s.prefix || ""}${s.firstName} ${s.lastName} (${s.positionName || s.position || "จนท."})`
      );
      const notes = prev.familyNotes ? `, ${prev.familyNotes}` : "";
      return {
        ...prev,
        coResidentStaff: updatedStaff,
        coResidents: `${staffNames.join(", ")}${notes}`
      };
    });
    showAlert(`เพิ่มผู้พักร่วม: ${fullName} เรียบร้อยแล้ว`, "info");
  };

  const handleRemoveCoResidentStaff = (userName) => {
    setCheckInForm((prev) => {
      const updatedStaff = (prev.coResidentStaff || []).filter((s) => s.userName !== userName);
      const staffNames = updatedStaff.map(
        (s) => `${s.prefix || ""}${s.firstName} ${s.lastName} (${s.positionName || s.position || "จนท."})`
      );
      const notes = prev.familyNotes ? (staffNames.length > 0 ? `, ${prev.familyNotes}` : prev.familyNotes) : "";
      return {
        ...prev,
        coResidentStaff: updatedStaff,
        coResidents: `${staffNames.join(", ")}${notes}`
      };
    });
  };

  const handleFamilyNotesChange = (text) => {
    setCheckInForm((prev) => {
      const staffNames = (prev.coResidentStaff || []).map(
        (s) => `${s.prefix || ""}${s.firstName} ${s.lastName} (${s.positionName || s.position || "จนท."})`
      );
      const combined = staffNames.length > 0
        ? (text ? `${staffNames.join(", ")}, ${text}` : staffNames.join(", "))
        : text;
      return {
        ...prev,
        familyNotes: text,
        coResidents: combined
      };
    });
  };

  const showAlert = (message, color = "success") => {
    setAlertInfo({ show: true, color, message });
    setTimeout(() => {
      setAlertInfo({ show: false, color: "success", message: "" });
    }, 4500);
  };

  // 1. Load Initial Data
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [resBuildings, resRooms, resStays, resStaff, doctorsRes, studentsRes, resUserView] = await Promise.allSettled([
        axios.get("/api/v1/dormitory/buildings"),
        axios.get("/api/v1/dormitory/rooms"),
        axios.get("/api/v1/dormitory/stays"),
        axios.get("/api/v1/dormitory/staff-candidates"),
        axios.get("/api/v1/trainee/doctors"),
        axios.get("/api/v1/trainee/students"),
        axios.get("/api/v1/userview?isShowAll=true")
      ]);

      if (resBuildings.status === "fulfilled" && resBuildings.value?.data && resBuildings.value.data.success && resBuildings.value.data.data?.length > 0) {
        const mapped = resBuildings.value.data.data.map((b) => ({
          id: b.dormitoryID,
          dormitoryID: b.dormitoryID,
          name: b.dormitoryID === 1 ? "อาคาร 1 (หอพักเจ้าหน้าที่ นักศึกษาผู้หญิง)" : b.dormitoryName,
          shortName: `ตึก ${b.buildingNo}`,
          buildingNo: String(b.buildingNo),
          note: b.description || `ตึก ${b.buildingNo} 3 ชั้น`,
          totalFloors: 3
        }));
        setBuildings(mapped);
      }

      const rawStays = (resStays.status === "fulfilled" && resStays.value?.data && resStays.value.data.success)
        ? (resStays.value.data.data || [])
        : [];
      
      const normalizedStays = rawStays.map((s) => ({
        ...s,
        StayID: s.stayID ?? s.StayID,
        stayID: s.stayID ?? s.StayID,
        TraineeName: s.traineeName ?? s.TraineeName,
        traineeName: s.traineeName ?? s.TraineeName,
        StaffCode: s.staffCode ?? s.StaffCode,
        staffCode: s.staffCode ?? s.StaffCode,
        PositionText: s.positionText ?? s.PositionText,
        positionText: s.positionText ?? s.PositionText,
        DepartmentName: s.departmentName ?? s.DepartmentName,
        departmentName: s.departmentName ?? s.DepartmentName,
        Institution: s.institutionName ?? s.institution ?? s.Institution,
        BuildingName: s.buildingName ?? s.BuildingName ?? `ตึก ${s.buildingNo || s.dormitoryID}`,
        buildingName: s.buildingName ?? s.BuildingName ?? `ตึก ${s.buildingNo || s.dormitoryID}`,
        BuildingNo: String(s.buildingNo ?? s.BuildingNo ?? s.dormitoryID ?? s.DormitoryID ?? ""),
        buildingNo: String(s.buildingNo ?? s.BuildingNo ?? s.dormitoryID ?? s.DormitoryID ?? ""),
        RoomNumber: String(s.roomNumber ?? s.RoomNumber ?? ""),
        roomNumber: String(s.roomNumber ?? s.RoomNumber ?? ""),
        RoomID: s.roomID ?? s.RoomID,
        roomID: s.roomID ?? s.RoomID,
        Floor: Number(s.floor ?? s.Floor ?? 1),
        floor: Number(s.floor ?? s.Floor ?? 1),
        CheckInDate: s.checkInDate ?? s.CheckInDate,
        checkInDate: s.checkInDate ?? s.CheckInDate,
        CheckOutDate: s.checkOutDate ?? s.CheckOutDate,
        checkOutDate: s.checkOutDate ?? s.CheckOutDate,
        IsCurrentStay: Boolean(s.isCurrentStay ?? s.IsCurrentStay),
        isCurrentStay: Boolean(s.isCurrentStay ?? s.IsCurrentStay),
        TraineeType: s.traineeType ?? s.TraineeType ?? "STAFF",
        traineeType: s.traineeType ?? s.TraineeType ?? "STAFF",
        CoResidents: s.coResidents ?? s.CoResidents,
        coResidents: s.coResidents ?? s.CoResidents,
        ContactPhone: s.contactPhone ?? s.ContactPhone ?? "",
        contactPhone: s.contactPhone ?? s.ContactPhone ?? "",
        FeeRateType: s.feeRateType ?? s.FeeRateType ?? "MONTHLY",
        feeRateType: s.feeRateType ?? s.FeeRateType ?? "MONTHLY",
        FeeAmount: Number(s.feeAmount ?? s.FeeAmount ?? 0),
        feeAmount: Number(s.feeAmount ?? s.FeeAmount ?? 0),
        Remarks: s.remarks ?? s.Remarks ?? "",
        remarks: s.remarks ?? s.Remarks ?? "",
        StayDateRangeText: s.stayDateRangeText ?? s.StayDateRangeText ?? "",
        stayDateRangeText: s.stayDateRangeText ?? s.StayDateRangeText ?? ""
      }));
      setStays(normalizedStays);

      const activeStays = normalizedStays.filter((s) => s.isCurrentStay || s.IsCurrentStay);

      if (resRooms.status === "fulfilled" && resRooms.value?.data && resRooms.value.data.success) {
        const rawRooms = resRooms.value.data.data || [];
        const normalizedRooms = rawRooms.map((r) => {
          const rId = r.roomID ?? r.RoomID;
          const rNo = String(r.roomNumber ?? r.RoomNumber ?? "");
          const rawBNo = String(r.buildingNo ?? r.BuildingNo ?? r.dormitoryID ?? r.DormitoryID ?? (rNo ? rNo.substring(0, 1) : "1"));
          const bNo = rawBNo.replace(/\D+/g, "") || (rNo ? rNo.substring(0, 1) : "1");
          
          // Match active stays for this room
          const roomStays = activeStays.filter(
            (s) => (s.roomID === rId || s.RoomID === rId) ||
                   (String(s.roomNumber || s.RoomNumber) === rNo && String(s.buildingNo || s.BuildingNo || s.dormitoryID) === bNo)
          );

          const residents = (r.currentResidents && r.currentResidents.length > 0)
            ? r.currentResidents.map((cr) => {
                const fullStay = activeStays.find((s) => (s.StayID === cr.StayID || s.stayID === cr.stayID || s.StayID === cr.stayId));
                return fullStay ? { ...cr, ...fullStay } : cr;
              })
            : roomStays;

          let familyMemberCount = 0;
          residents.forEach((res) => {
            const coRaw = res.CoResidents || res.coResidents || "";
            if (coRaw && typeof coRaw === "string") {
              const members = coRaw.split(/[,;\n]+/).map((m) => m.trim()).filter(Boolean);
              familyMemberCount += members.length;
            }
          });

          const totalOccupants = residents.length + familyMemberCount;
          const bedCap = Number(r.bedCapacity ?? r.BedCapacity ?? 2);
          const currentOcc = totalOccupants;
          const isOcc = currentOcc > 0;

          return {
            ...r,
            RoomID: rId,
            roomID: rId,
            DormitoryID: Number(bNo) || 1,
            dormitoryID: Number(bNo) || 1,
            BuildingNo: bNo,
            buildingNo: bNo,
            BuildingName: (bNo === "1" || r.dormitoryID === 1) ? "อาคาร 1 (หอพักเจ้าหน้าที่ นักศึกษาผู้หญิง)" :
                          (bNo === "2" || r.dormitoryID === 2) ? "อาคาร 2 (หอพักแพทย์)" :
                          (bNo === "3" || r.dormitoryID === 3) ? "อาคาร 3 (หอพักบุคลากร / ครอบครัว)" :
                          (bNo === "4" || r.dormitoryID === 4) ? "อาคาร 4 (หอพักเจ้าหน้าที่ นักศึกษาชาย)" :
                          (r.buildingName ?? r.BuildingName ?? `ตึก ${bNo}`),
            buildingName: (bNo === "1" || r.dormitoryID === 1) ? "อาคาร 1 (หอพักเจ้าหน้าที่ นักศึกษาผู้หญิง)" :
                          (bNo === "2" || r.dormitoryID === 2) ? "อาคาร 2 (หอพักแพทย์)" :
                          (bNo === "3" || r.dormitoryID === 3) ? "อาคาร 3 (หอพักบุคลากร / ครอบครัว)" :
                          (bNo === "4" || r.dormitoryID === 4) ? "อาคาร 4 (หอพักเจ้าหน้าที่ นักศึกษาชาย)" :
                          (r.buildingName ?? r.BuildingName ?? `ตึก ${bNo}`),
            RoomNumber: rNo,
            roomNumber: rNo,
            Floor: Number(r.floor ?? r.Floor ?? 1),
            floor: Number(r.floor ?? r.Floor ?? 1),
            BedCapacity: bedCap,
            bedCapacity: bedCap,
            CurrentOccupancy: currentOcc,
            currentOccupancy: currentOcc,
            TotalOccupants: totalOccupants,
            totalOccupants: totalOccupants,
            IsOverCapacity: totalOccupants > bedCap,
            isOverCapacity: totalOccupants > bedCap,
            TargetGroup: r.targetGroup ?? r.TargetGroup ?? "ALL",
            targetGroup: r.targetGroup ?? r.TargetGroup ?? "ALL",
            RoomType: r.roomType ?? r.RoomType ?? "มาตรฐาน",
            roomType: r.roomType ?? r.RoomType ?? "มาตรฐาน",
            Status: isOcc ? "OCCUPIED" : "AVAILABLE",
            status: isOcc ? "OCCUPIED" : "AVAILABLE",
            IsFamilyRoom: Boolean(r.isFamilyRoom ?? r.IsFamilyRoom ?? (Number(bNo) === 3 || String(rNo) === "424")),
            isFamilyRoom: Boolean(r.isFamilyRoom ?? r.IsFamilyRoom ?? (Number(bNo) === 3 || String(rNo) === "424")),
            MonthlyRate: Number(r.monthlyRate ?? r.MonthlyRate ?? 500),
            monthlyRate: Number(r.monthlyRate ?? r.MonthlyRate ?? 500),
            DailyRate: Number(r.dailyRate ?? r.DailyRate ?? 100),
            dailyRate: Number(r.dailyRate ?? r.DailyRate ?? 100),
            CurrentResidents: residents,
            currentResidents: residents
          };
        });
        setRooms(normalizedRooms);
      }

      let mergedStaff = [];
      if (resStaff.status === "fulfilled" && resStaff.value?.data && resStaff.value.data.success) {
        mergedStaff = resStaff.value.data.data || [];
      }

      const usersFromView = (resUserView?.status === "fulfilled" && resUserView.value?.data?.data) ? resUserView.value.data.data : [];
      if (usersFromView.length > 0) {
        mergedStaff = mergedStaff.map((s) => {
          const matched = usersFromView.find((u) => {
            if (s.citizenId && u.cid && String(s.citizenId) === String(u.cid)) return true;
            if (s.empCode && u.userID && String(s.empCode) === String(u.userID)) return true;
            const sName = (s.name || "").replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, "").trim();
            const uName = `${u.firstName || ""} ${u.lastName || ""}`.trim();
            return sName && uName && (sName === uName || sName.includes(uName) || uName.includes(sName));
          });
          return {
            ...s,
            userName: matched?.userName || s.empCode || s.id,
            avatarUrl: `https://e.trangskin.go.th/avatar/${matched?.userName || s.empCode || s.id}.jpg`
          };
        });
      }
      setStaffCandidates(mergedStaff);

      const apiDoctors = (doctorsRes.status === "fulfilled" && doctorsRes.value?.data?.data && doctorsRes.value.data.data.length > 0)
        ? doctorsRes.value.data.data
        : [];
      const apiStudents = (studentsRes.status === "fulfilled" && studentsRes.value?.data?.data && studentsRes.value.data.data.length > 0)
        ? studentsRes.value.data.data
        : [];
      setTraineeCandidates(buildCandidateList(apiDoctors, apiStudents));
    } catch (err) {
      console.error("Error fetching dormitory data:", err);
      showAlert("เกิดข้อผิดพลาดในการโหลดข้อมูล: " + (err.response?.data?.message || err.message), "danger");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // 2. Fetch Electricity Data when tab is active or Year/Month changes
  const fetchElectricity = useCallback(async () => {
    try {
      const res = await axios.get("/api/v1/dormitory/electricity", {
        params: {
          billingYear: billingYear,
          billingMonth: billingMonth,
          dormitoryId: electricityBuildingFilter > 0 ? electricityBuildingFilter : undefined
        }
      });
      if (res.data && res.data.success) {
        setElectricityList(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching electricity:", err);
    }
  }, [billingYear, billingMonth, electricityBuildingFilter]);

  useEffect(() => {
    if (activeTab === 2) {
      fetchElectricity();
    }
  }, [activeTab, fetchElectricity]);

  // Populate Electricity Map when rooms or electricity records change
  useEffect(() => {
    if (activeTab !== 2) return;

    const targetRooms = rooms.filter(
      (r) => electricityBuildingFilter === 0 || r.DormitoryID === electricityBuildingFilter
    );

    const activeDefaultRate = Number(electricityRate) > 0 ? Number(electricityRate) : DEFAULT_RATE_PER_UNIT;
    const map = {};
    targetRooms.forEach((r) => {
      const existing = electricityList.find((e) => e.roomID === r.RoomID);
      const residents = (r.CurrentResidents && r.CurrentResidents.length > 0) ? r.CurrentResidents : [];
      const residentNames = residents.map((x) => x.traineeName).filter(Boolean).join(", ");
      const activeStay = residents.length > 0 ? residents[0] : null;
      const isOccupied = Boolean(
        residents.length > 0 ||
        r.Status === "OCCUPIED" ||
        r.status === "OCCUPIED" ||
        Number(r.CurrentOccupancy) > 0 ||
        Number(r.currentOccupancy) > 0
      );

      // รวมสิทธิ์หน่วยค่าไฟฟรีของผู้พักทุกคนในห้องนี้
      const roomFreeUnits = residents.reduce((sum, res) => {
        const u = Number(res.FreeElectricityUnits ?? res.freeElectricityUnits ?? 0);
        return sum + (isNaN(u) ? 0 : u);
      }, 0);

      if (existing) {
        const pMeter = Number(existing.previousMeter) || 0;
        const cMeter = Number(existing.currentMeter) || 0;
        const units = Math.max(0, cMeter - pMeter);
        const discount = (existing.discountUnits !== undefined && existing.discountUnits !== null && Number(existing.discountUnits) > 0)
          ? Number(existing.discountUnits)
          : roomFreeUnits;
        const netUnits = (existing.netUnits !== undefined && existing.netUnits !== null)
          ? Number(existing.netUnits)
          : Math.max(0, units - discount);
        const rate = Number(existing.ratePerUnit) > 0 ? Number(existing.ratePerUnit) : activeDefaultRate;
        const base = Number(existing.baseFee) || 0;
        const amount = existing.isRecorded && existing.totalAmount !== undefined
          ? Number(existing.totalAmount)
          : (netUnits > 0 ? (netUnits * rate + base) : 0);

        map[r.RoomID] = {
          electricityID: existing.electricityID,
          roomID: r.RoomID,
          roomNumber: r.RoomNumber,
          buildingName: r.BuildingName,
          residents: residents,
          residentName: (residents.length > 1 ? residentNames : (existing.residentName || residentNames)) || "ห้องว่าง",
          residentType: existing.residentType || (activeStay ? activeStay.traineeType : ""),
          previousMeter: pMeter,
          currentMeter: cMeter,
          totalUnits: units,
          discountUnits: discount,
          netUnits: netUnits,
          roomFreeUnits: roomFreeUnits,
          ratePerUnit: rate,
          baseFee: base,
          totalAmount: amount,
          paymentStatus: existing.paymentStatus || "PENDING",
          meterDate: existing.meterDate ? existing.meterDate.split("T")[0] : moment().format("YYYY-MM-DD"),
          notes: existing.notes || "",
          isRecorded: true,
          isOccupied: isOccupied
        };
      } else {
        map[r.RoomID] = {
          electricityID: 0,
          roomID: r.RoomID,
          roomNumber: r.RoomNumber,
          buildingName: r.BuildingName,
          residents: residents,
          residentName: residentNames || "ห้องว่าง",
          residentType: activeStay ? activeStay.traineeType : "",
          previousMeter: 0,
          currentMeter: 0,
          totalUnits: 0,
          discountUnits: roomFreeUnits,
          netUnits: 0,
          roomFreeUnits: roomFreeUnits,
          ratePerUnit: activeDefaultRate,
          baseFee: 0,
          totalAmount: 0,
          paymentStatus: "PENDING",
          meterDate: moment().format("YYYY-MM-DD"),
          notes: "",
          isRecorded: false,
          isOccupied: isOccupied
        };
      }
    });

    const unrecordedRoomIds = targetRooms.filter((r) => !electricityList.some((e) => e.roomID === r.RoomID));
    if (unrecordedRoomIds.length > 0) {
      unrecordedRoomIds.forEach(async (r) => {
        try {
          const lastRes = await axios.get(`/api/v1/dormitory/electricity/last-meter/${r.RoomID}`);
          if (lastRes.data && lastRes.data.success && lastRes.data.lastMeter !== undefined) {
            setElectricityDataMap((prev) => {
              if (!prev[r.RoomID]) return prev;
              const prevMeter = Number(lastRes.data.lastMeter) || 0;
              const cMeter = Number(prev[r.RoomID].currentMeter) || 0;
              const units = Math.max(0, cMeter - prevMeter);
              const disc = Number(prev[r.RoomID].discountUnits) || 0;
              const net = Math.max(0, units - disc);
              const rRate = Number(prev[r.RoomID].ratePerUnit) || activeDefaultRate;
              const rBase = Number(prev[r.RoomID].baseFee) || 0;
              const amt = net > 0 ? (net * rRate + rBase) : 0;
              return {
                ...prev,
                [r.RoomID]: {
                  ...prev[r.RoomID],
                  previousMeter: prevMeter,
                  totalUnits: units,
                  netUnits: net,
                  totalAmount: amt
                }
              };
            });
          }
        } catch (e) {
          // ignore
        }
      });
    }

    setElectricityDataMap(map);
  }, [electricityList, rooms, electricityBuildingFilter, activeTab]);

  // ปรับเปลี่ยนอัตราค่าไฟ และคำนวณใหม่สำหรับห้องที่ยังไม่ได้บันทึก (Unsaved) ทันที
  const handleElectricityRateChange = (val) => {
    const newRate = Number(val);
    setElectricityRate(val === "" ? "" : newRate);
    if (!isNaN(newRate) && newRate > 0) {
      try {
        localStorage.setItem("dorm_electricity_rate", newRate.toString());
      } catch (e) {
        // ignore
      }
      setElectricityDataMap((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((roomId) => {
          const row = { ...next[roomId] };
          // ห้องที่ยังไม่ได้บันทึกในฐานข้อมูล ให้คำนวณใหม่ทันทีตามอัตราใหม่
          if (!row.isRecorded) {
            row.ratePerUnit = newRate;
            const net = Number(row.netUnits) || 0;
            const base = Number(row.baseFee) || 0;
            row.totalAmount = net > 0 ? (net * newRate + base) : 0;
            next[roomId] = row;
          }
        });
        return next;
      });
    }
  };

  // คำนวณค่าไฟใหม่ทุกห้องตามอัตราค่าไฟปัจจุบัน (รวมห้องที่เคยบันทึกแล้ว)
  const handleRecalculateAllElectricity = () => {
    const activeRate = Number(electricityRate) > 0 ? Number(electricityRate) : DEFAULT_RATE_PER_UNIT;
    setElectricityDataMap((prev) => {
      const next = { ...prev };
      let count = 0;
      Object.keys(next).forEach((roomId) => {
        const row = { ...next[roomId] };
        row.ratePerUnit = activeRate;
        const net = Number(row.netUnits) || 0;
        const base = Number(row.baseFee) || 0;
        row.totalAmount = net > 0 ? (net * activeRate + base) : 0;
        next[roomId] = row;
        count++;
      });
      showAlert(`คำนวณค่าไฟใหม่ทุกห้อง (${count} ห้อง) ตามอัตรา ${activeRate} บาท/หน่วย เรียบร้อยแล้ว (กด "💾 บันทึกทั้งหมด" เพื่อเซฟ)`, "info");
      return next;
    });
  };

  // Handle changes in electricity table row
  const handleElectricityChange = (roomId, field, value) => {
    setElectricityDataMap((prev) => {
      const row = { ...prev[roomId] };
      row[field] = value;

      const pMeter = Number(row.previousMeter) || 0;
      const cMeter = Number(row.currentMeter) || 0;
      const units = Math.max(0, cMeter - pMeter);
      const disc = Number(row.discountUnits) || 0;
      const netUnits = Math.max(0, units - disc);
      // หากมีการแก้ไขข้อมูล ให้ใช้อัตราค่าไฟปัจจุบัน (electricityRate) ในการคำนวณใหม่เสมอ
      const activeRate = Number(electricityRate) > 0 ? Number(electricityRate) : (Number(row.ratePerUnit) || DEFAULT_RATE_PER_UNIT);
      const base = Number(row.baseFee) || 0;
      const amount = netUnits > 0 ? (netUnits * activeRate + base) : 0;

      row.ratePerUnit = activeRate;
      row.totalUnits = units;
      row.discountUnits = disc;
      row.netUnits = netUnits;
      row.totalAmount = amount;

      return {
        ...prev,
        [roomId]: row
      };
    });
  };

  // Save single electricity record
  const handleSaveSingleElectricity = async (roomId) => {
    const row = electricityDataMap[roomId];
    if (!row) return;

    try {
      const activeRate = Number(row.ratePerUnit) || Number(electricityRate) || DEFAULT_RATE_PER_UNIT;
      const payload = {
        roomID: row.roomID,
        billingYear: billingYear,
        billingMonth: billingMonth,
        previousMeter: Number(row.previousMeter) || 0,
        currentMeter: Number(row.currentMeter) || 0,
        totalUnits: Number(row.totalUnits) || 0,
        discountUnits: Number(row.discountUnits) || 0,
        netUnits: Number(row.netUnits) || 0,
        ratePerUnit: activeRate,
        baseFee: Number(row.baseFee) || 0,
        totalAmount: Number(row.totalAmount) || 0,
        meterDate: row.meterDate || moment().format("YYYY-MM-DD"),
        residentName: row.residentName || "",
        residentType: row.residentType || "",
        paymentStatus: row.paymentStatus || "PENDING",
        notes: row.notes || ""
      };

      const res = await axios.post("/api/v1/dormitory/electricity", payload);
      if (res.data && res.data.success) {
        showAlert(`บันทึกค่าไฟห้อง ${row.roomNumber} เรียบร้อยแล้ว`, "success");
        fetchElectricity();
      }
    } catch (err) {
      showAlert("เกิดข้อผิดพลาดในการบันทึกค่าไฟ: " + (err.response?.data?.message || err.message), "danger");
    }
  };

  // Batch Save all electricity records
  const handleBatchSaveElectricity = async () => {
    setBatchSaving(true);
    try {
      const activeDefaultRate = Number(electricityRate) > 0 ? Number(electricityRate) : DEFAULT_RATE_PER_UNIT;
      const models = Object.values(electricityDataMap)
        .filter((r) => Number(r.currentMeter) > 0 || r.isRecorded)
        .map((r) => ({
          roomID: r.roomID,
          billingYear: billingYear,
          billingMonth: billingMonth,
          previousMeter: Number(r.previousMeter) || 0,
          currentMeter: Number(r.currentMeter) || 0,
          totalUnits: Number(r.totalUnits) || 0,
          discountUnits: Number(r.discountUnits) || 0,
          netUnits: Number(r.netUnits) || 0,
          ratePerUnit: Number(r.ratePerUnit) || activeDefaultRate,
          baseFee: Number(r.baseFee) || 0,
          totalAmount: Number(r.totalAmount) || 0,
          meterDate: r.meterDate || moment().format("YYYY-MM-DD"),
          residentName: r.residentName || "",
          residentType: r.residentType || "",
          paymentStatus: r.paymentStatus || "PENDING",
          notes: r.notes || ""
        }));

      if (models.length === 0) {
        showAlert("กรุณากรอกเลขมิเตอร์ขณะวัดอย่างน้อย 1 ห้องก่อนบันทึก", "warning");
        setBatchSaving(false);
        return;
      }

      const res = await axios.post("/api/v1/dormitory/electricity/batch", models);
      if (res.data && res.data.success) {
        showAlert(`บันทึกข้อมูลค่าไฟฟ้าทั้งหมด ${res.data.savedCount || models.length} ห้อง สำเร็จแล้ว`, "success");
        fetchElectricity();
      }
    } catch (err) {
      showAlert("เกิดข้อผิดพลาดในการบันทึก Batch: " + (err.response?.data?.message || err.message), "danger");
    } finally {
      setBatchSaving(false);
    }
  };

  // Toggle payment status quickly
  const handleTogglePaymentStatus = async (roomId) => {
    const row = electricityDataMap[roomId];
    if (!row) return;

    const nextStatus = row.paymentStatus === "PAID" ? "PENDING" : "PAID";
    handleElectricityChange(roomId, "paymentStatus", nextStatus);

    if (row.electricityID > 0) {
      try {
        await axios.put(`/api/v1/dormitory/electricity/${row.electricityID}`, {
          paymentStatus: nextStatus,
          paidDate: nextStatus === "PAID" ? moment().format("YYYY-MM-DD") : null
        });
        showAlert(`ปรับปรุงสถานะห้อง ${row.roomNumber} เป็น ${nextStatus === "PAID" ? "ชำระแล้ว" : "ค้างชำระ"}`);
        fetchElectricity();
      } catch (err) {
        showAlert("อัปเดตสถานะไม่สำเร็จ", "danger");
      }
    }
  };

  // Edit Room modal submit
  const handleSaveRoom = async () => {
    if (!editRoomModal.room) return;
    const room = editRoomModal.room;
    const occ = Number(room.CurrentOccupancy ?? (room.CurrentResidents ? room.CurrentResidents.length : 0));
    if (room.Status === "MAINTENANCE" && occ > 0) {
      alert(
        `⚠️ ไม่สามารถปิดปรับปรุงได้ในขณะนี้\n\n` +
        `ห้อง ${room.RoomNumber || ""} ยังมีผู้พักอาศัยอยู่จำนวน ${occ} คน\n` +
        `กรุณาดำเนินการกดย้ายออก (Check-out) ผู้พักให้เรียบร้อยก่อน เพื่อเช็คว่าปิดปรับปรุงจริงไหม`
      );
      return;
    }
    const cap = Number(room.BedCapacity || 2);
    const finalStatus = room.Status === "MAINTENANCE" ? "MAINTENANCE" : (occ >= cap ? "OCCUPIED" : "AVAILABLE");
    const catInfo = getRoomCategoryInfo(room);
    const isFeeApplicable = catInfo.key === "STUDENT" || catInfo.key === "DOCTOR";
    const payload = {
      ...room,
      Status: finalStatus,
      status: finalStatus,
      TargetGroup: catInfo.targetGroup,
      targetGroup: catInfo.targetGroup,
      RoomType: catInfo.roomType,
      roomType: catInfo.roomType,
      IsFamilyRoom: catInfo.isFamilyRoom,
      isFamilyRoom: catInfo.isFamilyRoom,
      MonthlyRate: isFeeApplicable ? (Number(room.MonthlyRate) || 500) : 0,
      monthlyRate: isFeeApplicable ? (Number(room.MonthlyRate) || 500) : 0,
      DailyRate: isFeeApplicable ? (Number(room.DailyRate) || 100) : 0,
      dailyRate: isFeeApplicable ? (Number(room.DailyRate) || 100) : 0
    };
    try {
      const res = await axios.put(`/api/v1/dormitory/rooms/${room.RoomID}`, payload);
      if (res.data && res.data.success) {
        showAlert("ปรับปรุงข้อมูลห้องพักสำเร็จ", "success");
        setEditRoomModal({ open: false, room: null });
        fetchAllData();
      }
    } catch (err) {
      showAlert("เกิดข้อผิดพลาดในการบันทึกห้อง: " + (err.response?.data?.message || err.message), "danger");
    }
  };

  // Quick add 1 bed to room
  const handleQuickAddOneBed = async (room) => {
    if (!room) return;
    const newCap = (room.BedCapacity || 2) + 1;
    try {
      const res = await axios.put(`/api/v1/dormitory/rooms/${room.RoomID}`, {
        ...room,
        BedCapacity: newCap
      });
      if (res.data && res.data.success) {
        showAlert(`เพิ่มเตียงห้อง ${room.RoomNumber} เป็น ${newCap} เตียง เรียบร้อยแล้ว`, "success");
        fetchAllData();
      }
    } catch (err) {
      showAlert("ไม่สามารถเพิ่มเตียงได้: " + (err.response?.data?.message || err.message), "danger");
    }
  };

  // Quick set or increase bed capacity for a room
  const handleSetRoomBedCapacity = async (room, targetCap) => {
    if (!room) return;
    const newCap = Number(targetCap);
    const rId = room.RoomID || room.roomID;
    try {
      const res = await axios.put(`/api/v1/dormitory/rooms/${rId}`, {
        ...room,
        BedCapacity: newCap,
        bedCapacity: newCap
      });
      if (res.data && res.data.success) {
        showAlert(`ปรับจำนวนเตียงห้อง ${room.RoomNumber || room.roomNumber} เป็น ${newCap} เตียง เรียบร้อยแล้ว`, "success");
        fetchAllData();
      }
    } catch (err) {
      showAlert("ไม่สามารถปรับจำนวนเตียงได้: " + (err.response?.data?.message || err.message), "danger");
    }
  };

  // Open manage beds modal
  const handleOpenManageBeds = (room) => {
    if (!room) return;
    setEditRoomModal({ open: true, room: { ...room } });
  };

  // Check-in submit
  const handleCheckInSubmit = async (e) => {
    e.preventDefault();

    let finalRoomId = checkInForm.roomId;
    if ((!finalRoomId || finalRoomId === 0) && currentSelectedRoom) {
      finalRoomId = currentSelectedRoom.RoomID;
    }
    if (!finalRoomId || finalRoomId === 0) {
      showAlert("กรุณาเลือกหรือระบุหมายเลขห้องพักที่ถูกต้อง", "warning");
      return;
    }

    // Check bed capacity against total occupants entering (main resident + family members)
    const targetRoom = rooms.find((r) => r.RoomID === finalRoomId);
    if (targetRoom) {
      const coRaw = checkInForm.coResidents || "";
      const familyCount = coRaw ? coRaw.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean).length : 0;
      const totalEntering = 1 + familyCount;
      const existingOcc = targetRoom.CurrentOccupancy || 0;
      const totalAfter = (existingOcc === 0) ? totalEntering : (existingOcc + totalEntering);
      const bedCap = Number(targetRoom.BedCapacity || 2);
      if (totalAfter > bedCap) {
        showAlert(
          `⚠️ มีผู้พักรวม ${totalAfter} คน (ผู้พักหลัก + สมาชิกครอบครัว ${familyCount} คน) ซึ่งเกินจำนวนเตียงของห้อง (${bedCap} เตียง)! กรุณากดปุ่มเพิ่มจำนวนเตียงเป็น ${totalAfter} เตียงในฟอร์มก่อนทำการบันทึก`,
          "danger"
        );
        return;
      }
    }

    if (checkInForm.residentCategory === "STUDENT" && overlappingOccupantsInfo.isFull) {
      showAlert(`ห้อง ${checkInForm.roomNo} เตียงเต็มแล้ว (${overlappingOccupantsInfo.count}/${overlappingOccupantsInfo.capacity} เตียง) ไม่สามารถบันทึกได้ กรุณาเลือกห้องอื่น`, "danger");
      return;
    }

    let traineeName = checkInForm.traineeName;
    let employeeKey = null;
    let staffCode = "";
    let citizenID = checkInForm.citizenID;
    let positionText = checkInForm.positionText;
    let departmentName = checkInForm.departmentName;

    if (checkInForm.residentCategory === "STAFF") {
      if (!checkInForm.selectedStaff && !traineeName) {
        showAlert("กรุณาเลือกเจ้าหน้าที่หรือระบุชื่อ", "warning");
        return;
      }
      if (checkInForm.selectedStaff) {
        const staff = checkInForm.selectedStaff;
        traineeName = `${staff.prefix || ""} ${staff.firstName || ""} ${staff.lastName || ""}`.trim();
        employeeKey = staff.employeeKey;
        staffCode = staff.staffCode || "";
        citizenID = staff.citizenID || "";
        positionText = staff.position || "";
        departmentName = staff.departmentName || "";
      }
    } else {
      if (!traineeName) {
        showAlert("กรุณากรอกชื่อ-สกุลผู้เข้าพัก", "warning");
        return;
      }
    }

    const isGuid = (val) => typeof val === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);

    const isB2 = checkInForm.building && (checkInForm.building.includes("2") || checkInForm.building.includes("แพทย์"));
    const feeRateType = checkInForm.residentCategory === "STUDENT"
      ? (isB2 ? "DAILY" : "MONTHLY")
      : (checkInForm.feeRateType || "MONTHLY");
    const feeAmount = checkInForm.residentCategory === "STUDENT"
      ? (autoMasterFeeInfo.amount || 500)
      : (currentSelectedRoom?.MonthlyRate || 500);

    const payload = {
      roomID: finalRoomId,
      traineeType: checkInForm.residentCategory === "STAFF" ? "STAFF" : (checkInForm.traineeType || "DOCTOR"),
      doctorID: (checkInForm.doctorID && isGuid(checkInForm.doctorID)) ? checkInForm.doctorID : null,
      studentID: (checkInForm.studentID && isGuid(checkInForm.studentID)) ? checkInForm.studentID : null,
      employeeKey: employeeKey,
      staffCode: staffCode,
      citizenID: citizenID,
      traineeName: traineeName,
      positionText: positionText,
      departmentName: departmentName,
      institution: departmentName || (checkInForm.residentCategory === "STAFF" ? "โรงพยาบาลโรคผิวหนังเขตร้อนภาคใต้" : ""),
      institutionName: departmentName || (checkInForm.residentCategory === "STAFF" ? "โรงพยาบาลโรคผิวหนังเขตร้อนภาคใต้" : ""),
      contactPhone: checkInForm.phone,
      phone: checkInForm.phone,
      coResidents: checkInForm.coResidents || "",
      checkInDate: checkInForm.checkInDate,
      expectedCheckOutDate: checkInForm.expectedCheckOutDate || null,
      stayDateRangeText: checkInForm.dateRange || checkInForm.selectedCandidate?.dateRange || "",
      feeRateType: feeRateType,
      feeAmount: feeAmount,
      freeElectricityUnits: Number(checkInForm.freeElectricityUnits) || 0,
      paymentStatus: checkInForm.residentCategory === "STUDENT" ? (checkInForm.paymentStatus || "PAID") : "PAID",
      remarks: checkInForm.remarks
    };

    try {
      const res = await axios.post("/api/v1/dormitory/stays", payload);
      if (res.data && res.data.success) {
        showAlert(`จัด ${traineeName} เข้าห้องพักสำเร็จ`, "success");
        setCheckInModal({ open: false, defaultRoomId: 0 });
        setSelectedCandidateId("");
        setCheckInForm({
          residentCategory: "STAFF",
          selectedStaff: null,
          traineeName: "",
          traineeType: "DOCTOR",
          positionText: "",
          departmentName: "",
          phone: "",
          citizenID: "",
          staffCode: "",
          doctorID: null,
          studentID: null,
          selectedCandidate: null,
          building: "ตึก 1",
          floor: 1,
          roomNo: "111",
          roomId: 0,
          checkInDate: moment().format("YYYY-MM-DD"),
          expectedCheckOutDate: "",
          dateRange: "",
          feeRateType: "MONTHLY",
          feeAmount: 500,
          freeElectricityUnits: 0,
          paymentStatus: "PAID",
          remarks: "",
          isFamilyStay: false,
          coResidents: "",
          coResidentStaff: [],
          familyNotes: ""
        });
        fetchAllData();
      }
    } catch (err) {
      showAlert("เกิดข้อผิดพลาดในการบันทึก Check-in: " + (err.response?.data?.message || err.message), "danger");
    }
  };

  // Check-out submit
  const handleCheckOutSubmit = async (e) => {
    e.preventDefault();
    if (!checkOutModal.stay) return;

    try {
      const res = await axios.post(`/api/v1/dormitory/stays/${checkOutModal.stay.StayID}/checkout`, {
        checkOutDate: checkOutForm.checkOutDate,
        remarks: checkOutForm.remarks
      });
      if (res.data && res.data.success) {
        showAlert(`บันทึกการย้ายออกของ ${checkOutModal.stay.TraineeName} เรียบร้อยแล้ว`, "success");
        setCheckOutModal({ open: false, stay: null });
        fetchAllData();
      }
    } catch (err) {
      showAlert("เกิดข้อผิดพลาดในการบันทึก Check-out: " + (err.response?.data?.message || err.message), "danger");
    }
  };

  // Handlers for Dormitory3DBuildingView
  const handleSelectRoomCheckIn = (room) => {
    if (!room) return;
    setCheckInForm((prev) => ({
      ...prev,
      building: room.BuildingName || `ตึก ${room.BuildingNo || room.DormitoryID}`,
      floor: room.Floor,
      roomNo: room.RoomNumber,
      roomId: room.RoomID,
      residentCategory: room.TargetGroup === "STAFF" ? "STAFF" : "STUDENT",
      selectedStaff: null,
      traineeName: "",
      traineeType: "STUDENT",
      positionText: "",
      departmentName: "",
      phone: "",
      citizenID: "",
      staffCode: "",
      doctorID: null,
      studentID: null,
      selectedCandidate: null,
      isFamilyStay: Boolean(room.IsFamilyRoom || room.RoomType === "ห้องครอบครัว" || room.DormitoryID === 3 || room.BuildingNo === "3")
    }));
    setCheckInModal({ open: true, defaultRoomId: room.RoomID });
  };

  const handleSelectRoomCheckOut = (res, room) => {
    const stayId = res?.StayID || res?.stayID || res?.id;
    const traineeName = res?.TraineeName || res?.traineeName || res?.Name || "";
    const roomNumber = room?.RoomNumber || res?.RoomNumber || "";
    const checkInDate = res?.CheckInDate || res?.checkInDate || "";

    setCheckOutModal({
      open: true,
      stay: {
        StayID: stayId,
        TraineeName: traineeName,
        RoomNumber: roomNumber,
        CheckInDate: checkInDate
      }
    });
  };

  const handleOpenEditStayModal = (res, room) => {
    if (!res) return;
    const stayId = res.StayID || res.stayID || res.id;
    const targetStay = stays.find((s) => s.StayID === stayId || s.stayID === stayId) || res;

    let inDate = targetStay.CheckInDate || targetStay.checkInDate || "";
    if (inDate && moment(inDate).isValid()) {
      inDate = moment(inDate).format("YYYY-MM-DD");
    }

    let expDate = targetStay.CheckOutDate || targetStay.checkOutDate || targetStay.expectedCheckOutDate || "";
    if (expDate && moment(expDate).isValid()) {
      expDate = moment(expDate).format("YYYY-MM-DD");
    } else {
      expDate = "";
    }

    setEditStayModal({
      open: true,
      stayId: stayId,
      stay: targetStay,
      room: room || targetStay.room,
      residentName: targetStay.TraineeName || targetStay.traineeName || res.TraineeName || res.traineeName || "",
      positionText: targetStay.PositionText || targetStay.positionText || res.PositionText || res.positionText || "",
      departmentName: targetStay.DepartmentName || targetStay.departmentName || res.DepartmentName || res.departmentName || "",
      roomNumber: room?.RoomNumber || room?.roomNumber || targetStay.RoomNumber || targetStay.roomNumber || "",
      buildingName: room?.BuildingName || room?.buildingName || targetStay.BuildingName || targetStay.buildingName || "",
      checkInDate: inDate,
      expectedCheckOutDate: expDate,
      phone: targetStay.ContactPhone || targetStay.contactPhone || targetStay.phone || res.ContactPhone || res.contactPhone || res.phone || "",
      feeRateType: targetStay.FeeRateType || targetStay.feeRateType || res.FeeRateType || res.feeRateType || "MONTHLY",
      freeElectricityUnits: Number(targetStay.FreeElectricityUnits ?? targetStay.freeElectricityUnits ?? res.FreeElectricityUnits ?? res.freeElectricityUnits ?? 0) || 0,
      remarks: targetStay.Remarks || targetStay.remarks || res.Remarks || res.remarks || "",
      coResidents: targetStay.CoResidents || targetStay.coResidents || res.CoResidents || res.coResidents || "",
      isSubmitting: false
    });
  };

  const handleEditStaySubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!editStayModal.stayId) {
      showAlert("ไม่พบรหัสการเข้าพัก (Stay ID)", "danger");
      return;
    }

    if (!editStayModal.checkInDate) {
      showAlert("กรุณาระบุวันที่เข้าพัก", "warning");
      return;
    }

    setEditStayModal((prev) => ({ ...prev, isSubmitting: true }));

    try {
      const payload = {
        isCurrentStay: true,
        checkInDate: editStayModal.checkInDate,
        checkOutDate: editStayModal.expectedCheckOutDate || null,
        stayDateRangeText: editStayModal.expectedCheckOutDate ? `ถึง ${editStayModal.expectedCheckOutDate}` : "",
        contactPhone: editStayModal.phone,
        phone: editStayModal.phone,
        feeRateType: editStayModal.feeRateType,
        freeElectricityUnits: Number(editStayModal.freeElectricityUnits) || 0,
        remarks: editStayModal.remarks,
        coResidents: editStayModal.coResidents
      };

      const res = await axios.put(`/api/v1/dormitory/stays/${editStayModal.stayId}`, payload);
      if (res.data && res.data.success) {
        showAlert(`บันทึกการแก้ไขข้อมูลของ ${editStayModal.residentName} เรียบร้อยแล้ว`, "success");
        setEditStayModal((prev) => ({ ...prev, open: false, isSubmitting: false }));
        fetchAllData();
      } else {
        showAlert(res.data?.message || "ไม่สามารถบันทึกข้อมูลได้", "danger");
        setEditStayModal((prev) => ({ ...prev, isSubmitting: false }));
      }
    } catch (err) {
      console.error("Error updating stay:", err);
      showAlert("เกิดข้อผิดพลาดในการบันทึกข้อมูล: " + (err.response?.data?.message || err.message), "danger");
      setEditStayModal((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleSwitchToElectricityTab = (room) => {
    setActiveTab(2);
    if (room) {
      const bNo = Number(room.DormitoryID || room.BuildingNo || 0);
      if (bNo) {
        setElectricityBuildingFilter(bNo);
      }
    }
  };

  const handleOpenMasterData = () => {
    history.push("/dormitory/master-data?preview_role=manager");
  };

  // Filtered residents/stays for Tab 1
  const filteredStays = useMemo(() => {
    return stays.filter((s) => {
      if (residentStatusFilter === "ACTIVE" && !s.IsCurrentStay) return false;
      if (residentStatusFilter === "CHECKED_OUT" && s.IsCurrentStay) return false;
      if (residentTypeFilter !== "ALL" && s.TraineeType !== residentTypeFilter) return false;
      if (residentSearch) {
        const q = residentSearch.toLowerCase();
        const nameMatch = (s.TraineeName || "").toLowerCase().includes(q);
        const roomMatch = (s.RoomNumber || "").toLowerCase().includes(q);
        const deptMatch = (s.DepartmentName || "").toLowerCase().includes(q);
        const posMatch = (s.PositionText || "").toLowerCase().includes(q);
        if (!nameMatch && !roomMatch && !deptMatch && !posMatch) return false;
      }
      return true;
    });
  }, [stays, residentStatusFilter, residentTypeFilter, residentSearch]);

  // Statistics
  const stats = useMemo(() => {
    const totalRooms = rooms.length;
    const staffRooms = rooms.filter((r) => r.TargetGroup === "STAFF").length;
    const studentRooms = rooms.filter((r) => r.TargetGroup === "STUDENT").length;
    const occupiedRooms = rooms.filter((r) => (r.CurrentResidents && r.CurrentResidents.length > 0) || r.Status === "OCCUPIED").length;
    const availableRooms = totalRooms - occupiedRooms;
    const activeOccupants = rooms.reduce((sum, r) => sum + (r.CurrentResidents?.length || 0), 0);

    // Electricity stats
    const electRows = Object.values(electricityDataMap);
    const occupiedElectRows = electRows.filter((r) => r.isOccupied);
    const totalUnits = electRows.reduce((sum, r) => sum + (Number(r.totalUnits) || 0), 0);
    const totalAmount = electRows.reduce((sum, r) => sum + (Number(r.totalAmount) || 0), 0);

    // ให้นับเฉพาะห้องที่ไม่ว่าง (Occupied Rooms Only)
    const targetOccupiedCount = occupiedElectRows.length;
    const recordedOccupiedCount = occupiedElectRows.filter((r) => Number(r.currentMeter) > 0 || r.isRecorded).length;
    const recordedCount = recordedOccupiedCount;

    const paidCount = electRows.filter((r) => r.paymentStatus === "PAID").length;
    const pendingAmount = electRows
      .filter((r) => r.paymentStatus === "PENDING" && Number(r.totalAmount) > 0)
      .reduce((sum, r) => sum + (Number(r.totalAmount) || 0), 0);

    return {
      totalRooms,
      staffRooms,
      studentRooms,
      availableRooms,
      occupiedRooms,
      activeOccupants,
      totalUnits,
      totalAmount,
      recordedCount,
      targetOccupiedCount,
      paidCount,
      pendingAmount
    };
  }, [rooms, electricityDataMap]);

  // กรองรายการห้องในตารางค่าไฟตามที่เลือก (เฉพาะห้องที่ไม่ว่าง หรือ ทุกห้อง)
  const displayedElectricityRows = useMemo(() => {
    const rows = Object.values(electricityDataMap);
    if (electricityOccupancyFilter === "OCCUPIED_ONLY") {
      return rows.filter((r) => r.isOccupied);
    }
    return rows;
  }, [electricityDataMap, electricityOccupancyFilter]);

  // Staff options for react-select
  const staffSelectOptions = useMemo(() => {
    return staffCandidates.map((s) => ({
      value: s.employeeKey,
      label: `${s.staffCode ? `[${s.staffCode}] ` : ""}${s.prefix || ""}${s.firstName} ${s.lastName} (${s.position || "ไม่ระบุตำแหน่ง"}) - ${s.departmentName || ""}`,
      staff: s
    }));
  }, [staffCandidates]);

  // รายชื่อผู้ที่มีห้องพักอยู่แล้วทั้งปัจจุบันและอนาคต (เพื่อนำออกจากตัวเลือก)
  const assignedTraineeSet = useMemo(() => {
    const set = new Set();
    (stays || []).forEach((s) => {
      const isCheckedOut =
        s.isCurrentStay === false ||
        s.IsCurrentStay === false ||
        s.status === "CHECKED_OUT" ||
        s.Status === "CHECKED_OUT";
      if (!isCheckedOut) {
        const name = (s.traineeName || s.TraineeName || "").trim();
        if (name && name !== "-") {
          set.add(name);
          const stripped = name.replace(/^(นพ\.|พญ\.|นาย|นางสาว|น\.ส\.|ดร\.)\s*/, "").trim();
          if (stripped) set.add(stripped);
        }
        if (s.doctorID || s.DoctorID) set.add(String(s.doctorID || s.DoctorID));
        if (s.studentID || s.StudentID) set.add(String(s.studentID || s.StudentID));
      }
    });
    return set;
  }, [stays]);

  // กรองเฉพาะคนที่อยู่ในช่วงเวลา หรือคนในอนาคต (ซ่อนคนที่พ้นกำหนดแล้ว)
  // และตัดคนที่มีห้องอยู่แล้วทั้งตอนนี้และอนาคตออก ไม่ให้แสดงในตัวเลือก
  const filteredCandidates = useMemo(() => {
    let list = traineeCandidates;
    if (onlyActiveOrFuture) {
      list = list.filter((c) => {
        const periodStatus = getTraineeTrainingPeriodStatus(c);
        return periodStatus.key !== "PAST";
      });
    }

    return list.filter((c) => {
      const cName = (c.name || "").trim();
      const stripped = cName.replace(/^(นพ\.|พญ\.|นาย|นางสาว|น\.ส\.|ดร\.)\s*/, "").trim();
      const hasRoom =
        assignedTraineeSet.has(cName) ||
        assignedTraineeSet.has(stripped) ||
        (c.id && assignedTraineeSet.has(String(c.id)));
      return !hasRoom;
    });
  }, [traineeCandidates, onlyActiveOrFuture, assignedTraineeSet]);

  // ตัวเลือกสำหรับ Searchable Dropdown (react-select)
  const traineeCandidateOptions = useMemo(() => {
    const docOptions = filteredCandidates
      .filter((c) => c.type === "DOCTOR")
      .map((c) => {
        const period = getTraineeTrainingPeriodStatus(c);
        const icon = period.key === "ACTIVE" ? "🟢 [กำลังฝึก]" : period.key === "UPCOMING" ? "⏳ [รอเริ่มฝึก/อนาคต]" : "⚪ [พ้นกำหนด]";
        return {
          value: c.id,
          label: `${icon} ${c.name} — ${c.position} (${c.institution || "สถาบันโรคผิวหนัง"} | ${c.dateRange || "ไม่ระบุวัน"})`,
          trainee: c
        };
      });

    const stdOptions = filteredCandidates
      .filter((c) => c.type === "STUDENT")
      .map((c) => {
        const period = getTraineeTrainingPeriodStatus(c);
        const icon = period.key === "ACTIVE" ? "🟢 [กำลังฝึก]" : period.key === "UPCOMING" ? "⏳ [รอเริ่มฝึก/อนาคต]" : "⚪ [พ้นกำหนด]";
        return {
          value: c.id,
          label: `${icon} ${c.name} — ${c.position} (${c.institution || "ไม่ระบุสถาบัน"} | ${c.dateRange || "ไม่ระบุวัน"})`,
          trainee: c
        };
      });

    const groups = [];
    if (docOptions.length > 0) {
      groups.push({
        label: `👨‍⚕️ แพทย์ฝึกอบรม / แพทย์ดูงาน (${docOptions.length} ท่านที่ยังไม่มีห้อง)`,
        options: docOptions
      });
    }
    if (stdOptions.length > 0) {
      groups.push({
        label: `🎓 นักศึกษาฝึกงาน / ฝึกปฏิบัติงาน (${stdOptions.length} ท่านที่ยังไม่มีห้อง)`,
        options: stdOptions
      });
    }
    groups.push({
      label: "✍️ อื่นๆ",
      options: [{ value: "CUSTOM", label: "✍️ กรอกชื่อบุคคลภายนอก / พิมพ์ชื่อใหม่เอง" }]
    });

    return groups;
  }, [filteredCandidates]);

  const selectedCandidateOption = useMemo(() => {
    if (!selectedCandidateId) return null;
    if (selectedCandidateId === "CUSTOM") {
      return { value: "CUSTOM", label: "✍️ กรอกชื่อบุคคลภายนอก / พิมพ์ชื่อใหม่เอง" };
    }
    for (const group of traineeCandidateOptions) {
      const found = group.options.find((o) => o.value === selectedCandidateId);
      if (found) return found;
    }
    return null;
  }, [selectedCandidateId, traineeCandidateOptions]);

  // Handler เมื่อเลือกแพทย์/นักศึกษาจาก Dropdown
  const handleSelectTraineeCandidate = (traineeId) => {
    if (!traineeId) {
      setSelectedCandidateId("");
      setCheckInForm((prev) => ({
        ...prev,
        selectedCandidate: null,
        doctorID: null,
        studentID: null,
        traineeName: "",
        positionText: "",
        departmentName: "",
        phone: ""
      }));
      return;
    }

    if (traineeId === "CUSTOM") {
      setSelectedCandidateId("CUSTOM");
      setCheckInForm((prev) => ({
        ...prev,
        selectedCandidate: null,
        doctorID: null,
        studentID: null,
        traineeName: "",
        positionText: "",
        departmentName: "",
        phone: "",
        dateRange: ""
      }));
      return;
    }

    const candidate = traineeCandidates.find((c) => String(c.id) === String(traineeId));
    if (candidate) {
      setSelectedCandidateId(candidate.id);
      let sDate = candidate.startDate || "";
      let eDate = candidate.endDate || "";
      if ((!sDate || !eDate) && candidate.dateRange) {
        const parsed = parseThaiDateRange(candidate.dateRange, candidate.fiscalYear || 2569);
        if (!sDate && parsed.start) sDate = parsed.start;
        if (!eDate && parsed.end) eDate = parsed.end;
      }
      const autoRange = (sDate || eDate) ? formatThaiDateRangeText(sDate, eDate) : (candidate.dateRange || "");

      let autoBuilding = checkInForm.building;
      let autoFloor = checkInForm.floor;
      let autoRoomNo = checkInForm.roomNo;
      let autoRoomId = checkInForm.roomId;

      if (candidate.roomNumber) {
        autoRoomNo = candidate.roomNumber;
        const matched = rooms.find((r) => String(r.RoomNumber) === String(candidate.roomNumber));
        if (matched) {
          autoRoomId = matched.RoomID;
          autoBuilding = matched.BuildingName || `ตึก ${matched.BuildingNo}`;
          autoFloor = matched.Floor;
        } else if (/^[1-4]\d{2}$/.test(candidate.roomNumber)) {
          autoBuilding = `ตึก ${candidate.roomNumber.substring(0, 1)}`;
          autoFloor = parseInt(candidate.roomNumber.substring(1, 2), 10) || 1;
        }
      }

      setCheckInForm((prev) => ({
        ...prev,
        selectedCandidate: candidate,
        doctorID: candidate.type === "DOCTOR" ? candidate.id : null,
        studentID: candidate.type === "STUDENT" ? candidate.id : null,
        traineeType: candidate.type || "DOCTOR",
        traineeName: candidate.name,
        positionText: candidate.position || "",
        departmentName: candidate.institution || candidate.department || "",
        phone: candidate.phone || prev.phone,
        checkInDate: sDate || prev.checkInDate || moment().format("YYYY-MM-DD"),
        expectedCheckOutDate: eDate || prev.expectedCheckOutDate,
        dateRange: autoRange || prev.dateRange,
        building: autoBuilding,
        floor: autoFloor,
        roomNo: autoRoomNo,
        roomId: autoRoomId || prev.roomId
      }));
      showAlert(`เลือก ${candidate.type === "DOCTOR" ? "แพทย์" : "นักศึกษา"}: ${candidate.name} เรียบร้อยแล้ว`, "info");
    }
  };

  const getBldgDigit = (bStr) => {
    if (!bStr) return "1";
    const m = String(bStr).match(/\d+/);
    return m ? m[0] : "1";
  };

  const getRoomNumberStr = (r) => {
    if (!r) return "";
    return String(r.RoomNumber || r.roomNumber || r.roomNo || r.RoomNo || "").trim();
  };

  const getRoomFloor = (r) => {
    if (!r) return 1;
    if (r.Floor !== undefined && r.Floor !== null && r.Floor !== "") return Number(r.Floor);
    if (r.floor !== undefined && r.floor !== null && r.floor !== "") return Number(r.floor);
    const rNum = getRoomNumberStr(r);
    if (rNum.length >= 2) {
      const flDigit = parseInt(rNum.substring(1, 2), 10);
      if (!isNaN(flDigit)) return flDigit;
    }
    return 1;
  };

  const getRoomBldgDigit = (r) => {
    if (!r) return "1";
    const val = r.BuildingNo || r.buildingNo || r.DormitoryID || r.dormitoryID || r.BuildingName || r.buildingName;
    if (val) return getBldgDigit(val);
    const rNum = getRoomNumberStr(r);
    if (rNum.length >= 1) {
      return getBldgDigit(rNum.substring(0, 1));
    }
    return "1";
  };

  // รายการห้องพักในตึกและชั้นที่เลือกจาก Master Data
  const studentRoomsOnFloor = useMemo(() => {
    const bldgNum = getBldgDigit(checkInForm.building);
    const floorNum = Number(checkInForm.floor) || 1;
    return rooms.filter((r) => {
      if (!r) return false;
      const rBldgNum = getRoomBldgDigit(r);
      const rFloor = getRoomFloor(r);
      return rBldgNum === bldgNum && rFloor === floorNum;
    });
  }, [rooms, checkInForm.building, checkInForm.floor]);

  // Handlers สำหรับเลือกอาคาร ชั้น และเลขห้อง
  const handleBuildingChange = (newBuilding) => {
    const bldgNum = getBldgDigit(newBuilding);
    const currentFloor = checkInForm.floor || 1;
    const bldgRooms = rooms.filter((r) => r && getRoomBldgDigit(r) === bldgNum);
    const sameFloorRoom = bldgRooms.find((r) => getRoomFloor(r) === Number(currentFloor)) || bldgRooms[0];
    const newRoomNo = sameFloorRoom ? getRoomNumberStr(sameFloorRoom) : "";
    const newRoomId = sameFloorRoom ? (sameFloorRoom.RoomID || sameFloorRoom.roomID || 0) : 0;
    const newFloor = sameFloorRoom ? getRoomFloor(sameFloorRoom) : currentFloor;

    setCheckInForm((prev) => ({
      ...prev,
      building: newBuilding,
      floor: newFloor,
      roomNo: newRoomNo || prev.roomNo,
      roomId: newRoomId || prev.roomId
    }));
  };

  const handleFloorChange = (newFloor) => {
    const bldgNum = getBldgDigit(checkInForm.building);
    const floorRoom = rooms.find((r) => r && getRoomBldgDigit(r) === bldgNum && getRoomFloor(r) === Number(newFloor));
    const newRoomNo = floorRoom ? getRoomNumberStr(floorRoom) : "";
    const newRoomId = floorRoom ? (floorRoom.RoomID || floorRoom.roomID || 0) : 0;

    setCheckInForm((prev) => ({
      ...prev,
      floor: newFloor,
      roomNo: newRoomNo || prev.roomNo,
      roomId: newRoomId || prev.roomId
    }));
  };

  const handleRoomNoChange = (newRoomNo) => {
    const cleanNo = String(newRoomNo || "").trim();
    const matchedRoom = rooms.find((r) => r && getRoomNumberStr(r) === cleanNo);
    if (matchedRoom) {
      const bldg = matchedRoom.BuildingName || matchedRoom.buildingName || `ตึก ${matchedRoom.BuildingNo || matchedRoom.buildingNo || getRoomBldgDigit(matchedRoom)}`;
      setCheckInForm((prev) => ({
        ...prev,
        roomNo: cleanNo,
        roomId: matchedRoom.RoomID || matchedRoom.roomID || 0,
        building: bldg,
        floor: getRoomFloor(matchedRoom)
      }));
    } else {
      setCheckInForm((prev) => ({
        ...prev,
        roomNo: cleanNo
      }));
    }
  };

  // ห้องที่ถูกเลือกอยู่ปัจจุบัน
  const currentSelectedRoom = useMemo(() => {
    if (checkInForm.roomId) {
      const byId = rooms.find((r) => (r.RoomID || r.roomID) === checkInForm.roomId);
      if (byId) return byId;
    }
    if (checkInForm.roomNo) {
      const byNo = rooms.find((r) => getRoomNumberStr(r) === String(checkInForm.roomNo).trim());
      if (byNo) return byNo;
    }
    return null;
  }, [rooms, checkInForm.roomId, checkInForm.roomNo]);

  // คำนวณอัตราค่าหอพักตาม Master Data อัตโนมัติ
  const autoMasterFeeInfo = useMemo(() => {
    const isB2 = checkInForm.building && (checkInForm.building.includes("2") || checkInForm.building.includes("แพทย์"));
    const dailyRate = currentSelectedRoom?.DailyRate || currentSelectedRoom?.dailyFee || (isB2 ? 100 : 17.86);
    const monthlyRate = currentSelectedRoom?.MonthlyRate || currentSelectedRoom?.fee || 500;

    let sDate = checkInForm.checkInDate || "";
    let eDate = checkInForm.expectedCheckOutDate || "";
    if ((!sDate || !eDate) && checkInForm.dateRange) {
      const parsed = parseThaiDateRange(checkInForm.dateRange, 2569);
      if (!sDate && parsed.start) sDate = parsed.start;
      if (!eDate && parsed.end) eDate = parsed.end;
    }

    if (sDate && eDate) {
      const days = countDaysBetween(sDate, eDate);
      if (isB2) {
        const amount = days * dailyRate;
        return {
          amount,
          text: `${amount.toLocaleString()} บาท (${days} วัน × ${dailyRate} บ./วัน)`,
          rateDesc: `วันละ ${dailyRate} บาท (ตึก 2 หอพักแพทย์)`
        };
      } else {
        const calc = calculateDormFeeByDays(checkInForm.building, days);
        return {
          amount: calc.totalAmount,
          text: `${calc.totalAmount.toLocaleString()} บาท/คน (${calc.breakdownText})`,
          rateDesc: `เดือนละ ${monthlyRate} บาท/คน (คิดรายคน หากอยู่คนเดียวคิด ${monthlyRate} บาท)`
        };
      }
    }

    if (isB2) {
      return {
        amount: dailyRate,
        text: `วันละ ${dailyRate} บาท/คน (ตึก 2 หอพักแพทย์)`,
        rateDesc: `วันละ ${dailyRate} บาท/คน (ตึก 2 หอพักแพทย์)`
      };
    } else {
      return {
        amount: monthlyRate,
        text: `${monthlyRate.toLocaleString()} บาท/คน/เดือน`,
        rateDesc: `เดือนละ ${monthlyRate.toLocaleString()} บาท/คน (คิดรายคน หากอยู่คนเดียวคิด ${monthlyRate.toLocaleString()} บาท)`
      };
    }
  }, [checkInForm.building, checkInForm.checkInDate, checkInForm.expectedCheckOutDate, checkInForm.dateRange, currentSelectedRoom]);

  // คำนวณจำนวนผู้พักที่ช่วงเวลาซ้อนทับกัน (Overlapping stays) เพื่อเช็คเตียงเต็ม
  const overlappingOccupantsInfo = useMemo(() => {
    if (!checkInForm.roomNo) return { overlaps: [], isFull: false, capacity: 2, count: 0, hasDates: false };

    let newStart = checkInForm.checkInDate || "";
    let newEnd = checkInForm.expectedCheckOutDate || "";
    if ((!newStart || !newEnd) && checkInForm.dateRange) {
      const parsed = parseThaiDateRange(checkInForm.dateRange, 2569);
      if (!newStart && parsed.start) newStart = parsed.start;
      if (!newEnd && parsed.end) newEnd = parsed.end;
    }

    const roomCapacity = currentSelectedRoom?.BedCapacity || currentSelectedRoom?.capacity || 2;

    const targetRoomStays = (stays || []).filter((s) => {
      if (s.isDeleted || s.IsDeleted || s.status === "ยกเลิก" || s.isActive === false || s.IsActive === false) return false;
      const isCheckedOut = s.isCurrentStay === false || s.IsCurrentStay === false || s.status === "CHECKED_OUT" || s.Status === "CHECKED_OUT";
      if (isCheckedOut) return false;

      const sRoom = s.RoomNumber || s.roomNo || "";
      const matchRoom = String(sRoom) === String(checkInForm.roomNo).trim();
      const sBldg = s.BuildingName || s.building || "";
      const matchBuilding = !checkInForm.building || sBldg === checkInForm.building || checkInForm.building.includes(sBldg) || sBldg.includes(checkInForm.building);
      return matchRoom && matchBuilding;
    });

    if (!newStart && !newEnd) {
      const activeStays = targetRoomStays.filter(isStayActive);
      return {
        overlaps: activeStays,
        isFull: activeStays.length >= roomCapacity,
        capacity: roomCapacity,
        count: activeStays.length,
        hasDates: false,
        startDate: "",
        endDate: ""
      };
    }

    const overlaps = targetRoomStays.filter((s) => {
      const sRange = getStayDateRange(s);
      if (!sRange.start && !sRange.end) {
        return isStayActive(s);
      }
      return checkDateRangesOverlap(newStart, newEnd, sRange.start, sRange.end);
    });

    return {
      overlaps,
      isFull: overlaps.length >= roomCapacity,
      capacity: roomCapacity,
      count: overlaps.length,
      hasDates: true,
      startDate: newStart,
      endDate: newEnd
    };
  }, [stays, checkInForm.roomNo, checkInForm.building, checkInForm.checkInDate, checkInForm.expectedCheckOutDate, checkInForm.dateRange, currentSelectedRoom]);

  // Available room options for Check-in
  const availableRoomOptions = useMemo(() => {
    return rooms
      .filter((r) => {
        const bedCap = Number(r.bedCapacity || r.BedCapacity || 2);
        const currentOcc = Number(r.currentOccupancy ?? r.CurrentOccupancy ?? r.currentResidents?.length ?? 0);
        const st = String(r.status || r.Status || "AVAILABLE").toUpperCase();
        const isCurrentSelected = (r.roomID || r.RoomID) === checkInForm.roomId;
        return isCurrentSelected || currentOcc < bedCap || st === "AVAILABLE";
      })
      .map((r) => {
        const rId = r.roomID || r.RoomID;
        const bNo = r.buildingNo || r.BuildingNo || r.dormitoryID || r.DormitoryID;
        const roomNo = r.roomNumber || r.RoomNumber;
        const floor = r.floor || r.Floor;
        const bedCap = Number(r.bedCapacity || r.BedCapacity || 2);
        const currentOcc = Number(r.currentOccupancy ?? r.CurrentOccupancy ?? r.currentResidents?.length ?? 0);
        const vacantBeds = Math.max(0, bedCap - currentOcc);
        const targetGroup = r.targetGroup || r.TargetGroup;
        const isFam = r.isFamilyRoom || r.IsFamilyRoom || r.roomType === "ห้องครอบครัว" || r.RoomType === "ห้องครอบครัว" || Number(bNo) === 3;

        let targetText = "ห้องทั่วไป";
        if (targetGroup === "STAFF") targetText = "เจ้าหน้าที่ รพ.";
        else if (targetGroup === "STUDENT") targetText = "นศ./แพทย์";

        return {
          value: rId,
          room: r,
          vacantBeds: vacantBeds,
          bedCapacity: bedCap,
          label: `🏢 [ตึก ${bNo}] ห้อง ${roomNo} (ชั้น ${floor}) — ว่าง ${vacantBeds}/${bedCap} เตียง [${targetText}]${isFam ? " 👨‍👩‍👧‍👦 (ห้องครอบครัว)" : ""}`
        };
      });
  }, [rooms, checkInForm.roomId]);

  // Buildings to render for Tab 0
  const buildingsToRender = useMemo(() => {
    if (selectedBuilding === "ALL") return buildings;
    return buildings.filter((b) => b.shortName === selectedBuilding || b.name?.includes(selectedBuilding));
  }, [buildings, selectedBuilding]);

  return (
    <div style={{ fontFamily: '"Sarabun", sans-serif', padding: "10px 5px" }}>
      {/* Alert banner */}
      {alertInfo.show && (
        <Alert color={alertInfo.color} toggle={() => setAlertInfo({ ...alertInfo, show: false })}>
          <strong>{alertInfo.color === "danger" ? "ผิดพลาด: " : "สำเร็จ: "}</strong>
          {alertInfo.message}
        </Alert>
      )}

      {/* Main Card Header & Navigation */}
      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "16px", overflow: "hidden", border: "1.5px solid #cbd5e1" }}>
        <div
          style={{
            background: "linear-gradient(135deg, #0b1e36 0%, #064e3b 45%, #0284c7 100%)",
            color: "#fff",
            padding: "24px 28px",
            position: "relative",
            overflow: "hidden"
          }}
        >
          {/* Subtle ambient light glow */}
          <div
            style={{
              position: "absolute",
              top: "-50px",
              right: "-50px",
              width: "280px",
              height: "280px",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(94,234,212,0.3) 0%, rgba(94,234,212,0) 70%)",
              pointerEvents: "none"
            }}
          />
          <Row className="align-items-center" style={{ position: "relative", zIndex: 1 }}>
            <Col md={8}>
              <div className="d-flex align-items-center">
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "14px",
                    backgroundColor: "rgba(255,255,255,0.18)",
                    backdropFilter: "blur(10px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "16px",
                    border: "2px solid rgba(255,255,255,0.4)",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.2)"
                  }}
                >
                  <HotelIcon style={{ fontSize: "32px", color: "#67e8f9" }} />
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                    <h3 className="mb-0 font-weight-bold" style={{ color: "#ffffff", letterSpacing: "-0.01em", textShadow: "0 2px 4px rgba(0,0,0,0.3)" }}>
                      ระบบจัดการหอพักและห้องพักบุคลากร
                    </h3>
                    <span
                      style={{
                        fontSize: "0.76rem",
                        fontWeight: "800",
                        letterSpacing: "0.06em",
                        padding: "4px 10px",
                        borderRadius: "20px",
                        backgroundColor: "#14b8a6",
                        color: "#ffffff",
                        border: "1.5px solid #5eead4",
                        textTransform: "uppercase",
                        boxShadow: "0 2px 6px rgba(20, 184, 166, 0.4)"
                      }}
                    >
                      Dormitory Management
                    </span>
                  </div>
                  <p style={{ color: "#f1f5f9", margin: 0, fontSize: "0.92rem", fontWeight: "500" }}>
                    โรงพยาบาลโรคผิวหนังเขตร้อนภาคใต้ จังหวัดตรัง • ผังห้องพัก 4 อาคาร, ทะเบียนผู้พัก (จนท. &amp; นศ./แพทย์), และจดมิเตอร์ค่าไฟ
                  </p>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-right mt-3 mt-md-0">
              <Button
                color="warning"
                size="sm"
                className="font-weight-bold mr-2 shadow"
                style={{
                  borderRadius: "10px",
                  padding: "9px 18px",
                  backgroundColor: "#f59e0b",
                  borderColor: "#d97706",
                  color: "#000000",
                  fontWeight: "800",
                  fontSize: "0.92rem",
                  boxShadow: "0 4px 14px rgba(245, 158, 11, 0.5)"
                }}
                onClick={() => {
                  setSelectedCandidateId("");
                  setStaffSelectorOpen(true);
                  setCoResidentSelectorOpen(false);
                  setCheckInForm((prev) => ({
                    ...prev,
                    residentCategory: "STAFF",
                    selectedStaff: null,
                    traineeName: "",
                    traineeType: "STUDENT",
                    positionText: "",
                    departmentName: "",
                    phone: "",
                    citizenID: "",
                    staffCode: "",
                    doctorID: null,
                    studentID: null,
                    selectedCandidate: null,
                    building: "ตึก 1",
                    floor: 1,
                    roomNo: "111",
                    roomId: availableRoomOptions[0]?.value || 0
                  }));
                  setCheckInModal({ open: true, defaultRoomId: 0 });
                }}
              >
                <PersonAddIcon style={{ fontSize: "19px", marginRight: "6px", verticalAlign: "text-bottom" }} />
                + จัดสรรห้องพัก / เช็คอิน
              </Button>
              {canManageMasterData && (
                <Button
                  color="warning"
                  size="sm"
                  className="mr-2"
                  style={{
                    borderRadius: "10px",
                    padding: "9px 15px",
                    fontWeight: "700",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "#ffffff",
                    border: "1.5px solid rgba(255,255,255,0.5)",
                    backdropFilter: "blur(8px)"
                  }}
                  onClick={() => history.push("/dormitory/master-data")}
                  title="จัดการข้อมูลหลัก Master Data (ห้องพักและอาคาร)"
                >
                  <StorageIcon style={{ fontSize: "18px", marginRight: "5px", verticalAlign: "text-bottom" }} />
                  Master Data
                </Button>
              )}
              <Button
                color="info"
                size="sm"
                style={{
                  borderRadius: "10px",
                  padding: "9px 13px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  border: "1.5px solid rgba(255,255,255,0.5)",
                  color: "#ffffff"
                }}
                onClick={fetchAllData}
                disabled={loading}
                title="รีเฟรชข้อมูล"
              >
                <RefreshIcon style={{ fontSize: "19px" }} />
              </Button>
            </Col>
          </Row>
        </div>

        {/* Modern Segmented / Pill Navigation Tabs */}
        <div style={{ backgroundColor: "#f8fafc", padding: "12px 18px", borderTop: "1.5px solid #cbd5e1" }}>
          <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
            <button
              type="button"
              onClick={() => setActiveTab(0)}
              style={{
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "11px 20px",
                borderRadius: "12px",
                border: activeTab === 0 ? "2px solid #059669" : "1.5px solid #cbd5e1",
                fontWeight: "800",
                fontSize: "0.95rem",
                color: activeTab === 0 ? "#064e3b" : "#1e293b",
                backgroundColor: activeTab === 0 ? "#ecfdf5" : "#ffffff",
                boxShadow: activeTab === 0 ? "0 4px 12px rgba(5, 150, 105, 0.2)" : "0 1px 3px rgba(0,0,0,0.04)",
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
            >
              <MeetingRoomIcon style={{ fontSize: "1.3rem", color: activeTab === 0 ? "#059669" : "#64748b" }} />
              🏢 ผังตึก 3 มิติและห้องพัก ({viewMode === "3D" ? "3D Building" : "Floor Grid"})
            </button>

            <button
              type="button"
              onClick={() => setActiveTab(1)}
              style={{
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "11px 20px",
                borderRadius: "12px",
                border: activeTab === 1 ? "2px solid #2563eb" : "1.5px solid #cbd5e1",
                fontWeight: "800",
                fontSize: "0.95rem",
                color: activeTab === 1 ? "#1e40af" : "#1e293b",
                backgroundColor: activeTab === 1 ? "#eff6ff" : "#ffffff",
                boxShadow: activeTab === 1 ? "0 4px 12px rgba(37, 99, 235, 0.2)" : "0 1px 3px rgba(0,0,0,0.04)",
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
            >
              <PeopleIcon style={{ fontSize: "1.3rem", color: activeTab === 1 ? "#2563eb" : "#64748b" }} />
              ทะเบียนประวัติการเข้าพัก
              <span
                style={{
                  fontSize: "0.78rem",
                  padding: "3px 10px",
                  borderRadius: "14px",
                  backgroundColor: activeTab === 1 ? "#bfdbfe" : "#e2e8f0",
                  color: activeTab === 1 ? "#1e3a8a" : "#0f172a",
                  fontWeight: "800"
                }}
              >
                {stays.filter((s) => s.IsCurrentStay).length} คน
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab(2)}
              style={{
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "11px 20px",
                borderRadius: "12px",
                border: activeTab === 2 ? "2px solid #d97706" : "1.5px solid #cbd5e1",
                fontWeight: "800",
                fontSize: "0.95rem",
                color: activeTab === 2 ? "#92400e" : "#1e293b",
                backgroundColor: activeTab === 2 ? "#fffbeb" : "#ffffff",
                boxShadow: activeTab === 2 ? "0 4px 12px rgba(217, 119, 6, 0.25)" : "0 1px 3px rgba(0,0,0,0.04)",
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
            >
              <FlashOnIcon style={{ fontSize: "1.3rem", color: activeTab === 2 ? "#d97706" : "#64748b" }} />
              จดมิเตอร์และคำนวณค่าไฟ (Electricity)
            </button>

            {canManageMasterData && (
              <button
                type="button"
                onClick={() => history.push("/dormitory/master-data")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  padding: "11px 18px",
                  borderRadius: "12px",
                  border: "2px dashed #0284c7",
                  fontWeight: "700",
                  fontSize: "0.92rem",
                  color: "#0369a1",
                  backgroundColor: "#f0f9ff",
                  cursor: "pointer",
                  transition: "all 0.2s ease"
                }}
                title="ไปที่หน้าจัดการข้อมูลหลัก Master Data (ห้องพักและอาคาร)"
              >
                <StorageIcon style={{ fontSize: "1.2rem", color: "#0284c7" }} />
                Master Data ↗
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* ========================================================================= */}
      {/* TAB 0: ผังห้องพัก (Floor Grid 4 อาคาร x 3 ชั้น)                           */}
      {/* ========================================================================= */}
      {activeTab === 0 && (
        <div>
          {/* Summary KPI Status Ribbon with Rich Colors & High Contrast */}
          <div className="row mb-3">
            {/* 1. ห้องพักทั้งหมด */}
            <div className="col-xl col-md-4 col-sm-6 mb-3">
              <div
                className={classes.statCard}
                style={{
                  borderTop: "5px solid #2563eb",
                  border: roomOccupancyFilter === "ALL" && roomTargetFilter === "ALL" ? "2.5px solid #2563eb" : "1.5px solid #bfdbfe",
                  background: "linear-gradient(180deg, #eff6ff 0%, #ffffff 65%)",
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.08)"
                }}
                onClick={() => { setRoomOccupancyFilter("ALL"); setRoomTargetFilter("ALL"); }}
                title="คลิกเพื่อแสดงห้องพักทั้งหมด"
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: "0.9rem", color: "#1e40af", fontWeight: "800" }}>🏢 ห้องพักทั้งหมด</span>
                  <div style={{ width: "38px", height: "38px", borderRadius: "10px", backgroundColor: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <HotelIcon style={{ fontSize: "22px", color: "#1d4ed8" }} />
                  </div>
                </div>
                <div style={{ fontSize: "2.1rem", fontWeight: "800", color: "#1e3a8a", lineHeight: 1.1 }}>
                  {stats.totalRooms} <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#3b82f6" }}>ห้อง</span>
                </div>
                <div className="mt-2" style={{ fontSize: "0.8rem", color: "#1e40af", fontWeight: "600" }}>
                  4 อาคาร • 3 ชั้น (12 ชั้นรวม)
                </div>
              </div>
            </div>

            {/* 2. ห้องเจ้าหน้าที่ */}
            <div className="col-xl col-md-4 col-sm-6 mb-3">
              <div
                className={classes.statCard}
                style={{
                  borderTop: "5px solid #1d4ed8",
                  border: roomTargetFilter === "STAFF" ? "2.5px solid #1d4ed8" : "1.5px solid #93c5fd",
                  background: "linear-gradient(180deg, #dbeafe 0%, #ffffff 65%)",
                  boxShadow: "0 4px 12px rgba(29, 78, 216, 0.08)"
                }}
                onClick={() => setRoomTargetFilter(roomTargetFilter === "STAFF" ? "ALL" : "STAFF")}
                title="คลิกเพื่อกรองเฉพาะห้องเจ้าหน้าที่ / บุคลากร"
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: "0.9rem", color: "#1e40af", fontWeight: "800" }}>👨‍💼 ห้องเจ้าหน้าที่ (STAFF)</span>
                  <div style={{ width: "38px", height: "38px", borderRadius: "10px", backgroundColor: "#bfdbfe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: "20px" }}>👨‍💼</span>
                  </div>
                </div>
                <div style={{ fontSize: "2.1rem", fontWeight: "800", color: "#1e3a8a", lineHeight: 1.1 }}>
                  {stats.staffRooms} <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#1d4ed8" }}>ห้อง</span>
                </div>
                <div className="mt-2" style={{ fontSize: "0.8rem", color: "#2563eb", fontWeight: "600" }}>
                  ข้าราชการ, พนักงาน, ลูกจ้าง
                </div>
              </div>
            </div>

            {/* 3. ห้อง นศ./แพทย์ */}
            <div className="col-xl col-md-4 col-sm-6 mb-3">
              <div
                className={classes.statCard}
                style={{
                  borderTop: "5px solid #0f766e",
                  border: roomTargetFilter === "STUDENT" ? "2.5px solid #0f766e" : "1.5px solid #5eead4",
                  background: "linear-gradient(180deg, #ccfbf1 0%, #ffffff 65%)",
                  boxShadow: "0 4px 12px rgba(15, 118, 110, 0.08)"
                }}
                onClick={() => setRoomTargetFilter(roomTargetFilter === "STUDENT" ? "ALL" : "STUDENT")}
                title="คลิกเพื่อกรองเฉพาะห้องนักศึกษา / แพทย์"
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: "0.9rem", color: "#0f766e", fontWeight: "800" }}>🎓 ห้อง นศ./แพทย์ (STUDENT)</span>
                  <div style={{ width: "38px", height: "38px", borderRadius: "10px", backgroundColor: "#99f6e4", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: "20px" }}>🎓</span>
                  </div>
                </div>
                <div style={{ fontSize: "2.1rem", fontWeight: "800", color: "#115e59", lineHeight: 1.1 }}>
                  {stats.studentRooms} <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#0f766e" }}>ห้อง</span>
                </div>
                <div className="mt-2" style={{ fontSize: "0.8rem", color: "#0d9488", fontWeight: "600" }}>
                  แพทย์เพิ่มพูนทักษะ, นศ.ฝึกงาน
                </div>
              </div>
            </div>

            {/* 4. กำลังพักอาศัย */}
            <div className="col-xl col-md-4 col-sm-6 mb-3">
              <div
                className={classes.statCard}
                style={{
                  borderTop: "5px solid #7c3aed",
                  border: roomOccupancyFilter === "ACTIVE" ? "2.5px solid #7c3aed" : "1.5px solid #c4b5fd",
                  background: "linear-gradient(180deg, #ede9fe 0%, #ffffff 65%)",
                  boxShadow: "0 4px 12px rgba(124, 58, 237, 0.08)"
                }}
                onClick={() => setRoomOccupancyFilter(roomOccupancyFilter === "ACTIVE" ? "ALL" : "ACTIVE")}
                title="คลิกเพื่อกรองเฉพาะห้องที่มีคนพักอยู่"
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: "0.9rem", color: "#6d28d9", fontWeight: "800" }}>👥 กำลังพักอาศัย</span>
                  <div style={{ width: "38px", height: "38px", borderRadius: "10px", backgroundColor: "#ddd6fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <PeopleIcon style={{ fontSize: "22px", color: "#6d28d9" }} />
                  </div>
                </div>
                <div style={{ fontSize: "2.1rem", fontWeight: "800", color: "#4c1d95", lineHeight: 1.1 }}>
                  {stats.occupiedRooms} <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#7c3aed" }}>ห้อง</span>
                </div>
                <div className="mt-2" style={{ fontSize: "0.8rem", color: "#581c87", fontWeight: "700" }}>
                  ผู้พัก {stats.activeOccupants} คน ({stats.totalRooms > 0 ? Math.round((stats.occupiedRooms / stats.totalRooms) * 100) : 0}% ครองห้อง)
                </div>
              </div>
            </div>

            {/* 5. ห้องว่างพร้อมพัก */}
            <div className="col-xl col-md-4 col-sm-6 mb-3">
              <div
                className={classes.statCard}
                style={{
                  borderTop: "5px solid #059669",
                  border: roomOccupancyFilter === "VACANT" ? "2.5px solid #059669" : "1.5px solid #6ee7b7",
                  background: "linear-gradient(180deg, #d1fae5 0%, #ffffff 65%)",
                  boxShadow: "0 4px 12px rgba(5, 150, 105, 0.1)"
                }}
                onClick={() => setRoomOccupancyFilter(roomOccupancyFilter === "VACANT" ? "ALL" : "VACANT")}
                title="คลิกเพื่อกรองเฉพาะห้องว่างพร้อมพัก"
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: "0.9rem", color: "#047857", fontWeight: "800" }}>🟢 ห้องว่างพร้อมพัก</span>
                  <div style={{ width: "38px", height: "38px", borderRadius: "10px", backgroundColor: "#a7f3d0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: "20px" }}>✨</span>
                  </div>
                </div>
                <div style={{ fontSize: "2.1rem", fontWeight: "800", color: "#064e3b", lineHeight: 1.1 }}>
                  {stats.availableRooms} <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#047857" }}>ห้อง</span>
                </div>
                <div className="mt-2" style={{ fontSize: "0.8rem", color: "#047857", fontWeight: "700" }}>
                  ว่างพร้อมจัดสรรเข้าพักได้ทันที
                </div>
              </div>
            </div>
          </div>

          {/* DORMITORY VIEW MODE: 3D Isometric View (Default Approved) OR Classic Floor Grid */}
          {viewMode === "3D" ? (
            <Dormitory3DBuildingView
              buildings={buildings}
              rooms={rooms}
              staffCandidates={staffCandidates}
              onSelectRoomCheckIn={handleSelectRoomCheckIn}
              onSelectRoomCheckOut={handleSelectRoomCheckOut}
              onSelectEditStay={handleOpenEditStayModal}
              onSelectEditRoom={(room) => setEditRoomModal({ open: true, room: { ...room, BedCapacity: room.suggestedBedCapacity || room.BedCapacity } })}
              onQuickIncreaseBedCapacity={handleSetRoomBedCapacity}
              onSwitchToElectricityTab={handleSwitchToElectricityTab}
              onOpenMasterData={handleOpenMasterData}
              onViewGridToggle={() => setViewMode("GRID")}
            />
          ) : (
            <div>
              {/* Switch back to 3D View Banner */}
              <div
                className="d-flex align-items-center justify-content-between flex-wrap p-3 mb-4 bg-white"
                style={{
                  borderRadius: "16px",
                  border: "1.5px solid #cbd5e1",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                }}
              >
                <div>
                  <h6 className="mb-1 font-weight-bold" style={{ color: "#0f172a" }}>
                    ▦ ผังตารางห้องพักแบบดั้งเดิม (Classic Floor Grid)
                  </h6>
                  <small className="text-muted">
                    กำลังแสดงรายการห้องพักแบบตารางรายอาคารและชั้น สามารถคลิกสลับกลับไปมุมมอง 3 มิติได้
                  </small>
                </div>
                <button
                  type="button"
                  onClick={() => setViewMode("3D")}
                  className="btn btn-sm btn-primary font-weight-bold mt-2 mt-sm-0"
                  style={{ borderRadius: "10px", padding: "8px 18px", fontSize: "0.88rem" }}
                >
                  🏢 สลับกลับไปมุมมองตึก 3 มิติ (3D Isometric View)
                </button>
              </div>

              {/* Modern Filter Bar Card with Vibrant Accent */}
          <div
            className="p-3 mb-4"
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              border: "1.5px solid #cbd5e1",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
            }}
          >
            {/* Row 1: Building Chips & Floor Selector */}
            <div className="d-flex flex-wrap align-items-center justify-content-between pb-3 mb-2" style={{ borderBottom: "1.5px solid #f1f5f9" }}>
              <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
                <span className="mr-2" style={{ fontSize: "0.92rem", fontWeight: "800", color: "#0f172a" }}>
                  <BusinessIcon style={{ fontSize: "1.25rem", verticalAlign: "middle", marginRight: "4px", color: "#0f766e" }} /> เลือกอาคาร:
                </span>
                {["ALL", "ตึก 1", "ตึก 2", "ตึก 3", "ตึก 4"].map((b) => {
                  const isSel = selectedBuilding === b;
                  let activeBg = "#1e293b";
                  let activeBorder = "#0f172a";
                  let idleColor = "#0f172a";
                  let idleBorder = "#cbd5e1";
                  let idleBg = "#f8fafc";

                  if (b === "ตึก 1") {
                    activeBg = "#1d4ed8";
                    activeBorder = "#1e40af";
                    idleColor = "#1e40af";
                    idleBorder = "#bfdbfe";
                    idleBg = "#eff6ff";
                  } else if (b === "ตึก 2") {
                    activeBg = "#ea580c";
                    activeBorder = "#c2410c";
                    idleColor = "#c2410c";
                    idleBorder = "#fed7aa";
                    idleBg = "#fff7ed";
                  } else if (b === "ตึก 3") {
                    activeBg = "#7c3aed";
                    activeBorder = "#6d28d9";
                    idleColor = "#6d28d9";
                    idleBorder = "#ddd6fe";
                    idleBg = "#faf5ff";
                  } else if (b === "ตึก 4") {
                    activeBg = "#059669";
                    activeBorder = "#047857";
                    idleColor = "#047857";
                    idleBorder = "#a7f3d0";
                    idleBg = "#ecfdf5";
                  }

                  return (
                    <button
                      key={b}
                      type="button"
                      style={{
                        fontSize: "0.88rem",
                        borderRadius: "20px",
                        padding: "6px 16px",
                        border: isSel ? `2px solid ${activeBorder}` : `1.5px solid ${idleBorder}`,
                        backgroundColor: isSel ? activeBg : idleBg,
                        color: isSel ? "#ffffff" : idleColor,
                        fontWeight: "800",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        boxShadow: isSel ? "0 4px 10px rgba(0,0,0,0.2)" : "0 1px 2px rgba(0,0,0,0.03)"
                      }}
                      onClick={() => setSelectedBuilding(b)}
                    >
                      {b === "ALL" ? "🏢 ทุกอาคาร" : `🏢 ${b}`}
                    </button>
                  );
                })}
              </div>

              <div className="d-flex align-items-center mt-2 mt-md-0">
                <span className="mr-2" style={{ fontSize: "0.92rem", fontWeight: "800", color: "#0f172a" }}>เลือกชั้น:</span>
                <select
                  className="form-control form-control-sm"
                  style={{
                    width: "155px",
                    borderRadius: "10px",
                    border: "2px solid #cbd5e1",
                    backgroundColor: "#ffffff",
                    fontWeight: "800",
                    color: "#0f172a",
                    fontSize: "0.88rem"
                  }}
                  value={floorFilter}
                  onChange={(e) => setFloorFilter(e.target.value)}
                >
                  <option value="ALL">ทุกชั้น (ชั้น 1-3)</option>
                  <option value="3">ชั้น 3 (บนสุด)</option>
                  <option value="2">ชั้น 2</option>
                  <option value="1">ชั้น 1 (ล่างสุด)</option>
                </select>
              </div>
            </div>

            {/* Row 2: Target Group & Occupancy Chips */}
            <div className="d-flex flex-wrap align-items-center justify-content-between pt-1" style={{ gap: "10px" }}>
              <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
                <span className="mr-1" style={{ fontSize: "0.9rem", fontWeight: "800", color: "#0f172a" }}>
                  <FilterListIcon style={{ fontSize: "1.2rem", verticalAlign: "middle", marginRight: "3px", color: "#0f766e" }} /> กลุ่มผู้พัก:
                </span>

                <button
                  type="button"
                  style={{
                    fontSize: "0.85rem",
                    borderRadius: "20px",
                    padding: "5px 14px",
                    fontWeight: "800",
                    backgroundColor: roomTargetFilter === "ALL" ? "#1e293b" : "#ffffff",
                    color: roomTargetFilter === "ALL" ? "#ffffff" : "#1e293b",
                    border: roomTargetFilter === "ALL" ? "2px solid #0f172a" : "1.5px solid #cbd5e1",
                    cursor: "pointer",
                    transition: "all 0.15s ease"
                  }}
                  onClick={() => setRoomTargetFilter("ALL")}
                >
                  📋 ทุกกลุ่ม
                </button>

                <button
                  type="button"
                  style={{
                    fontSize: "0.85rem",
                    borderRadius: "20px",
                    padding: "5px 14px",
                    fontWeight: "800",
                    backgroundColor: roomTargetFilter === "STAFF" ? "#1d4ed8" : "#eff6ff",
                    color: roomTargetFilter === "STAFF" ? "#ffffff" : "#1e40af",
                    border: roomTargetFilter === "STAFF" ? "2px solid #1e40af" : "1.5px solid #bfdbfe",
                    cursor: "pointer",
                    transition: "all 0.15s ease"
                  }}
                  onClick={() => setRoomTargetFilter("STAFF")}
                >
                  👨‍💼 เจ้าหน้าที่ (STAFF)
                </button>

                <button
                  type="button"
                  style={{
                    fontSize: "0.85rem",
                    borderRadius: "20px",
                    padding: "5px 14px",
                    fontWeight: "800",
                    backgroundColor: roomTargetFilter === "STUDENT" ? "#0f766e" : "#f0fdfa",
                    color: roomTargetFilter === "STUDENT" ? "#ffffff" : "#0f766e",
                    border: roomTargetFilter === "STUDENT" ? "2px solid #115e59" : "1.5px solid #99f6e4",
                    cursor: "pointer",
                    transition: "all 0.15s ease"
                  }}
                  onClick={() => setRoomTargetFilter("STUDENT")}
                >
                  🎓 นศ./แพทย์ (STUDENT)
                </button>

                <span className="mx-2" style={{ color: "#cbd5e1", fontWeight: "bold" }}>|</span>

                <span className="mr-1" style={{ fontSize: "0.9rem", fontWeight: "800", color: "#0f172a" }}>
                  สถานะห้อง:
                </span>

                <button
                  type="button"
                  style={{
                    fontSize: "0.85rem",
                    borderRadius: "20px",
                    padding: "5px 14px",
                    fontWeight: "800",
                    backgroundColor: roomOccupancyFilter === "ALL" ? "#334155" : "#ffffff",
                    color: roomOccupancyFilter === "ALL" ? "#ffffff" : "#334155",
                    border: roomOccupancyFilter === "ALL" ? "2px solid #1e293b" : "1.5px solid #cbd5e1",
                    cursor: "pointer",
                    transition: "all 0.15s ease"
                  }}
                  onClick={() => setRoomOccupancyFilter("ALL")}
                >
                  📋 ทุกห้อง
                </button>

                <button
                  type="button"
                  style={{
                    fontSize: "0.85rem",
                    borderRadius: "20px",
                    padding: "5px 14px",
                    fontWeight: "800",
                    backgroundColor: roomOccupancyFilter === "ACTIVE" ? "#7c3aed" : "#faf5ff",
                    color: roomOccupancyFilter === "ACTIVE" ? "#ffffff" : "#6d28d9",
                    border: roomOccupancyFilter === "ACTIVE" ? "2px solid #6d28d9" : "1.5px solid #ddd6fe",
                    cursor: "pointer",
                    transition: "all 0.15s ease"
                  }}
                  onClick={() => setRoomOccupancyFilter("ACTIVE")}
                >
                  🟣 มีคนพัก ({stats.occupiedRooms})
                </button>

                <button
                  type="button"
                  style={{
                    fontSize: "0.85rem",
                    borderRadius: "20px",
                    padding: "5px 14px",
                    fontWeight: "800",
                    backgroundColor: roomOccupancyFilter === "VACANT" ? "#059669" : "#ecfdf5",
                    color: roomOccupancyFilter === "VACANT" ? "#ffffff" : "#047857",
                    border: roomOccupancyFilter === "VACANT" ? "2px solid #047857" : "1.5px solid #a7f3d0",
                    cursor: "pointer",
                    transition: "all 0.15s ease"
                  }}
                  onClick={() => setRoomOccupancyFilter("VACANT")}
                >
                  🟢 ห้องว่าง ({stats.availableRooms})
                </button>
              </div>

              {(selectedBuilding !== "ALL" || floorFilter !== "ALL" || roomTargetFilter !== "ALL" || roomOccupancyFilter !== "ALL") && (
                <div className="mt-1 mt-md-0">
                  <button
                    type="button"
                    style={{
                      fontSize: "0.85rem",
                      padding: "6px 14px",
                      borderRadius: "8px",
                      border: "2px solid #f87171",
                      backgroundColor: "#fee2e2",
                      color: "#b91c1c",
                      fontWeight: "800",
                      cursor: "pointer"
                    }}
                    onClick={() => {
                      setSelectedBuilding("ALL");
                      setFloorFilter("ALL");
                      setRoomTargetFilter("ALL");
                      setRoomOccupancyFilter("ALL");
                    }}
                  >
                    ✕ ล้างตัวกรองทั้งหมด
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Render Buildings & Floors (4 Buildings x 3 Floors) with Distinctive Vibrant Banners */}
          {buildingsToRender.map((bldg) => {
            const bldgRooms = rooms.filter(
              (r) => r.DormitoryID === bldg.dormitoryID || r.BuildingNo === bldg.buildingNo || r.BuildingName?.includes(bldg.shortName)
            );
            const bldgOccupied = bldgRooms.filter((r) => (r.CurrentResidents && r.CurrentResidents.length > 0) || r.Status === "OCCUPIED").length;
            const bldgStaffRooms = bldgRooms.filter((r) => r.TargetGroup === "STAFF").length;
            const bldgStudentRooms = bldgRooms.filter((r) => r.TargetGroup === "STUDENT").length;
            const floorsList = floorFilter === "ALL" ? [3, 2, 1] : [Number(floorFilter)];

            const bldgNo = String(bldg.buildingNo || bldg.dormitoryID);
            let bldgGradient = "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)";
            let bldgBorderColor = "#93c5fd";
            let floorAccentColor = "#2563eb";

            if (bldgNo === "2") {
              bldgGradient = "linear-gradient(135deg, #c2410c 0%, #f97316 100%)";
              bldgBorderColor = "#fed7aa";
              floorAccentColor = "#ea580c";
            } else if (bldgNo === "3") {
              bldgGradient = "linear-gradient(135deg, #6d28d9 0%, #a855f7 100%)";
              bldgBorderColor = "#d8b4fe";
              floorAccentColor = "#7c3aed";
            } else if (bldgNo === "4") {
              bldgGradient = "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)";
              bldgBorderColor = "#99f6e4";
              floorAccentColor = "#0d9488";
            }

            return (
              <div
                key={bldg.id || bldg.dormitoryID}
                style={{
                  marginBottom: "32px",
                  border: `2px solid ${bldgBorderColor}`,
                  borderRadius: "18px",
                  overflow: "hidden",
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)"
                }}
              >
                {/* Vibrant Building Banner Header */}
                <div
                  style={{
                    background: bldgGradient,
                    padding: "16px 24px",
                    color: "#ffffff",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "12px"
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "12px",
                        backgroundColor: "rgba(255,255,255,0.22)",
                        backdropFilter: "blur(8px)",
                        border: "1.5px solid rgba(255,255,255,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "14px",
                        fontSize: "22px"
                      }}
                    >
                      🏢
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontWeight: "800", color: "#ffffff", textShadow: "0 1px 3px rgba(0,0,0,0.3)" }}>
                        {bldg.name || bldg.dormitoryName}
                      </h4>
                      <div style={{ color: "#ffffff", opacity: 0.95, fontSize: "0.85rem", fontWeight: "600" }}>
                        {bldg.note} • จำนวน {bldg.totalFloors || 3} ชั้น
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
                    <span
                      style={{
                        fontSize: "0.82rem",
                        padding: "5px 12px",
                        borderRadius: "20px",
                        backgroundColor: "#ffffff",
                        color: "#1e40af",
                        fontWeight: "800",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                      }}
                    >
                      👨‍💼 จนท. {bldgStaffRooms} ห้อง
                    </span>
                    <span
                      style={{
                        fontSize: "0.82rem",
                        padding: "5px 12px",
                        borderRadius: "20px",
                        backgroundColor: "#ffffff",
                        color: "#0f766e",
                        fontWeight: "800",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                      }}
                    >
                      🎓 นศ. {bldgStudentRooms} ห้อง
                    </span>
                    <span
                      style={{
                        fontSize: "0.82rem",
                        padding: "5px 12px",
                        borderRadius: "20px",
                        backgroundColor: "rgba(255,255,255,0.25)",
                        color: "#ffffff",
                        border: "1.5px solid rgba(255,255,255,0.5)",
                        fontWeight: "800"
                      }}
                    >
                      ทั้งหมด {bldgRooms.length} ห้อง
                    </span>
                    <span
                      style={{
                        fontSize: "0.82rem",
                        padding: "5px 14px",
                        borderRadius: "20px",
                        backgroundColor: "#ffffff",
                        color: bldgOccupied === bldgRooms.length && bldgRooms.length > 0 ? "#b45309" : "#15803d",
                        fontWeight: "800",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                      }}
                    >
                      เข้าพัก {bldgOccupied} ห้อง / ว่าง {bldgRooms.length - bldgOccupied} ห้อง
                    </span>
                  </div>
                </div>

                {/* Building Floors Body */}
                <div style={{ padding: "20px 24px" }}>
                  {floorsList.map((floorNum) => {
                    const floorAllRooms = bldgRooms.filter((r) => r.Floor === floorNum);
                    const floorRooms = floorAllRooms.filter((r) => {
                      const hasResidents = (r.CurrentResidents && r.CurrentResidents.length > 0) || r.Status === "OCCUPIED";

                      if (roomTargetFilter !== "ALL" && r.TargetGroup !== roomTargetFilter && r.TargetGroup !== "ALL") return false;
                      if (roomOccupancyFilter === "ACTIVE" && !hasResidents) return false;
                      if (roomOccupancyFilter === "VACANT" && hasResidents) return false;
                      return true;
                    });

                    return (
                      <div key={floorNum} style={{ marginBottom: "24px" }}>
                        <div
                          style={{
                            backgroundColor: "#f8fafc",
                            border: "1.5px solid #cbd5e1",
                            borderLeft: `6px solid ${floorAccentColor}`,
                            padding: "10px 18px",
                            borderRadius: "10px",
                            marginBottom: "14px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.02)"
                          }}
                        >
                          <span style={{ fontSize: "1rem", fontWeight: "800", color: "#0f172a" }}>
                            🚩 ชั้น {floorNum} {floorNum === 3 ? "(ชั้นบนสุด)" : floorNum === 1 ? "(ชั้นล่าง)" : ""}
                          </span>
                          <span style={{ fontSize: "0.85rem", color: "#1e293b", fontWeight: "700" }}>
                            {floorRooms.length} ห้องในชั้นนี้ (ห้องเจ้าหน้าที่ {floorRooms.filter((r) => r.TargetGroup === "STAFF").length} | ห้อง นศ. {floorRooms.filter((r) => r.TargetGroup === "STUDENT").length})
                          </span>
                        </div>

                        {floorAllRooms.length === 0 ? (
                          <div style={{ backgroundColor: "#f8fafc", border: "1.5px dashed #cbd5e1", borderRadius: "10px", padding: "16px", textAlign: "center", color: "#64748b", fontSize: "0.9rem", fontWeight: "600" }}>
                            ยังไม่มีข้อมูลห้องพักในชั้น {floorNum}
                          </div>
                        ) : floorRooms.length === 0 ? (
                          <div style={{ backgroundColor: "#f8fafc", border: "1.5px dashed #cbd5e1", borderRadius: "10px", padding: "14px", textAlign: "center", color: "#64748b", fontSize: "0.88rem", fontWeight: "600" }}>
                            ไม่มีห้องพักที่ตรงกับเงื่อนไขในชั้น {floorNum} (มีห้องในชั้นนี้ทั้งหมด {floorAllRooms.length} ห้อง)
                          </div>
                        ) : (
                          <div className="row">
                            {floorRooms.map((room) => {
                              const residents = room.CurrentResidents || [];
                              const isVacant = residents.length === 0 && room.Status === "AVAILABLE";
                              const isStaff = room.TargetGroup === "STAFF";
                              const isStudent = room.TargetGroup === "STUDENT";
                              const isFull = residents.length >= room.BedCapacity || room.Status === "OCCUPIED";

                              return (
                                <div key={room.RoomID} className="col-lg-4 col-md-6 mb-3">
                                  <div
                                    className={classes.roomCard}
                                    style={{
                                      border: "1.5px solid #cbd5e1",
                                      borderTop: `5px solid ${
                                        isVacant ? "#10b981" : isStaff ? "#2563eb" : "#0d9488"
                                      }`,
                                      background: isVacant
                                        ? "linear-gradient(180deg, #f0fdf4 0%, #ffffff 45%)"
                                        : "#ffffff"
                                    }}
                                  >
                                    <div>
                                      {/* Header: Room Number, Building Tag, TargetGroup Badge */}
                                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px", flexWrap: "wrap", gap: "6px" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                          <div
                                            style={{
                                              width: "40px",
                                              height: "40px",
                                              borderRadius: "10px",
                                              backgroundColor: isVacant ? "#dcfce7" : isStaff ? "#dbeafe" : "#ccfbf1",
                                              border: `1.5px solid ${isVacant ? "#86efac" : isStaff ? "#93c5fd" : "#5eead4"}`,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              color: isVacant ? "#15803d" : isStaff ? "#1d4ed8" : "#0f766e"
                                            }}
                                          >
                                            <HotelIcon style={{ fontSize: "22px" }} />
                                          </div>
                                          <div>
                                            <h4 style={{ margin: 0, fontWeight: "800", color: "#0f172a", fontSize: "1.2rem" }}>
                                              ห้อง {room.RoomNumber}
                                            </h4>
                                            <span style={{ fontSize: "0.78rem", color: "#1e293b", fontWeight: "700" }}>
                                              {bldg.shortName} ชั้น {room.Floor}
                                            </span>
                                          </div>
                                        </div>

                                        <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
                                          {/* Unified Room Category Badge */}
                                          {(() => {
                                            const catInfo = getRoomCategoryInfo(room);
                                            return (
                                              <span
                                                style={{
                                                  fontSize: "0.76rem",
                                                  padding: "3px 9px",
                                                  borderRadius: "6px",
                                                  backgroundColor: catInfo.badgeBg,
                                                  color: catInfo.badgeColor,
                                                  border: `1.5px solid ${catInfo.badgeBorder}`,
                                                  fontWeight: "800",
                                                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)"
                                                }}
                                              >
                                                {catInfo.fullTag}
                                              </span>
                                            );
                                          })()}

                                          {/* Occupancy Status Badge */}
                                          {room.Status === "MAINTENANCE" ? (
                                            <span
                                              style={{
                                                fontSize: "0.76rem",
                                                padding: "3px 9px",
                                                borderRadius: "6px",
                                                backgroundColor: "#f1f5f9",
                                                color: "#475569",
                                                border: "1.5px solid #cbd5e1",
                                                fontWeight: "800"
                                              }}
                                            >
                                              🔧 ปิดปรับปรุง
                                            </span>
                                          ) : residents.length === 0 ? (
                                            <span
                                              style={{
                                                fontSize: "0.76rem",
                                                padding: "3px 9px",
                                                borderRadius: "6px",
                                                backgroundColor: "#dcfce7",
                                                color: "#15803d",
                                                border: "1.5px solid #86efac",
                                                fontWeight: "800"
                                              }}
                                            >
                                              🟢 ว่าง
                                            </span>
                                          ) : residents.length < room.BedCapacity ? (
                                            <span
                                              style={{
                                                fontSize: "0.76rem",
                                                padding: "3px 9px",
                                                borderRadius: "6px",
                                                backgroundColor: "#fef3c7",
                                                color: "#b45309",
                                                border: "1.5px solid #fde68a",
                                                fontWeight: "800"
                                              }}
                                            >
                                              🟡 ว่างอีก {room.BedCapacity - residents.length} คน
                                            </span>
                                          ) : (
                                            <span
                                              style={{
                                                fontSize: "0.76rem",
                                                padding: "3px 9px",
                                                borderRadius: "6px",
                                                backgroundColor: "#fee2e2",
                                                color: "#b91c1c",
                                                border: "1.5px solid #fca5a5",
                                                fontWeight: "800"
                                              }}
                                            >
                                              🔴 ไม่ว่าง
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* Info line */}
                                      <div
                                        style={{
                                          fontSize: "0.85rem",
                                          color: "#1e293b",
                                          marginBottom: "8px",
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          flexWrap: "wrap",
                                          gap: "4px"
                                        }}
                                      >
                                        <div>
                                          <strong>ความจุ:</strong> {room.BedCapacity} เตียง • <strong>ค่าเช่า:</strong> {room.MonthlyRate || 500} ฿/คน/ด.
                                        </div>
                                        <button
                                          type="button"
                                          style={{
                                            fontSize: "0.76rem",
                                            padding: "3px 9px",
                                            borderRadius: "6px",
                                            border: "1.5px solid #cbd5e1",
                                            backgroundColor: "#f8fafc",
                                            color: "#0f172a",
                                            fontWeight: "700",
                                            cursor: "pointer"
                                          }}
                                          onClick={() => setEditRoomModal({ open: true, room: { ...room } })}
                                          title="เปลี่ยนประเภทห้อง (เจ้าหน้าที่ / นศ.) หรือปรับปรุงจำนวนเตียง"
                                        >
                                          ⚙️ แก้ไขห้อง
                                        </button>
                                      </div>

                                      <hr style={{ margin: "8px 0", borderColor: "#e2e8f0" }} />

                                      {/* Residents Box */}
                                      <div style={{ minHeight: "85px", marginBottom: "10px" }}>
                                        <div style={{ fontSize: "0.82rem", fontWeight: "800", color: "#0f172a", marginBottom: "6px" }}>
                                          ผู้พักอาศัยปัจจุบัน ({residents.length} คน):
                                        </div>
                                        {residents.length === 0 ? (
                                          <div
                                            style={{
                                              backgroundColor: "#f0fdf4",
                                              border: "2px dashed #86efac",
                                              borderRadius: "10px",
                                              padding: "12px",
                                              textAlign: "center",
                                              color: "#166534",
                                              fontSize: "0.88rem",
                                              fontWeight: "800"
                                            }}
                                          >
                                            ✨ ห้องว่างพร้อมจัดคนเข้าพัก
                                          </div>
                                        ) : (
                                          residents.map((occ, rIdx) => (
                                            <div
                                              key={rIdx}
                                              style={{
                                                backgroundColor: "#ffffff",
                                                border: "1.5px solid #cbd5e1",
                                                borderLeft: `4px solid ${occ.traineeType === "STAFF" ? "#2563eb" : "#0d9488"}`,
                                                borderRadius: "8px",
                                                padding: "8px 10px",
                                                marginBottom: "6px",
                                                fontSize: "0.84rem",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                boxShadow: "0 1px 2px rgba(0,0,0,0.03)"
                                              }}
                                            >
                                              <div style={{ flex: 1, minWidth: 0, paddingRight: "8px" }}>
                                                <strong style={{ color: "#0f172a", fontSize: "0.92rem", fontWeight: "800" }}>
                                                  {occ.traineeName}
                                                </strong>
                                                <div style={{ fontSize: "0.78rem", color: "#1e293b", fontWeight: "600", marginTop: "1px" }}>
                                                  {occ.traineeType === "STAFF" ? "👨‍💼 จนท." : occ.traineeType === "DOCTOR" ? "👨‍⚕️ แพทย์" : "🎓 นศ."}
                                                  {occ.positionText ? ` • ${occ.positionText}` : ""}
                                                  {occ.departmentName ? ` (${occ.departmentName})` : ""}
                                                  {occ.checkInDate ? ` • เข้า ${moment(occ.checkInDate).format("D/M/YY")}` : ""}
                                                </div>
                                                {occ.coResidents && (
                                                  <div
                                                    style={{
                                                      fontSize: "0.75rem",
                                                      color: "#92400e",
                                                      backgroundColor: "#fef3c7",
                                                      padding: "2px 8px",
                                                      borderRadius: "4px",
                                                      marginTop: "3px",
                                                      border: "1.5px solid #fcd34d",
                                                      fontWeight: "700",
                                                      display: "inline-block"
                                                    }}
                                                  >
                                                    👨‍👩‍👧‍👦 พักร่วม: <strong>{occ.coResidents}</strong>
                                                  </div>
                                                )}
                                              </div>
                                              <div className="d-flex align-items-center" style={{ gap: "4px", flexShrink: 0 }}>
                                                <button
                                                  type="button"
                                                  style={{
                                                    fontSize: "0.75rem",
                                                    padding: "4px 8px",
                                                    borderRadius: "6px",
                                                    border: "1.5px solid #93c5fd",
                                                    backgroundColor: "#eff6ff",
                                                    color: "#1d4ed8",
                                                    fontWeight: "800",
                                                    cursor: "pointer",
                                                    whiteSpace: "nowrap"
                                                  }}
                                                  title="แก้ไขข้อมูลการเข้าพัก"
                                                  onClick={() => handleOpenEditStayModal(occ, room)}
                                                >
                                                  แก้ไข
                                                </button>
                                                <button
                                                  type="button"
                                                  style={{
                                                    fontSize: "0.75rem",
                                                    padding: "4px 9px",
                                                    borderRadius: "6px",
                                                    border: "1.5px solid #fca5a5",
                                                    backgroundColor: "#fee2e2",
                                                    color: "#b91c1c",
                                                    fontWeight: "800",
                                                    cursor: "pointer",
                                                    whiteSpace: "nowrap"
                                                  }}
                                                  title="แจ้งย้ายออกจากห้องพัก"
                                                  onClick={() => {
                                                    setCheckOutModal({
                                                      open: true,
                                                      stay: {
                                                        StayID: occ.stayID,
                                                        TraineeName: occ.traineeName,
                                                        RoomNumber: room.RoomNumber,
                                                        CheckInDate: occ.checkInDate
                                                      }
                                                    });
                                                  }}
                                                >
                                                  ย้ายออก
                                                </button>
                                              </div>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    </div>

                                    {/* Card bottom action */}
                                    <div
                                      className="pt-2 d-flex justify-content-between align-items-center"
                                      style={{ borderTop: "1.5px solid #f1f5f9" }}
                                    >
                                      <span style={{ fontSize: "0.85rem", fontWeight: "800", color: isVacant ? "#15803d" : isFull ? "#b91c1c" : "#b45309" }}>
                                        {residents.length < room.BedCapacity ? `ว่าง ${room.BedCapacity - residents.length} เตียง` : "🔴 เตียงเต็ม"}
                                      </span>
                                      <button
                                        type="button"
                                        style={{
                                          fontSize: "0.88rem",
                                          padding: "7px 16px",
                                          borderRadius: "10px",
                                          fontWeight: "800",
                                          border: "none",
                                          cursor: residents.length >= room.BedCapacity ? "not-allowed" : "pointer",
                                          backgroundColor: residents.length >= room.BedCapacity ? "#e2e8f0" : "#059669",
                                          color: residents.length >= room.BedCapacity ? "#94a3b8" : "#ffffff",
                                          boxShadow: residents.length >= room.BedCapacity ? "none" : "0 4px 12px rgba(5, 150, 105, 0.4)",
                                          transition: "all 0.15s ease"
                                        }}
                                        disabled={residents.length >= room.BedCapacity}
                                        onClick={() => {
                                          setSelectedCandidateId("");
                                          setStaffSelectorOpen(true);
                                          setCoResidentSelectorOpen(false);
                                          setCheckInForm((prev) => ({
                                            ...prev,
                                            roomId: room.RoomID,
                                            building: room.BuildingName || `ตึก ${room.BuildingNo}`,
                                            floor: room.Floor,
                                            roomNo: room.RoomNumber,
                                            residentCategory: room.TargetGroup === "STAFF" ? "STAFF" : "STUDENT",
                                            selectedStaff: null,
                                            traineeName: "",
                                            traineeType: "STUDENT",
                                            positionText: "",
                                            departmentName: "",
                                            phone: "",
                                            citizenID: "",
                                            staffCode: "",
                                            doctorID: null,
                                            studentID: null,
                                            selectedCandidate: null,
                                            isFamilyStay: Boolean(room.IsFamilyRoom || room.RoomType === "ห้องครอบครัว" || room.DormitoryID === 3 || room.BuildingNo === "3")
                                          }));
                                          setCheckInModal({ open: true, defaultRoomId: room.RoomID });
                                        }}
                                      >
                                        + จัดคนเข้าห้องนี้
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 1: RESIDENTS MANAGEMENT (คนผูกกับห้อง)                                */}
      {/* ========================================================================= */}
      {activeTab === 1 && (
        <div>
          {/* Controls bar */}
          <div
            className="p-3 mb-4"
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              border: "1.5px solid #cbd5e1",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
            }}
          >
            <Row className="align-items-center">
              <Col md={4} className="mb-2 mb-md-0">
                <InputGroup>
                  <Input
                    placeholder="🔍 ค้นหาชื่อผู้พัก, เลขห้อง, แผนก/ตำแหน่ง..."
                    value={residentSearch}
                    onChange={(e) => setResidentSearch(e.target.value)}
                    style={{
                      borderRadius: "10px 0 0 10px",
                      border: "2px solid #cbd5e1",
                      backgroundColor: "#ffffff",
                      fontSize: "0.92rem",
                      fontWeight: "600",
                      color: "#0f172a"
                    }}
                  />
                  <InputGroupAddon addonType="append">
                    <Button
                      color="secondary"
                      style={{
                        borderRadius: "0 10px 10px 0",
                        backgroundColor: "#1e293b",
                        borderColor: "#1e293b",
                        color: "#ffffff"
                      }}
                    >
                      <SearchIcon style={{ fontSize: "20px" }} />
                    </Button>
                  </InputGroupAddon>
                </InputGroup>
              </Col>
              <Col md={3} className="mb-2 mb-md-0">
                <Input
                  type="select"
                  value={residentTypeFilter}
                  onChange={(e) => setResidentTypeFilter(e.target.value)}
                  style={{
                    borderRadius: "10px",
                    border: "2px solid #cbd5e1",
                    backgroundColor: "#ffffff",
                    fontSize: "0.9rem",
                    fontWeight: "800",
                    color: "#0f172a"
                  }}
                >
                  <option value="ALL">กลุ่มผู้พักทั้งหมด (เจ้าหน้าที่ &amp; นศ.)</option>
                  <option value="STAFF">เจ้าหน้าที่ / บุคลากร (STAFF)</option>
                  <option value="STUDENT">นักศึกษาฝึกงาน (STUDENT)</option>
                  <option value="DOCTOR">แพทย์ฝึกอบรม (DOCTOR)</option>
                </Input>
              </Col>
              <Col md={3} className="mb-2 mb-md-0">
                <Input
                  type="select"
                  value={residentStatusFilter}
                  onChange={(e) => setResidentStatusFilter(e.target.value)}
                  style={{
                    borderRadius: "10px",
                    border: "2px solid #cbd5e1",
                    backgroundColor: "#ffffff",
                    fontSize: "0.9rem",
                    fontWeight: "800",
                    color: "#0f172a"
                  }}
                >
                  <option value="ACTIVE">🟢 เฉพาะผู้ที่กำลังพักอยู่ (Current Residents)</option>
                  <option value="CHECKED_OUT">⚪ ย้ายออกแล้ว (History Records)</option>
                  <option value="ALL">📋 ประวัติทั้งหมด (All)</option>
                </Input>
              </Col>
              <Col md={2} className="text-right">
                <button
                  type="button"
                  style={{
                    width: "100%",
                    padding: "10px 16px",
                    borderRadius: "10px",
                    border: "none",
                    backgroundColor: "#1d4ed8",
                    color: "#ffffff",
                    fontWeight: "800",
                    fontSize: "0.92rem",
                    boxShadow: "0 4px 10px rgba(29, 78, 216, 0.35)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px"
                  }}
                  onClick={() => {
                    setSelectedCandidateId("");
                    setStaffSelectorOpen(true);
                    setCoResidentSelectorOpen(false);
                    setCheckInForm((prev) => ({
                      ...prev,
                      residentCategory: "STAFF",
                      selectedStaff: null,
                      traineeName: "",
                      traineeType: "STUDENT",
                      positionText: "",
                      departmentName: "",
                      phone: "",
                      citizenID: "",
                      staffCode: "",
                      doctorID: null,
                      studentID: null,
                      selectedCandidate: null,
                      building: "ตึก 1",
                      floor: 1,
                      roomNo: "111",
                      roomId: availableRoomOptions[0]?.value || 0
                    }));
                    setCheckInModal({ open: true, defaultRoomId: 0 });
                  }}
                >
                  <PersonAddIcon style={{ fontSize: "19px" }} />
                  + เพิ่มผู้พัก
                </button>
              </Col>
            </Row>
          </div>

          {/* Residents Table */}
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              border: "1.5px solid #cbd5e1",
              overflow: "hidden",
              boxShadow: "0 4px 14px rgba(0,0,0,0.04)"
            }}
          >
            <Table responsive hover className="mb-0 align-middle">
              <thead style={{ backgroundColor: "#1e293b", color: "#ffffff", fontSize: "0.88rem", borderBottom: "2px solid #0f172a" }}>
                <tr>
                  <th style={{ width: "65px", padding: "14px 16px", color: "#ffffff", fontWeight: "800" }}>ลำดับ</th>
                  <th style={{ width: "140px", padding: "14px 16px", color: "#ffffff", fontWeight: "800" }}>เลขห้อง / ตึก</th>
                  <th style={{ padding: "14px 16px", color: "#ffffff", fontWeight: "800" }}>ชื่อ - นามสกุล ผู้พักอาศัย</th>
                  <th style={{ padding: "14px 16px", color: "#ffffff", fontWeight: "800" }}>ประเภท</th>
                  <th style={{ padding: "14px 16px", color: "#ffffff", fontWeight: "800" }}>ตำแหน่ง / หน่วยงาน</th>
                  <th style={{ padding: "14px 16px", color: "#ffffff", fontWeight: "800" }}>วันที่เข้าอยู่</th>
                  <th style={{ padding: "14px 16px", color: "#ffffff", fontWeight: "800" }}>วันที่ย้ายออก</th>
                  <th style={{ padding: "14px 16px", color: "#ffffff", fontWeight: "800" }}>สถานะ</th>
                  <th className="text-center" style={{ width: "120px", padding: "14px 16px", color: "#ffffff", fontWeight: "800" }}>จัดการ</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: "0.9rem" }}>
                {filteredStays.length > 0 ? (
                  filteredStays.map((stay, idx) => {
                    const isStaff = stay.TraineeType === "STAFF";
                    const isCurrent = stay.IsCurrentStay;

                    return (
                      <tr key={stay.StayID} style={{ borderBottom: "1.5px solid #f1f5f9" }}>
                        <td style={{ padding: "14px 16px", fontWeight: "700", color: "#64748b" }}>{idx + 1}</td>
                        <td style={{ padding: "14px 16px" }}>
                          <span
                            style={{
                              fontWeight: "800",
                              color: "#1e40af",
                              backgroundColor: "#dbeafe",
                              padding: "4px 10px",
                              borderRadius: "8px",
                              border: "1.5px solid #93c5fd",
                              fontSize: "0.88rem"
                            }}
                          >
                            ห้อง {stay.RoomNumber}
                          </span>
                          {(stay.Room?.IsFamilyRoom || stay.Room?.RoomType === "ห้องครอบครัว" || stay.IsFamilyRoom || stay.RoomType === "ห้องครอบครัว" || stay.BuildingNo === "3" || stay.BuildingNo === 3 || stay.DormitoryID === 3) && (
                            <span
                              style={{
                                backgroundColor: "#ffedd5",
                                color: "#c2410c",
                                border: "1.5px solid #fdba74",
                                marginLeft: "6px",
                                fontSize: "0.74rem",
                                padding: "2px 8px",
                                borderRadius: "6px",
                                fontWeight: "800"
                              }}
                            >
                              ครอบครัว
                            </span>
                          )}
                          <div style={{ color: "#334155", fontWeight: "700", fontSize: "0.8rem", marginTop: "4px" }}>
                            {stay.BuildingName}
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ color: "#0f172a", fontWeight: "800", fontSize: "0.96rem" }}>{stay.TraineeName}</div>
                          {stay.StaffCode && <small style={{ color: "#475569", fontWeight: "600", marginRight: "8px" }}>รหัส: {stay.StaffCode}</small>}
                          {stay.CoResidents && (
                            <div style={{ fontSize: "0.78rem", color: "#92400e", backgroundColor: "#fef3c7", padding: "2px 8px", borderRadius: "4px", border: "1.5px solid #fcd34d", fontWeight: "700", marginTop: "3px", display: "inline-block" }}>
                              👨‍👩‍👧‍👦 อยู่กับ: <strong>{stay.CoResidents}</strong>
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          {isStaff ? (
                            <span
                              style={{
                                fontSize: "0.78rem",
                                padding: "4px 10px",
                                borderRadius: "6px",
                                backgroundColor: "#dbeafe",
                                color: "#1e40af",
                                border: "1.5px solid #93c5fd",
                                fontWeight: "800"
                              }}
                            >
                              👨‍💼 เจ้าหน้าที่
                            </span>
                          ) : stay.TraineeType === "DOCTOR" ? (
                            <span
                              style={{
                                fontSize: "0.78rem",
                                padding: "4px 10px",
                                borderRadius: "6px",
                                backgroundColor: "#ccfbf1",
                                color: "#0f766e",
                                border: "1.5px solid #5eead4",
                                fontWeight: "800"
                              }}
                            >
                              👨‍⚕️ แพทย์
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: "0.78rem",
                                padding: "4px 10px",
                                borderRadius: "6px",
                                backgroundColor: "#ede9fe",
                                color: "#6d28d9",
                                border: "1.5px solid #c4b5fd",
                                fontWeight: "800"
                              }}
                            >
                              🎓 นักศึกษา
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ color: "#0f172a", fontWeight: "700" }}>{stay.PositionText || "-"}</div>
                          <div style={{ color: "#475569", fontWeight: "600", fontSize: "0.82rem" }}>{stay.DepartmentName || stay.Institution}</div>
                        </td>
                        <td style={{ padding: "14px 16px", color: "#0f172a", fontWeight: "700" }}>
                          {stay.CheckInDate ? moment(stay.CheckInDate).format("D MMMM BBBB") : "-"}
                        </td>
                        <td style={{ padding: "14px 16px", color: "#334155", fontWeight: "600" }}>
                          {stay.CheckOutDate ? (
                            moment(stay.CheckOutDate).format("D MMMM BBBB")
                          ) : (
                            <span style={{ color: "#64748b" }}>- (ยังไม่ย้ายออก)</span>
                          )}
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          {isCurrent ? (
                            <span
                              style={{
                                fontSize: "0.8rem",
                                padding: "4px 12px",
                                borderRadius: "20px",
                                backgroundColor: "#dcfce7",
                                color: "#15803d",
                                border: "1.5px solid #86efac",
                                fontWeight: "800"
                              }}
                            >
                              🟢 กำลังพักอยู่
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: "0.8rem",
                                padding: "4px 12px",
                                borderRadius: "20px",
                                backgroundColor: "#f1f5f9",
                                color: "#475569",
                                border: "1.5px solid #cbd5e1",
                                fontWeight: "700"
                              }}
                            >
                              ย้ายออกแล้ว
                            </span>
                          )}
                        </td>
                        <td className="text-center" style={{ padding: "14px 16px" }}>
                          {isCurrent && (
                            <div className="d-flex align-items-center justify-content-center" style={{ gap: "6px" }}>
                              <button
                                type="button"
                                style={{
                                  fontSize: "0.78rem",
                                  padding: "5px 12px",
                                  borderRadius: "8px",
                                  border: "1.5px solid #93c5fd",
                                  backgroundColor: "#eff6ff",
                                  color: "#1d4ed8",
                                  fontWeight: "800",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  boxShadow: "0 1px 3px rgba(37, 99, 235, 0.12)"
                                }}
                                title="แก้ไขข้อมูลการเข้าพัก"
                                onClick={() => handleOpenEditStayModal(stay, null)}
                              >
                                <EditIcon style={{ fontSize: "16px" }} />
                                แก้ไข
                              </button>
                              <button
                                type="button"
                                style={{
                                  fontSize: "0.78rem",
                                  padding: "5px 12px",
                                  borderRadius: "8px",
                                  border: "1.5px solid #fca5a5",
                                  backgroundColor: "#fee2e2",
                                  color: "#b91c1c",
                                  fontWeight: "800",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  boxShadow: "0 1px 3px rgba(220, 38, 38, 0.15)"
                                }}
                                onClick={() => {
                                  setCheckOutModal({
                                    open: true,
                                    stay: {
                                      StayID: stay.StayID,
                                      TraineeName: stay.TraineeName,
                                      RoomNumber: stay.RoomNumber,
                                      CheckInDate: stay.CheckInDate
                                    }
                                  });
                                }}
                              >
                                <ExitToAppIcon style={{ fontSize: "16px" }} />
                                ย้ายออก
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-5 text-muted" style={{ fontWeight: "700", fontSize: "0.95rem" }}>
                      ไม่พบข้อมูลผู้เข้าพักตามเงื่อนไขที่เลือก
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: ELECTRICITY BILLING (จดมิเตอร์และคำนวณค่าไฟ)                      */}
      {/* ========================================================================= */}
      {activeTab === 2 && (
        <div>
          {/* Electricity KPI Summary with Vibrant Colors & High Contrast */}
          <Row className="mb-3">
            <Col sm={6} md={3} className="mb-3">
              <div
                className={classes.statCard}
                style={{
                  borderTop: "5px solid #d97706",
                  border: "1.5px solid #fde68a",
                  background: "linear-gradient(180deg, #fffbeb 0%, #ffffff 65%)",
                  boxShadow: "0 4px 12px rgba(217, 119, 6, 0.1)"
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span style={{ fontSize: "0.9rem", color: "#b45309", fontWeight: "800" }}>รวมหน่วยไฟฟ้ารายเดือน</span>
                  <div style={{ width: "38px", height: "38px", borderRadius: "10px", backgroundColor: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FlashOnIcon style={{ fontSize: "22px", color: "#d97706" }} />
                  </div>
                </div>
                <div style={{ fontSize: "2.1rem", fontWeight: "800", color: "#92400e", lineHeight: 1.1 }}>
                  {stats.totalUnits.toLocaleString()} <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#d97706" }}>หน่วย</span>
                </div>
                <div className="mt-2" style={{ fontSize: "0.8rem", color: "#92400e", fontWeight: "700" }}>
                  เดือน {THAI_MONTHS.find((m) => m.value === billingMonth)?.label} {billingYear + 543}
                </div>
              </div>
            </Col>

            <Col sm={6} md={3} className="mb-3">
              <div
                className={classes.statCard}
                style={{
                  borderTop: "5px solid #059669",
                  border: "1.5px solid #a7f3d0",
                  background: "linear-gradient(180deg, #ecfdf5 0%, #ffffff 65%)",
                  boxShadow: "0 4px 12px rgba(5, 150, 105, 0.1)"
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span style={{ fontSize: "0.9rem", color: "#047857", fontWeight: "800" }}>ยอดเงินค่าไฟฟ้าทั้งสิ้น</span>
                  <div style={{ width: "38px", height: "38px", borderRadius: "10px", backgroundColor: "#d1fae5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <AttachMoneyIcon style={{ fontSize: "22px", color: "#059669" }} />
                  </div>
                </div>
                <div style={{ fontSize: "2.1rem", fontWeight: "800", color: "#064e3b", lineHeight: 1.1 }}>
                  {stats.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })} <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#047857" }}>฿</span>
                </div>
                <div className="mt-2" style={{ fontSize: "0.8rem", color: "#065f46", fontWeight: "700" }}>
                  อัตราหน่วยละ {electricityRate} บาท
                </div>
              </div>
            </Col>

            <Col sm={6} md={3} className="mb-3">
              <div
                className={classes.statCard}
                style={{
                  borderTop: "5px solid #2563eb",
                  border: "1.5px solid #bfdbfe",
                  background: "linear-gradient(180deg, #eff6ff 0%, #ffffff 65%)",
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.1)"
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span style={{ fontSize: "0.9rem", color: "#1d4ed8", fontWeight: "800" }}>สถานะการจดมิเตอร์</span>
                  <div style={{ width: "38px", height: "38px", borderRadius: "10px", backgroundColor: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <EditIcon style={{ fontSize: "20px", color: "#2563eb" }} />
                  </div>
                </div>
                <div style={{ fontSize: "2.1rem", fontWeight: "800", color: "#1e3a8a", lineHeight: 1.1 }}>
                  {stats.recordedCount} / {stats.targetOccupiedCount} <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#2563eb" }}>ห้อง</span>
                </div>
                <div className="mt-2" style={{ fontSize: "0.8rem", color: "#1e40af", fontWeight: "700" }}>
                  จดแล้ว {stats.targetOccupiedCount > 0 ? Math.round((stats.recordedCount / stats.targetOccupiedCount) * 100) : 0}% (นับเฉพาะห้องที่ไม่ว่าง)
                </div>
              </div>
            </Col>

            <Col sm={6} md={3} className="mb-3">
              <div
                className={classes.statCard}
                style={{
                  borderTop: "5px solid #dc2626",
                  border: "1.5px solid #fecaca",
                  background: "linear-gradient(180deg, #fff1f2 0%, #ffffff 65%)",
                  boxShadow: "0 4px 12px rgba(220, 38, 38, 0.1)"
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span style={{ fontSize: "0.9rem", color: "#b91c1c", fontWeight: "800" }}>ยอดค้างชำระ</span>
                  <div style={{ width: "38px", height: "38px", borderRadius: "10px", backgroundColor: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <WarningIcon style={{ fontSize: "20px", color: "#dc2626" }} />
                  </div>
                </div>
                <div style={{ fontSize: "2.1rem", fontWeight: "800", color: "#991b1b", lineHeight: 1.1 }}>
                  {stats.pendingAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })} <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#b91c1c" }}>฿</span>
                </div>
                <div className="mt-2" style={{ fontSize: "0.8rem", color: "#991b1b", fontWeight: "700" }}>
                  ชำระแล้ว {stats.paidCount} ห้อง
                </div>
              </div>
            </Col>
          </Row>

          {/* Month & Building Selector Bar */}
          <div
            className="p-3 mb-4"
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              border: "1.5px solid #cbd5e1",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
            }}
          >
            <Row className="align-items-center">
              <Col md={2} className="mb-2 mb-md-0">
                <FormGroup className="mb-0">
                  <Label className="small font-weight-bold mb-1" style={{ color: "#0f172a", fontWeight: "800", fontSize: "0.86rem" }}>
                    เลือกเดือน
                  </Label>
                  <Input
                    type="select"
                    value={billingMonth}
                    onChange={(e) => setBillingMonth(Number(e.target.value))}
                    style={{ borderRadius: "10px", border: "2px solid #cbd5e1", backgroundColor: "#ffffff", fontWeight: "800", color: "#0f172a" }}
                  >
                    {THAI_MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2} className="mb-2 mb-md-0">
                <FormGroup className="mb-0">
                  <Label className="small font-weight-bold mb-1" style={{ color: "#0f172a", fontWeight: "800", fontSize: "0.86rem" }}>
                    เลือกปี (พ.ศ.)
                  </Label>
                  <Input
                    type="select"
                    value={billingYear}
                    onChange={(e) => setBillingYear(Number(e.target.value))}
                    style={{ borderRadius: "10px", border: "2px solid #cbd5e1", backgroundColor: "#ffffff", fontWeight: "800", color: "#0f172a" }}
                  >
                    {[currentYearCE - 1, currentYearCE, currentYearCE + 1].map((y) => (
                      <option key={y} value={y}>
                        พ.ศ. {y + 543}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2} className="mb-2 mb-md-0">
                <FormGroup className="mb-0">
                  <Label className="small font-weight-bold mb-1" style={{ color: "#0f172a", fontWeight: "800", fontSize: "0.86rem" }}>
                    กรองตามอาคาร
                  </Label>
                  <Input
                    type="select"
                    value={electricityBuildingFilter}
                    onChange={(e) => setElectricityBuildingFilter(Number(e.target.value))}
                    style={{ borderRadius: "10px", border: "2px solid #cbd5e1", backgroundColor: "#ffffff", fontWeight: "800", color: "#0f172a" }}
                  >
                    <option value={0}>ทั้งหมดทุกอาคาร (ตึก 1 - 4)</option>
                    {buildings.map((b) => (
                      <option key={b.dormitoryID} value={b.dormitoryID}>
                        {b.name || b.dormitoryName}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2} className="mb-2 mb-md-0">
                <FormGroup className="mb-0">
                  <Label className="small font-weight-bold mb-1" style={{ color: "#0f172a", fontWeight: "800", fontSize: "0.86rem" }}>
                    อัตราค่าไฟ (บาท/หน่วย)
                  </Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={electricityRate}
                    onChange={(e) => handleElectricityRateChange(e.target.value)}
                    style={{ borderRadius: "10px", border: "2px solid #cbd5e1", backgroundColor: "#ffffff", fontWeight: "800", color: "#0f172a" }}
                  />
                </FormGroup>
              </Col>
              <Col md={2} className="mb-2 mb-md-0 d-flex align-items-end">
                <button
                  type="button"
                  style={{
                    width: "100%",
                    padding: "10px 10px",
                    borderRadius: "10px",
                    border: "1.5px solid #3b82f6",
                    backgroundColor: "#eff6ff",
                    color: "#1d4ed8",
                    fontWeight: "800",
                    fontSize: "0.84rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    boxShadow: "0 2px 6px rgba(59, 130, 246, 0.15)",
                    transition: "all 0.2s ease"
                  }}
                  title="คำนวณค่าไฟใหม่ทุกห้องในตารางตามอัตราค่าไฟนี้ (รวมถึงห้องที่บันทึกแล้ว)"
                  onClick={handleRecalculateAllElectricity}
                >
                  <RefreshIcon style={{ fontSize: "18px" }} />
                  คำนวณใหม่ทุกห้อง
                </button>
              </Col>
              <Col md={2} className="text-right d-flex align-items-end">
                <button
                  type="button"
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: "10px",
                    border: "none",
                    backgroundColor: batchSaving ? "#94a3b8" : "#059669",
                    color: "#ffffff",
                    fontWeight: "800",
                    fontSize: "0.92rem",
                    boxShadow: "0 4px 10px rgba(5, 150, 105, 0.35)",
                    cursor: batchSaving ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px"
                  }}
                  disabled={batchSaving}
                  onClick={handleBatchSaveElectricity}
                >
                  <SaveIcon style={{ fontSize: "18px" }} />
                  {batchSaving ? "กำลังบันทึก..." : "💾 บันทึกทั้งหมด"}
                </button>
              </Col>
            </Row>
          </div>

          {/* Electricity Table Filter Toolbar */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 px-1">
            <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
              <span style={{ fontSize: "0.88rem", fontWeight: "800", color: "#334155", marginRight: "4px" }}>
                กรองรายการห้อง:
              </span>
              <button
                type="button"
                onClick={() => setElectricityOccupancyFilter("OCCUPIED_ONLY")}
                style={{
                  padding: "6px 16px",
                  borderRadius: "20px",
                  border: electricityOccupancyFilter === "OCCUPIED_ONLY" ? "2px solid #2563eb" : "1.5px solid #cbd5e1",
                  backgroundColor: electricityOccupancyFilter === "OCCUPIED_ONLY" ? "#eff6ff" : "#ffffff",
                  color: electricityOccupancyFilter === "OCCUPIED_ONLY" ? "#1d4ed8" : "#64748b",
                  fontWeight: "800",
                  fontSize: "0.84rem",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  boxShadow: electricityOccupancyFilter === "OCCUPIED_ONLY" ? "0 2px 6px rgba(37,99,235,0.18)" : "none",
                  transition: "all 0.2s ease"
                }}
              >
                <span>⚡ เฉพาะห้องที่ไม่ว่าง (มีผู้พัก)</span>
                <span
                  style={{
                    backgroundColor: electricityOccupancyFilter === "OCCUPIED_ONLY" ? "#2563eb" : "#e2e8f0",
                    color: electricityOccupancyFilter === "OCCUPIED_ONLY" ? "#ffffff" : "#475569",
                    padding: "1px 8px",
                    borderRadius: "10px",
                    fontSize: "0.75rem",
                    fontWeight: "800"
                  }}
                >
                  {stats.targetOccupiedCount}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setElectricityOccupancyFilter("ALL")}
                style={{
                  padding: "6px 16px",
                  borderRadius: "20px",
                  border: electricityOccupancyFilter === "ALL" ? "2px solid #2563eb" : "1.5px solid #cbd5e1",
                  backgroundColor: electricityOccupancyFilter === "ALL" ? "#eff6ff" : "#ffffff",
                  color: electricityOccupancyFilter === "ALL" ? "#1d4ed8" : "#64748b",
                  fontWeight: "800",
                  fontSize: "0.84rem",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  boxShadow: electricityOccupancyFilter === "ALL" ? "0 2px 6px rgba(37,99,235,0.18)" : "none",
                  transition: "all 0.2s ease"
                }}
              >
                <span>🏢 ทุกห้องรวมห้องว่าง</span>
                <span
                  style={{
                    backgroundColor: electricityOccupancyFilter === "ALL" ? "#2563eb" : "#e2e8f0",
                    color: electricityOccupancyFilter === "ALL" ? "#ffffff" : "#475569",
                    padding: "1px 8px",
                    borderRadius: "10px",
                    fontSize: "0.75rem",
                    fontWeight: "800"
                  }}
                >
                  {Object.keys(electricityDataMap).length}
                </span>
              </button>
            </div>

            <div style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: "700" }}>
              แสดง {displayedElectricityRows.length} ห้อง จากทั้งหมด {Object.keys(electricityDataMap).length} ห้อง
            </div>
          </div>

          {/* Electricity Table */}
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              border: "1.5px solid #cbd5e1",
              overflow: "hidden",
              boxShadow: "0 4px 14px rgba(0,0,0,0.04)"
            }}
          >
            <Table responsive hover className="mb-0 align-middle">
              <thead style={{ backgroundColor: "#1e293b", color: "#ffffff", fontSize: "0.88rem", borderBottom: "2px solid #0f172a" }}>
                <tr className="text-center">
                  <th style={{ width: "95px", padding: "14px 8px", color: "#ffffff", fontWeight: "800" }}>เลขห้อง</th>
                  <th style={{ width: "130px", padding: "14px 8px", color: "#ffffff", fontWeight: "800" }}>อาคาร</th>
                  <th className="text-left" style={{ padding: "14px 10px", color: "#ffffff", fontWeight: "800" }}>ผู้พักอาศัยปัจจุบัน / สิทธิ์ไฟฟรีรายคน</th>
                  <th style={{ width: "115px", padding: "14px 6px", color: "#ffffff", fontWeight: "800" }}>มิเตอร์เดิม</th>
                  <th style={{ width: "125px", padding: "14px 6px", color: "#ffffff", fontWeight: "800" }}>มิเตอร์ใหม่</th>
                  <th style={{ width: "95px", padding: "14px 6px", color: "#ffffff", fontWeight: "800" }}>ใช้จริง</th>
                  <th style={{ width: "115px", padding: "14px 6px", color: "#ffffff", fontWeight: "800" }}>สิทธิ์ฟรีรวม</th>
                  <th style={{ width: "105px", padding: "14px 6px", color: "#ffffff", fontWeight: "800" }}>สุทธิคิดเงิน</th>
                  <th style={{ width: "125px", padding: "14px 8px", color: "#ffffff", fontWeight: "800" }}>รวมเงิน (บาท)</th>
                  <th style={{ width: "105px", padding: "14px 6px", color: "#ffffff", fontWeight: "800" }}>สถานะ</th>
                  <th style={{ width: "95px", padding: "14px 6px", color: "#ffffff", fontWeight: "800" }}>จัดการ</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: "0.9rem" }}>
                {displayedElectricityRows.length > 0 ? (
                  displayedElectricityRows.map((row) => {
                    const isPaid = row.paymentStatus === "PAID";

                    return (
                      <tr key={row.roomID} style={{ borderBottom: "1.5px solid #f1f5f9" }}>
                        <td className="text-center" style={{ padding: "12px 8px" }}>
                          <span
                            style={{
                              fontWeight: "800",
                              color: "#1e40af",
                              backgroundColor: "#dbeafe",
                              padding: "4px 8px",
                              borderRadius: "8px",
                              border: "1.5px solid #93c5fd",
                              fontSize: "0.88rem"
                            }}
                          >
                            ห้อง {row.roomNumber}
                          </span>
                        </td>
                        <td className="text-center" style={{ padding: "12px 8px", color: "#334155", fontWeight: "700", fontSize: "0.85rem" }}>
                          {row.buildingName}
                        </td>
                        <td className="text-left" style={{ padding: "12px 10px" }}>
                          {row.residents && row.residents.length > 1 ? (
                            <div>
                              <div className="d-flex align-items-center gap-1 mb-1">
                                <span
                                  style={{
                                    fontSize: "0.72rem",
                                    padding: "1px 6px",
                                    borderRadius: "10px",
                                    backgroundColor: "#fef3c7",
                                    color: "#b45309",
                                    border: "1px solid #fde68a",
                                    fontWeight: "800"
                                  }}
                                >
                                  👥 ผู้พักอาศัยร่วม {row.residents.length} ท่าน (รวมสิทธิ์ฟรี)
                                </span>
                              </div>
                              {row.residents.map((res, rIdx) => {
                                const freeU = Number(res.FreeElectricityUnits ?? res.freeElectricityUnits ?? 0);
                                return (
                                  <div
                                    key={res.stayID || rIdx}
                                    className="d-flex align-items-center flex-wrap"
                                    style={{
                                      marginBottom: rIdx < row.residents.length - 1 ? "4px" : "0",
                                      gap: "6px"
                                    }}
                                  >
                                    <span style={{ color: "#0f172a", fontWeight: "800", fontSize: "0.9rem" }}>
                                      {rIdx + 1}. {res.traineeName}
                                    </span>
                                    {res.traineeType && (
                                      <span
                                        style={{
                                          fontSize: "0.7rem",
                                          padding: "1px 5px",
                                          borderRadius: "4px",
                                          backgroundColor: res.traineeType === "STAFF" ? "#dbeafe" : "#ccfbf1",
                                          color: res.traineeType === "STAFF" ? "#1e40af" : "#0f766e",
                                          border: res.traineeType === "STAFF" ? "1px solid #93c5fd" : "1px solid #5eead4",
                                          fontWeight: "800",
                                          display: "inline-block"
                                        }}
                                      >
                                        {res.traineeType === "STAFF" ? "👨‍💼 จนท." : "🎓 นศ./แพทย์"}
                                      </span>
                                    )}
                                    {freeU > 0 ? (
                                      <span
                                        style={{
                                          fontSize: "0.7rem",
                                          padding: "1px 6px",
                                          borderRadius: "4px",
                                          backgroundColor: "#dcfce7",
                                          color: "#15803d",
                                          border: "1px solid #86efac",
                                          fontWeight: "800"
                                        }}
                                      >
                                        ⚡ สิทธิ์ฟรี {freeU} น.
                                      </span>
                                    ) : (
                                      <span style={{ fontSize: "0.68rem", color: "#94a3b8" }}>(0 น.)</span>
                                    )}
                                    {res.coResidents && (
                                      <span style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: "600" }}>
                                        ({res.coResidents})
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : row.residents && row.residents.length === 1 ? (
                            (() => {
                              const singleRes = row.residents[0];
                              const freeU = Number(singleRes.FreeElectricityUnits ?? singleRes.freeElectricityUnits ?? 0);
                              return (
                                <div>
                                  <div style={{ color: "#0f172a", fontWeight: "800", fontSize: "0.92rem" }}>
                                    {singleRes.traineeName}
                                  </div>
                                  <div className="d-flex align-items-center gap-1 mt-1 flex-wrap">
                                    {singleRes.traineeType && (
                                      <span
                                        style={{
                                          fontSize: "0.72rem",
                                          padding: "1px 6px",
                                          borderRadius: "4px",
                                          backgroundColor: singleRes.traineeType === "STAFF" ? "#dbeafe" : "#ccfbf1",
                                          color: singleRes.traineeType === "STAFF" ? "#1e40af" : "#0f766e",
                                          border: singleRes.traineeType === "STAFF" ? "1.5px solid #93c5fd" : "1.5px solid #5eead4",
                                          fontWeight: "800"
                                        }}
                                      >
                                        {singleRes.traineeType === "STAFF" ? "👨‍💼 จนท." : "🎓 นศ./แพทย์"}
                                      </span>
                                    )}
                                    {freeU > 0 ? (
                                      <span
                                        style={{
                                          fontSize: "0.72rem",
                                          padding: "1px 6px",
                                          borderRadius: "4px",
                                          backgroundColor: "#dcfce7",
                                          color: "#15803d",
                                          border: "1px solid #86efac",
                                          fontWeight: "800"
                                        }}
                                      >
                                        ⚡ สิทธิ์ฟรี {freeU} หน่วย
                                      </span>
                                    ) : (
                                      <span style={{ fontSize: "0.72rem", color: "#94a3b8" }}>ไม่มีสิทธิ์ลด</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })()
                          ) : (
                            <div>
                              <div style={{ color: row.residentName === "ห้องว่าง" ? "#94a3b8" : "#0f172a", fontWeight: "800", fontSize: "0.92rem" }}>
                                {row.residentName}
                              </div>
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "12px 6px" }}>
                          <Input
                            type="number"
                            step="0.01"
                            className="text-right"
                            style={{ borderRadius: "8px", border: "2px solid #cbd5e1", fontWeight: "800", fontSize: "0.9rem", color: "#0f172a" }}
                            value={row.previousMeter}
                            onChange={(e) => handleElectricityChange(row.roomID, "previousMeter", Number(e.target.value))}
                          />
                        </td>
                        <td style={{ padding: "12px 6px" }}>
                          <Input
                            type="number"
                            step="0.01"
                            className="text-right"
                            style={{ borderRadius: "8px", border: "2px solid #86efac", backgroundColor: "#f0fdf4", fontWeight: "800", fontSize: "0.9rem", color: "#15803d" }}
                            placeholder="เลขมิเตอร์"
                            value={row.currentMeter === 0 ? "" : row.currentMeter}
                            onChange={(e) => handleElectricityChange(row.roomID, "currentMeter", Number(e.target.value))}
                          />
                        </td>
                        <td className="text-center" style={{ padding: "12px 6px" }}>
                          <span style={{ fontWeight: "800", fontSize: "0.95rem", color: row.totalUnits > 0 ? "#0f172a" : "#64748b" }}>
                            {Number(row.totalUnits || 0).toLocaleString(undefined, { minimumFractionDigits: 1 })}
                          </span>
                        </td>
                        <td className="text-center" style={{ padding: "12px 6px" }}>
                          <div className="d-flex align-items-center justify-content-center">
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              className="text-right"
                              style={{
                                width: "75px",
                                borderRadius: "8px",
                                border: (Number(row.discountUnits) || 0) > 0 ? "2px solid #86efac" : "1.5px solid #cbd5e1",
                                backgroundColor: (Number(row.discountUnits) || 0) > 0 ? "#f0fdf4" : "#ffffff",
                                fontWeight: "800",
                                fontSize: "0.9rem",
                                color: (Number(row.discountUnits) || 0) > 0 ? "#15803d" : "#64748b",
                                padding: "4px 6px"
                              }}
                              title={row.residents && row.residents.length > 1 ? `รวมสิทธิ์ ${row.residents.length} คน = ${row.discountUnits} หน่วย` : "สิทธิ์ลดหน่วยไฟฟรี"}
                              value={row.discountUnits ?? 0}
                              onChange={(e) => handleElectricityChange(row.roomID, "discountUnits", Number(e.target.value) || 0)}
                            />
                          </div>
                        </td>
                        <td className="text-center" style={{ padding: "12px 6px" }}>
                          {Number(row.netUnits || 0) === 0 ? (
                            <span
                              style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                borderRadius: "6px",
                                backgroundColor: "#dcfce7",
                                color: "#166534",
                                border: "1px solid #86efac",
                                fontWeight: "800",
                                fontSize: "0.85rem"
                              }}
                            >
                              0 (ฟรี)
                            </span>
                          ) : (
                            <span style={{ fontWeight: "800", fontSize: "0.95rem", color: "#b91c1c" }}>
                              {Number(row.netUnits || 0).toLocaleString(undefined, { minimumFractionDigits: 1 })}
                            </span>
                          )}
                        </td>
                        <td className="text-right" style={{ padding: "12px 8px" }}>
                          {Number(row.totalAmount || 0) === 0 ? (
                            <span
                              style={{
                                display: "inline-block",
                                padding: "3px 8px",
                                borderRadius: "8px",
                                backgroundColor: "#dcfce7",
                                color: "#15803d",
                                border: "1.5px solid #86efac",
                                fontWeight: "800",
                                fontSize: "0.9rem"
                              }}
                            >
                              0.00 ฿ (ฟรี)
                            </span>
                          ) : (
                            <div>
                              <span style={{ fontSize: "0.96rem", fontWeight: "800", color: "#1e3a8a" }}>
                                {Number(row.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} ฿
                              </span>
                              <div style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: "600" }}>
                                ({row.netUnits} น. × {row.ratePerUnit || electricityRate} บ.)
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="text-center" style={{ padding: "12px 6px" }}>
                          <button
                            type="button"
                            style={{
                              fontSize: "0.78rem",
                              padding: "4px 10px",
                              borderRadius: "20px",
                              border: isPaid ? "1.5px solid #86efac" : "1.5px solid #fca5a5",
                              backgroundColor: isPaid ? "#dcfce7" : "#fee2e2",
                              color: isPaid ? "#15803d" : "#b91c1c",
                              fontWeight: "800",
                              cursor: "pointer",
                              minWidth: "85px"
                            }}
                            onClick={() => handleTogglePaymentStatus(row.roomID)}
                          >
                            {isPaid ? "✓ ชำระแล้ว" : "⏳ รอชำระ"}
                          </button>
                        </td>
                        <td className="text-center" style={{ padding: "12px 6px" }}>
                          <button
                            type="button"
                            style={{
                              padding: "5px 8px",
                              borderRadius: "8px",
                              border: "1.5px solid #bfdbfe",
                              backgroundColor: "#eff6ff",
                              color: "#1d4ed8",
                              cursor: "pointer",
                              marginRight: "4px"
                            }}
                            title="บันทึกห้องนี้"
                            onClick={() => handleSaveSingleElectricity(row.roomID)}
                          >
                            <SaveIcon style={{ fontSize: "16px" }} />
                          </button>
                          <button
                            type="button"
                            style={{
                              padding: "5px 8px",
                              borderRadius: "8px",
                              border: "1.5px solid #a5f3fc",
                              backgroundColor: "#ecfeff",
                              color: "#0891b2",
                              cursor: "pointer"
                            }}
                            title="พิมพ์ใบแจ้งค่าไฟ"
                            onClick={() => {
                              setBillPrintModal({
                                open: true,
                                bill: {
                                  ...row,
                                  monthName: THAI_MONTHS.find((m) => m.value === billingMonth)?.label,
                                  yearTH: billingYear + 543
                                }
                              });
                            }}
                          >
                            <PrintIcon style={{ fontSize: "16px" }} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={11} className="text-center py-5 text-muted" style={{ fontWeight: "700", fontSize: "0.95rem" }}>
                      ไม่พบข้อมูลมิเตอร์ห้องพักตามเงื่อนไขที่เลือก
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: EDIT ROOM CATEGORY & DETAILS                                      */}
      {/* ========================================================================= */}
      <Modal isOpen={editRoomModal.open} toggle={() => setEditRoomModal({ open: false, room: null })}>
        <ModalHeader toggle={() => setEditRoomModal({ open: false, room: null })}>
          แก้ไขข้อมูลและประเภทห้องพัก {editRoomModal.room?.RoomNumber}
        </ModalHeader>
        <ModalBody>
          {editRoomModal.room && (
            <div>
              {/* ประเภทห้องพักและกลุ่มเป้าหมายผู้พัก (Unified Single Dropdown) */}
              {(() => {
                const currentCat = getRoomCategoryInfo(editRoomModal.room);

                return (
                  <FormGroup>
                    <Label className="font-weight-bold" style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                      🏷️ ประเภทห้องพักและกลุ่มผู้พัก (Room Category) *
                    </Label>
                    <Input
                      type="select"
                      value={currentCat.key}
                      onChange={(e) => {
                        const nextCatKey = e.target.value;
                        setEditRoomModal((prev) => ({
                          ...prev,
                          room: applyRoomCategory(prev.room, nextCatKey)
                        }));
                      }}
                      style={{
                        height: "44px",
                        fontSize: "0.95rem",
                        fontWeight: "600",
                        borderColor: currentCat.badgeBorder,
                        borderWidth: "2px"
                      }}
                    >
                      {ROOM_CATEGORY_LIST.map((cat) => (
                        <option key={cat.key} value={cat.key}>
                          {cat.label}
                        </option>
                      ))}
                    </Input>

                    {/* กล่องสรุปรายละเอียดประเภทห้องที่เลือก */}
                    <div
                      style={{
                        marginTop: "8px",
                        padding: "10px 14px",
                        borderRadius: "10px",
                        backgroundColor: currentCat.badgeBg,
                        border: `1.5px solid ${currentCat.badgeBorder}`,
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                      }}
                    >
                      <span style={{ fontSize: "1.4rem" }}>{currentCat.fullTag.split(" ")[0]}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: "0.88rem", color: currentCat.badgeColor }}>
                          {currentCat.label}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#475569", marginTop: "2px" }}>
                          {currentCat.description}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: "0.74rem",
                          fontWeight: 700,
                          padding: "3px 8px",
                          borderRadius: "8px",
                          backgroundColor: "#ffffff",
                          color: currentCat.badgeColor,
                          border: `1px solid ${currentCat.badgeBorder}`,
                          whiteSpace: "nowrap"
                        }}
                      >
                        Target: {currentCat.targetGroup} | {currentCat.roomType}
                      </span>
                    </div>
                  </FormGroup>
                );
              })()}

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label className="font-weight-bold">จำนวนเตียง (ความจุ) *</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={editRoomModal.room.BedCapacity}
                      onChange={(e) => {
                        const newCap = Number(e.target.value);
                        setEditRoomModal((prev) => ({
                          ...prev,
                          room: { ...prev.room, BedCapacity: newCap }
                        }));
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label className="font-weight-bold">สถานะห้อง</Label>
                    <Input
                      type="select"
                      value={editRoomModal.room.Status === "MAINTENANCE" ? "MAINTENANCE" : "AVAILABLE"}
                      onChange={(e) => {
                        const nextStatus = e.target.value;
                        const room = editRoomModal.room;
                        const occ = Number(room.CurrentOccupancy ?? (room.CurrentResidents ? room.CurrentResidents.length : 0));
                        if (nextStatus === "MAINTENANCE" && occ > 0) {
                          alert(
                            `⚠️ ไม่สามารถปิดปรับปรุงได้ในขณะนี้\n\n` +
                            `ห้อง ${room.RoomNumber || ""} ยังมีผู้พักอาศัยอยู่จำนวน ${occ} คน\n` +
                            `กรุณาดำเนินการกดย้ายออก (Check-out) ผู้พักให้เรียบร้อยก่อน เพื่อเช็คว่าปิดปรับปรุงจริงไหม`
                          );
                          return;
                        }
                        const cap = Number(room.BedCapacity || 2);
                        setEditRoomModal((prev) => ({
                          ...prev,
                          room: {
                            ...prev.room,
                            Status: nextStatus === "MAINTENANCE" ? "MAINTENANCE" : (occ >= cap ? "OCCUPIED" : "AVAILABLE")
                          }
                        }));
                      }}
                      style={{
                        borderColor: editRoomModal.room.Status === "MAINTENANCE" ? "#f57c00" : "#2e7d32",
                        fontWeight: "bold"
                      }}
                    >
                      <option value="AVAILABLE">🟢 เปิดใช้งานปกติ (ระบบคำนวณสถานะว่าง/ไม่ว่างอัตโนมัติตามผู้เข้าพัก)</option>
                      <option value="MAINTENANCE">🔧 ปิดปรับปรุง (งดใช้งานชั่วคราว)</option>
                    </Input>

                    {(() => {
                      const room = editRoomModal.room;
                      const occ = Number(room.CurrentOccupancy ?? (room.CurrentResidents ? room.CurrentResidents.length : 0));
                      const cap = Number(room.BedCapacity || 2);
                      if (room.Status === "MAINTENANCE") {
                        return (
                          <div className="alert alert-secondary mt-2 py-1 px-2" style={{ fontSize: "0.82rem", borderRadius: "6px" }}>
                            🔧 <b>ห้องปิดปรับปรุง:</b> งดให้จัดคนเข้าพักชั่วคราว
                          </div>
                        );
                      }
                      if (occ === 0) {
                        return (
                          <div className="alert alert-success mt-2 py-1 px-2" style={{ fontSize: "0.82rem", borderRadius: "6px" }}>
                            ✅ <b>สถานะห้อง:</b> ว่าง (ยังไม่มีผู้เข้าพัก จัดได้สูงสุด {cap} คน)
                          </div>
                        );
                      }
                      if (occ < cap) {
                        return (
                          <div className="alert alert-warning mt-2 py-1 px-2" style={{ fontSize: "0.82rem", borderRadius: "6px" }}>
                            👥 <b>สถานะห้อง:</b> พักอยู่ {occ} คน (ว่างอีก {cap - occ} คน)
                          </div>
                        );
                      }
                      if (occ > cap) {
                        return (
                          <div className="alert alert-danger mt-2 py-1 px-2" style={{ fontSize: "0.82rem", borderRadius: "6px", backgroundColor: "#fee2e2", borderColor: "#ef4444", color: "#991b1b" }}>
                            ⚠️ <b>ผู้พักเกินเตียง:</b> พักอยู่ {occ} คน เกินความจุ {cap} เตียง (กรุณาปรับจำนวนเตียงเป็นอย่างน้อย {occ} เตียง)
                          </div>
                        );
                      }
                      return (
                        <div className="alert alert-danger mt-2 py-1 px-2" style={{ fontSize: "0.82rem", borderRadius: "6px" }}>
                          ⛔ <b>สถานะห้อง:</b> ไม่ว่าง (มีผู้พักเต็มจำนวนเตียง {occ}/{cap} คน)
                        </div>
                      );
                    })()}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                {(() => {
                  const cat = getRoomCategoryInfo(editRoomModal.room);
                  const isFeeApplicable = cat.key === "STUDENT" || cat.key === "DOCTOR";
                  if (isFeeApplicable) {
                    return (
                      <>
                        <Col md={6}>
                          <FormGroup>
                            <Label className="font-weight-bold">อัตราค่าห้องรายเดือน (บาท / ต่อคน) *</Label>
                            <Input
                              type="number"
                              value={editRoomModal.room.MonthlyRate}
                              onChange={(e) => {
                                const rate = Number(e.target.value);
                                setEditRoomModal((prev) => ({
                                  ...prev,
                                  room: { ...prev.room, MonthlyRate: rate, monthlyRate: rate, fee: rate }
                                }));
                              }}
                            />
                            <small className="text-success d-block mt-1 font-weight-bold" style={{ fontSize: "0.82rem" }}>
                              💡 คิดอัตราต่อคน: {editRoomModal.room.MonthlyRate || 500} บาท/คน/เดือน (หากอยู่คนเดียวก็คิด {editRoomModal.room.MonthlyRate || 500} บาท/คน)
                            </small>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label className="font-weight-bold">อัตราค่าห้องรายวัน (บาท / ต่อคน)</Label>
                            <Input
                              type="number"
                              value={editRoomModal.room.DailyRate}
                              onChange={(e) => {
                                const rate = Number(e.target.value);
                                setEditRoomModal((prev) => ({
                                  ...prev,
                                  room: { ...prev.room, DailyRate: rate, dailyRate: rate, dailyFee: rate }
                                }));
                              }}
                            />
                            <small className="text-muted d-block mt-1" style={{ fontSize: "0.8rem" }}>
                              💡 คิดอัตราต่อคน: {editRoomModal.room.DailyRate || 100} บาท/คน/วัน
                            </small>
                          </FormGroup>
                        </Col>
                      </>
                    );
                  }
                  return (
                    <Col md={12}>
                      <div className="alert alert-light border py-2 px-3 mb-3" style={{ fontSize: "0.85rem", color: "#475569" }}>
                        ℹ️ <b>ห้องพักสวัสดิการ รพ. / ส่วนกลาง:</b> ไม่มีค่าเช่ารายเดือนและรายวัน (คิดเฉพาะค่าไฟตามมิเตอร์จริง)
                      </div>
                    </Col>
                  );
                })()}
              </Row>

              <FormGroup>
                <Label className="font-weight-bold">หมายเหตุ</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={editRoomModal.room.Notes || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditRoomModal((prev) => ({
                      ...prev,
                      room: { ...prev.room, Notes: val }
                    }));
                  }}
                />
              </FormGroup>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditRoomModal({ open: false, room: null })}>
            ยกเลิก
          </Button>
          <Button color="primary" onClick={handleSaveRoom}>
            บันทึกการแก้ไข
          </Button>
        </ModalFooter>
      </Modal>

      {/* ========================================================================= */}
      {/* MODAL: CHECK-IN RESIDENT (จัดคนเข้าห้องพัก)                               */}
      {/* ========================================================================= */}
      <Modal
        isOpen={checkInModal.open}
        size="xl"
        toggle={() => {
          setSelectedCandidateId("");
          setCheckInModal({ open: false, defaultRoomId: 0 });
        }}
        style={{ maxWidth: "1200px" }}
      >
        <ModalHeader
          toggle={() => {
            setSelectedCandidateId("");
            setCheckInModal({ open: false, defaultRoomId: 0 });
          }}
        >
          {checkInForm.residentCategory === "STUDENT"
            ? "➕ จัดสรรห้องพัก / บันทึกการเข้าพักหอพัก"
            : "บันทึกการจัดคนเข้าห้องพัก (Check-in)"}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleCheckInSubmit}>
            {/* Step 1: Resident Category */}
            <div className="mb-3">
              <label className="font-weight-bold" style={{ fontSize: "0.95rem", color: "#1e293b", marginBottom: "8px", display: "block" }}>
                กลุ่มประเภทผู้เข้าพัก *
              </label>
              <div className="d-flex flex-wrap" style={{ gap: "14px" }}>
                <div
                  onClick={() => setCheckInForm({ ...checkInForm, residentCategory: "STAFF" })}
                  style={{
                    flex: "1 1 260px",
                    padding: "12px 18px",
                    borderRadius: "10px",
                    border: checkInForm.residentCategory === "STAFF" ? "2px solid #1976d2" : "1px solid #cbd5e1",
                    backgroundColor: checkInForm.residentCategory === "STAFF" ? "#eff6ff" : "#ffffff",
                    boxShadow: checkInForm.residentCategory === "STAFF" ? "0 2px 8px rgba(25, 118, 210, 0.15)" : "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    transition: "all 0.2s ease-in-out"
                  }}
                >
                  <input
                    type="radio"
                    id="catStaff"
                    name="residentCategory"
                    checked={checkInForm.residentCategory === "STAFF"}
                    onChange={() => setCheckInForm({ ...checkInForm, residentCategory: "STAFF" })}
                    style={{
                      cursor: "pointer",
                      width: "18px",
                      height: "18px",
                      accentColor: "#1976d2",
                      margin: 0
                    }}
                  />
                  <label htmlFor="catStaff" style={{ cursor: "pointer", margin: 0 }}>
                    <div style={{ fontWeight: "700", color: "#1e40af", fontSize: "0.95rem" }}>
                      🏥 เจ้าหน้าที่ / บุคลากร รพ. (STAFF)
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "2px" }}>
                      ข้าราชการ, พนักงานราชการ, ลูกจ้าง
                    </div>
                  </label>
                </div>

                <div
                  onClick={() => setCheckInForm({ ...checkInForm, residentCategory: "STUDENT" })}
                  style={{
                    flex: "1 1 260px",
                    padding: "12px 18px",
                    borderRadius: "10px",
                    border: checkInForm.residentCategory === "STUDENT" ? "2px solid #0891b2" : "1px solid #cbd5e1",
                    backgroundColor: checkInForm.residentCategory === "STUDENT" ? "#ecfeff" : "#ffffff",
                    boxShadow: checkInForm.residentCategory === "STUDENT" ? "0 2px 8px rgba(8, 145, 178, 0.15)" : "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    transition: "all 0.2s ease-in-out"
                  }}
                >
                  <input
                    type="radio"
                    id="catStudent"
                    name="residentCategory"
                    checked={checkInForm.residentCategory === "STUDENT"}
                    onChange={() => setCheckInForm({ ...checkInForm, residentCategory: "STUDENT" })}
                    style={{
                      cursor: "pointer",
                      width: "18px",
                      height: "18px",
                      accentColor: "#0891b2",
                      margin: 0
                    }}
                  />
                  <label htmlFor="catStudent" style={{ cursor: "pointer", margin: 0 }}>
                    <div style={{ fontWeight: "700", color: "#0e7490", fontSize: "0.95rem" }}>
                      🎓 นักศึกษาฝึกงาน / แพทย์ (STUDENT / DOCTOR)
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "2px" }}>
                      แพทย์เพิ่มพูนทักษะ, นักศึกษาฝึกงานภายนอก
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* If STAFF: Inline TSUser with expand/collapse */}
            {checkInForm.residentCategory === "STAFF" ? (
              <div className="mb-3">
                {/* 1. If Staff is selected: Display Profile Card */}
                {checkInForm.selectedStaff && (
                  <div
                    className="p-3 mb-2 rounded border"
                    style={{
                      backgroundColor: "#e8f5e9",
                      borderColor: "#c8e6c9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "10px"
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <img
                        src={`https://e.trangskin.go.th/avatar/${checkInForm.selectedStaff.userName || checkInForm.selectedStaff.staffCode}.jpg`}
                        alt="avatar"
                        style={{
                          width: 58,
                          height: 58,
                          borderRadius: "50%",
                          objectFit: "cover",
                          marginRight: "14px",
                          border: "2px solid #81c784"
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://e.trangskin.go.th/avatar/default.jpg";
                        }}
                      />
                      <div>
                        <div className="font-weight-bold" style={{ fontSize: "1.05rem", color: "#1b5e20" }}>
                          {checkInForm.traineeName || `${checkInForm.selectedStaff.firstName} ${checkInForm.selectedStaff.lastName}`}
                          {checkInForm.selectedStaff.nickName ? ` (${checkInForm.selectedStaff.nickName})` : ""}
                        </div>
                        <div className="text-muted small">
                          <strong>ตำแหน่ง:</strong> {checkInForm.positionText || "-"} | <strong>สังกัด:</strong> {checkInForm.departmentName || "-"}
                          {checkInForm.phone && ` | 📞 ${checkInForm.phone}`}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <Button
                        size="sm"
                        color="info"
                        className="mr-2 font-weight-bold"
                        onClick={() => setStaffSelectorOpen(!staffSelectorOpen)}
                      >
                        🔄 {staffSelectorOpen ? "ซ่อนผังเลือก" : "เปลี่ยนคน / ขยายเลือกใหม่"}
                      </Button>
                      <Button
                        size="sm"
                        color="outline-danger"
                        onClick={() => {
                          setCheckInForm((prev) => ({
                            ...prev,
                            selectedStaff: null,
                            traineeName: "",
                            positionText: "",
                            departmentName: "",
                            phone: "",
                            citizenID: "",
                            staffCode: ""
                          }));
                          setStaffSelectorOpen(true);
                        }}
                      >
                        ✕ ล้าง
                      </Button>
                    </div>
                  </div>
                )}

                {/* 2. Inline Expand/Collapse Bar for TSUser */}
                <div
                  className="d-flex justify-content-between align-items-center p-2 px-3 rounded mb-2"
                  style={{
                    backgroundColor: "#e0f2f1",
                    border: "1px solid #80cbc4",
                    cursor: "pointer",
                    userSelect: "none"
                  }}
                  onClick={() => setStaffSelectorOpen(!staffSelectorOpen)}
                >
                  <div className="font-weight-bold" style={{ color: "#00695c" }}>
                    <PeopleIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                    {checkInForm.selectedStaff
                      ? "ผังโครงสร้าง รพ. และค้นหาเจ้าหน้าที่ (เหมือนจองห้องประชุม)"
                      : "เลือกเจ้าหน้าที่จากโครงสร้าง รพ. / ค้นหาตามชื่อ (เหมือนจองห้องประชุม) *"}
                  </div>
                  <Button
                    size="sm"
                    color="link"
                    className="p-0 font-weight-bold"
                    style={{ color: "#00695c", textDecoration: "none" }}
                  >
                    {staffSelectorOpen ? "▲ กดเพื่อซ่อนผัง" : "▼ กดเพื่อขยายเลือกคน"}
                  </Button>
                </div>

                {/* 3. Embedded TSUser component inside Collapse */}
                <Collapse isOpen={staffSelectorOpen}>
                  <div
                    className="p-2 border rounded bg-white mb-2 shadow-sm"
                    style={{ maxHeight: "490px", overflowY: "auto", border: "1px solid #80cbc4" }}
                  >
                    <div className="small text-muted mb-2 px-2">
                      💡 คลิกเลือกฝ่าย/กลุ่มงานจากเมนูต้นไม้ทางซ้าย หรือพิมพ์ค้นหาตามชื่อทางขวา แล้วคลิกที่รูปภาพเพื่อเลือกเจ้าหน้าที่
                    </div>
                    <TSUser
                      isMultiple={false}
                      value={checkInForm.selectedStaff ? [checkInForm.selectedStaff] : []}
                      onChange={handleSelectStaffFromTSUser}
                    />
                  </div>
                </Collapse>

                {/* Select Room */}
            <FormGroup>
              <Label className="font-weight-bold" style={{ color: "#0f172a", fontSize: "0.95rem" }}>
                🏢 เลือกห้องพักที่จะเข้าอยู่ (แสดงจำนวนเตียงว่าง) *
              </Label>
              <Input
                type="select"
                value={checkInForm.roomId}
                onChange={(e) => {
                  const rId = Number(e.target.value);
                  const selRoom = rooms.find((r) => (r.roomID || r.RoomID) === rId);
                  const isFam = selRoom && (
                    selRoom.isFamilyRoom ||
                    selRoom.IsFamilyRoom ||
                    selRoom.roomType === "ห้องครอบครัว" ||
                    selRoom.RoomType === "ห้องครอบครัว" ||
                    Number(selRoom.dormitoryID || selRoom.DormitoryID || selRoom.buildingNo || selRoom.BuildingNo) === 3
                  );
                  setCheckInForm((prev) => ({
                    ...prev,
                    roomId: rId,
                    building: selRoom?.buildingName || selRoom?.BuildingName || `ตึก ${selRoom?.buildingNo || selRoom?.BuildingNo || ""}`,
                    floor: selRoom?.floor || selRoom?.Floor || 1,
                    roomNo: selRoom?.roomNumber || selRoom?.RoomNumber || "",
                    isFamilyStay: isFam ? true : prev.isFamilyStay
                  }));
                }}
                style={{
                  fontSize: "0.92rem",
                  fontWeight: "700",
                  color: "#0f172a",
                  borderRadius: "10px",
                  border: "2px solid #94a3b8",
                  padding: "8px 12px",
                  backgroundColor: "#ffffff"
                }}
              >
                <option value={0}>-- กรุณาเลือกห้องพักที่มีเตียงว่าง ({availableRoomOptions.length} ห้องพร้อมพัก) --</option>
                {availableRoomOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Input>
            </FormGroup>

            {/* Family Room & Co-Residents Section (ตึก 3 / IsFamilyRoom) */}
            {(() => {
              const selRoom = rooms.find((r) => r.RoomID === checkInForm.roomId);
              const isFamRoom = selRoom && (
                selRoom.IsFamilyRoom ||
                selRoom.RoomType === "ห้องครอบครัว" ||
                selRoom.RoomType === "ครอบครัว" ||
                selRoom.DormitoryID === 3 ||
                selRoom.BuildingNo === "3" ||
                selRoom.BuildingNo === 3 ||
                String(selRoom.RoomNumber) === "424"
              );
              const showFamilySection = isFamRoom || checkInForm.isFamilyStay;

              return (
                <div className="mb-3">
                  {!isFamRoom && checkInForm.roomId > 0 && (
                    <div
                      onClick={() => setCheckInForm((prev) => ({ ...prev, isFamilyStay: !prev.isFamilyStay }))}
                      style={{
                        cursor: "pointer",
                        userSelect: "none",
                        marginBottom: "12px",
                        padding: "10px 14px",
                        borderRadius: "12px",
                        backgroundColor: checkInForm.isFamilyStay ? "#fffbeb" : "#f8fafc",
                        border: checkInForm.isFamilyStay ? "2px solid #f59e0b" : "1.5px solid #e2e8f0",
                        boxShadow: checkInForm.isFamilyStay ? "0 3px 10px rgba(245, 158, 11, 0.12)" : "none",
                        transition: "all 0.2s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                        <span style={{ fontSize: "1.3rem", marginRight: "10px" }}>👨‍👩‍👧‍👦</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "0.92rem", color: checkInForm.isFamilyStay ? "#92400e" : "#1e293b" }}>
                            พักอาศัยแบบครอบครัว / มีผู้พักร่วม (Family Stay)
                          </div>
                          <div style={{ fontSize: "0.78rem", color: checkInForm.isFamilyStay ? "#b45309" : "#64748b" }}>
                            เปิดให้กรอกข้อมูลสมาชิกครอบครัว หรือเจ้าหน้าที่ร่วมพัก
                          </div>
                        </div>
                      </div>

                      {/* Switch */}
                      <div
                        style={{
                          width: "44px",
                          height: "24px",
                          borderRadius: "16px",
                          backgroundColor: checkInForm.isFamilyStay ? "#f59e0b" : "#cbd5e1",
                          position: "relative",
                          flexShrink: 0,
                          marginLeft: "12px",
                          transition: "background-color 0.25s ease"
                        }}
                      >
                        <div
                          style={{
                            width: "18px",
                            height: "18px",
                            borderRadius: "50%",
                            backgroundColor: "#ffffff",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            position: "absolute",
                            top: "3px",
                            left: checkInForm.isFamilyStay ? "23px" : "3px",
                            transition: "left 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {showFamilySection && (
                    <div
                      className="p-3 rounded"
                      style={{
                        backgroundColor: "#fff8e1",
                        border: "1.5px solid #ffe082",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between mb-2 pb-2 flex-wrap" style={{ borderBottom: "1px dashed #ffd54f" }}>
                        <div className="d-flex align-items-center mb-1 mb-md-0">
                          <span style={{ fontSize: "1.35rem", marginRight: "8px" }}>👨‍👩‍👧‍👦</span>
                          <div>
                            <strong style={{ color: "#d97706", fontSize: "0.95rem" }}>
                              {isFamRoom
                                ? (String(selRoom?.RoomNumber) === "424"
                                    ? "ห้องครอบครัวพิเศษ (ตึก 4 ชั้น 2 / ห้อง 424 - ตามมติกรรมการบ้านพัก) - ระบุว่าอยู่กับใคร"
                                    : "ห้องครอบครัว (ตึก 3 / Family Room) - ระบุว่าอยู่กับใคร")
                                : "ข้อมูลผู้พักร่วม / ครอบครัว (อยู่กับใคร)"}
                            </strong>
                            <div className="text-muted small">
                              เลือกเจ้าหน้าที่ รพ. ที่พักร่วม หรือพิมพ์ระบุสมาชิกครอบครัว (เช่น คู่สมรส, บุตร)
                            </div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          color="warning"
                          className="text-dark font-weight-bold shadow-sm"
                          onClick={() => setCoResidentSelectorOpen(!coResidentSelectorOpen)}
                        >
                          <PeopleIcon style={{ verticalAlign: "middle", marginRight: "4px" }} />
                          {coResidentSelectorOpen ? "▲ ซ่อนผังเลือกผู้พักร่วม" : "+ เลือก จนท. ผู้พักร่วม (เหมือนจองห้องประชุม)"}
                        </Button>
                      </div>

                      {/* Inline TSUser for Co-Residents inside Collapse */}
                      <Collapse isOpen={coResidentSelectorOpen}>
                        <div
                          className="p-2 border rounded bg-white my-2 shadow-sm"
                          style={{ maxHeight: "420px", overflowY: "auto", border: "1.5px solid #ffe082" }}
                        >
                          <div className="small text-muted mb-2 px-2">
                            💡 คลิกเลือกเจ้าหน้าที่ รพ. ที่จะร่วมพักอาศัยในห้องครอบครัวนี้:
                          </div>
                          <TSUser
                            isMultiple={false}
                            value={[]}
                            onChange={handleSelectCoResidentStaffFromTSUser}
                          />
                        </div>
                      </Collapse>

                      {/* List of Co-Resident Staff Picked */}
                      {checkInForm.coResidentStaff && checkInForm.coResidentStaff.length > 0 && (
                        <div className="mb-2">
                          <Label className="small font-weight-bold text-dark mb-1">
                            เจ้าหน้าที่ รพ. ที่ร่วมพักอาศัย:
                          </Label>
                          <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                            {checkInForm.coResidentStaff.map((staff) => (
                              <Badge
                                key={staff.userName || staff.employeeKey}
                                color="light"
                                className="p-2 border d-flex align-items-center"
                                style={{ backgroundColor: "#fff", borderColor: "#ffb74d", fontSize: "0.82rem" }}
                              >
                                <img
                                  src={`https://e.trangskin.go.th/avatar/${staff.userName}.jpg`}
                                  alt="avatar"
                                  style={{ width: 22, height: 22, borderRadius: "50%", marginRight: "6px", objectFit: "cover" }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://e.trangskin.go.th/avatar/default.jpg";
                                  }}
                                />
                                <span className="mr-2">
                                  {staff.prefix || ""}{staff.firstName} {staff.lastName} ({staff.positionName || staff.position || "จนท."})
                                </span>
                                <button
                                  type="button"
                                  className="close text-danger ml-1"
                                  style={{ fontSize: "16px", lineHeight: "1" }}
                                  onClick={() => handleRemoveCoResidentStaff(staff.userName)}
                                  title="ลบออก"
                                >
                                  &times;
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Additional family members text input */}
                      <FormGroup className="mb-2">
                        <Label className="small font-weight-bold text-dark mb-1">
                          ระบุสมาชิกครอบครัวเพิ่มเติม (เช่น คู่สมรส, บุตร):
                        </Label>
                        <Input
                          placeholder="เช่น คู่สมรส (นาย...), บุตร 2 คน (ด.ช...., ด.ญ....)"
                          value={checkInForm.familyNotes}
                          onChange={(e) => handleFamilyNotesChange(e.target.value)}
                        />
                      </FormGroup>

                      {/* Summary / Result of CoResidents to be saved */}
                      <FormGroup className="mb-0">
                        <Label className="small font-weight-bold text-dark mb-1">
                          สรุปข้อมูลผู้พักร่วมทั้งหมด (ที่จะบันทึกลงระบบ):
                        </Label>
                        <Input
                          type="textarea"
                          rows={2}
                          value={checkInForm.coResidents}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCheckInForm((prev) => ({ ...prev, coResidents: val }));
                          }}
                          placeholder="ระบุว่าอยู่กับใคร..."
                        />
                      </FormGroup>

                      {/* Over-capacity live warning & 1-click bed increment */}
                      {(() => {
                        const coRaw = checkInForm.coResidents || "";
                        const familyCount = coRaw ? coRaw.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean).length : 0;
                        const totalEntering = 1 + familyCount;
                        const existingOcc = selRoom?.CurrentOccupancy || 0;
                        const totalAfter = (existingOcc === 0) ? totalEntering : (existingOcc + totalEntering);
                        const roomCap = Number(selRoom?.BedCapacity || 2);
                        if (totalAfter <= roomCap) return null;

                        return (
                          <div
                            className="p-3 mt-3 rounded d-flex flex-column flex-md-row align-items-md-center justify-content-between"
                            style={{
                              backgroundColor: "#fef2f2",
                              border: "1.5px solid #ef4444",
                              color: "#991b1b"
                            }}
                          >
                            <div className="mb-2 mb-md-0 pr-2">
                              <div style={{ fontWeight: 800, fontSize: "0.95rem" }}>
                                ⚠️ จำนวนผู้พักรวม ({totalAfter} คน) เกินจำนวนเตียงของห้อง ({roomCap} เตียง)!
                              </div>
                              <div style={{ fontSize: "0.82rem", color: "#b91c1c" }}>
                                มีผู้พักหลัก 1 คน + สมาชิกครอบครัว/ผู้พักร่วม {familyCount} คน กรุณากดเพิ่มจำนวนเตียงเพื่อรองรับทุกคน
                              </div>
                            </div>
                            <Button
                              color="danger"
                              size="sm"
                              style={{ fontWeight: 800, borderRadius: "8px", whiteSpace: "nowrap" }}
                              onClick={() => handleSetRoomBedCapacity(selRoom, totalAfter)}
                            >
                              🛏️ เพิ่มจำนวนเตียงเป็น {totalAfter} เตียงทันที
                            </Button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Dates (รูปแบบ พ.ศ. ตามระบบลา) */}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="font-weight-bold">วันที่เข้าพัก (Check-in Date) (พ.ศ.) *</Label>
                  <Datetime
                    value={checkInForm.checkInDate && moment(checkInForm.checkInDate).isValid() ? moment(checkInForm.checkInDate) : null}
                    renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                    inputProps={{
                      placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                      readOnly: "readonly",
                      style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold", border: "1.5px solid #94a3b8", borderRadius: "10px" },
                      className: "form-control",
                      value: checkInForm.checkInDate && moment(checkInForm.checkInDate).isValid() ? moment(checkInForm.checkInDate).format("D MMMM BBBB") : ""
                    }}
                    closeOnSelect={true}
                    closeOnTab={true}
                    dateFormat="D MMMM BBBB"
                    timeFormat={false}
                    onChange={(val) => {
                      let dStr = "";
                      if (moment.isMoment(val) && val.isValid()) dStr = val.format("YYYY-MM-DD");
                      else if (val && typeof val === "string" && moment(val).isValid()) dStr = moment(val).format("YYYY-MM-DD");
                      setCheckInForm((prev) => ({ ...prev, checkInDate: dStr }));
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <div className="d-flex justify-content-between align-items-center">
                    <Label className="font-weight-bold">วันที่คาดว่าจะย้ายออก (ถ้ามี) (พ.ศ.)</Label>
                    {checkInForm.expectedCheckOutDate && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-danger p-0"
                        style={{ fontSize: "0.74rem", textDecoration: "underline" }}
                        onClick={() => setCheckInForm((prev) => ({ ...prev, expectedCheckOutDate: "" }))}
                      >
                        ล้างวันที่
                      </button>
                    )}
                  </div>
                  <Datetime
                    value={checkInForm.expectedCheckOutDate && moment(checkInForm.expectedCheckOutDate).isValid() ? moment(checkInForm.expectedCheckOutDate) : null}
                    renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                    inputProps={{
                      placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                      readOnly: "readonly",
                      style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold", border: "1.5px solid #94a3b8", borderRadius: "10px" },
                      className: "form-control",
                      value: checkInForm.expectedCheckOutDate && moment(checkInForm.expectedCheckOutDate).isValid() ? moment(checkInForm.expectedCheckOutDate).format("D MMMM BBBB") : ""
                    }}
                    closeOnSelect={true}
                    closeOnTab={true}
                    dateFormat="D MMMM BBBB"
                    timeFormat={false}
                    onChange={(val) => {
                      let dStr = "";
                      if (moment.isMoment(val) && val.isValid()) dStr = val.format("YYYY-MM-DD");
                      else if (val && typeof val === "string" && moment(val).isValid()) dStr = moment(val).format("YYYY-MM-DD");
                      setCheckInForm((prev) => ({ ...prev, expectedCheckOutDate: dStr }));
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="font-weight-bold">เบอร์โทรศัพท์ติดต่อ</Label>
                  <Input
                    placeholder="08X-XXX-XXXX"
                    value={checkInForm.phone}
                    onChange={(e) => setCheckInForm({ ...checkInForm, phone: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="font-weight-bold">รูปแบบค่าธรรมเนียม</Label>
                  <Input
                    type="select"
                    value={checkInForm.feeRateType}
                    onChange={(e) => setCheckInForm({ ...checkInForm, feeRateType: e.target.value })}
                  >
                    <option value="MONTHLY">รายเดือน (ตามอัตราของตึก/ห้อง)</option>
                    <option value="DAILY">รายวัน</option>
                    <option value="FREE">ฟรีสวัสดิการ (ไม่มีค่าห้อง)</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            {/* สิทธิ์หน่วยค่าไฟใช้ฟรี (สวัสดิการเจ้าหน้าที่) */}
            <div
              style={{
                backgroundColor: "#f0fdf4",
                border: "1.5px solid #86efac",
                borderRadius: "12px",
                padding: "14px 16px",
                marginBottom: "16px"
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div style={{ fontWeight: "800", color: "#166534", fontSize: "0.95rem" }}>
                  ⚡ สิทธิ์หน่วยค่าไฟใช้ฟรี (หน่วย/เดือน)
                </div>
                <span
                  style={{
                    backgroundColor: "#dcfce7",
                    color: "#15803d",
                    border: "1px solid #86efac",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    fontSize: "0.76rem",
                    fontWeight: "800"
                  }}
                >
                  สวัสดิการค่าไฟ
                </span>
              </div>
              <div className="row align-items-center">
                <div className="col-sm-5 mb-2 mb-sm-0">
                  <div className="input-group">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      className="form-control text-right"
                      style={{
                        fontWeight: "800",
                        fontSize: "1.1rem",
                        color: "#14532d",
                        border: "2px solid #86efac",
                        backgroundColor: "#ffffff",
                        borderRadius: "8px 0 0 8px"
                      }}
                      placeholder="0"
                      value={checkInForm.freeElectricityUnits ?? 0}
                      onChange={(e) => setCheckInForm({ ...checkInForm, freeElectricityUnits: Number(e.target.value) || 0 })}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text" style={{ fontWeight: "700", backgroundColor: "#dcfce7", color: "#166534", border: "2px solid #86efac", borderLeft: 0, borderRadius: "0 8px 8px 0" }}>
                        หน่วย / เดือน
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-sm-7">
                  <div style={{ fontSize: "0.82rem", color: "#374151", lineHeight: "1.4" }}>
                    💡 <b>กติกาส่วนลด:</b> หากห้องมีเจ้าหน้าที่พักตั้งแต่ 2 คนขึ้นไป ระบบจะรวมสิทธิ์ฟรีของทุกคนเข้าด้วยกันตอนคิดค่าไฟ หากยอดใช้จริงไม่เกินสิทธิ์รวม ค่าไฟจะเป็น <b>0 บาท</b> (หากเกินคิดเฉพาะส่วนต่าง)
                  </div>
                </div>
              </div>
            </div>

            <FormGroup>
              <Label className="font-weight-bold">หมายเหตุ</Label>
              <Input
                type="textarea"
                rows={2}
                placeholder="ระบุหมายเหตุเพิ่มเติม (ถ้ามี)..."
                value={checkInForm.remarks}
                onChange={(e) => setCheckInForm({ ...checkInForm, remarks: e.target.value })}
              />
            </FormGroup>
          </div>
        ) : (
          /* ================================================================= */
          /* IF STUDENT / DOCTOR: Exact UI matching Trainee Dormitory          */
          /* ================================================================= */
          <div className="row g-3">
            <div className="col-md-4 mb-2">
              <label className="form-label font-weight-bold">เลือกอาคาร / ตึก *</label>
              <select
                className="form-control"
                value={checkInForm.building}
                onChange={(e) => handleBuildingChange(e.target.value)}
              >
                <option value="ตึก 1">🏢 อาคารหอพัก 1 (ตึก 1)</option>
                <option value="ตึก 2">🏢 อาคารหอพัก 2 (ตึก 2)</option>
                <option value="ตึก 3">🏢 อาคารหอพัก 3 (ตึก 3)</option>
                <option value="ตึก 4">🏢 อาคารหอพัก 4 (ตึก 4)</option>
              </select>
            </div>

            <div className="col-md-4 mb-2">
              <label className="form-label font-weight-bold">เลือกชั้น *</label>
              <select
                className="form-control"
                value={checkInForm.floor}
                onChange={(e) => handleFloorChange(Number(e.target.value))}
              >
                <option value={1}>ชั้น 1</option>
                <option value={2}>ชั้น 2</option>
                <option value={3}>ชั้น 3</option>
              </select>
            </div>

            <div className="col-md-4 mb-2">
              <label className="form-label font-weight-bold">เลือกหมายเลขห้อง *</label>
              <select
                className="form-control"
                value={checkInForm.roomNo}
                onChange={(e) => handleRoomNoChange(e.target.value)}
                required
              >
                <option value="">-- กรุณาเลือกห้องพัก ({studentRoomsOnFloor.length} ห้อง) --</option>
                {studentRoomsOnFloor.map((r) => {
                  const rNum = getRoomNumberStr(r);
                  const rType = r.RoomType || r.roomType || "ทั่วไป";
                  const rCap = r.BedCapacity || r.bedCapacity || 2;
                  return (
                    <option key={r.RoomID || r.roomID || rNum} value={rNum}>
                      ห้อง {rNum} ({rType} - {rCap} เตียง)
                    </option>
                  );
                })}
                {checkInForm.roomNo && !studentRoomsOnFloor.some((r) => getRoomNumberStr(r) === String(checkInForm.roomNo)) && (
                  <option value={checkInForm.roomNo}>
                    ห้อง {checkInForm.roomNo} (ห้องที่เลือกไว้)
                  </option>
                )}
              </select>
            </div>

            {/* เลือกผู้เข้าพักจากฐานข้อมูล / คนที่เพิ่มไป */}
            <div className="col-md-12 mb-2">
              <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap">
                <label className="form-label font-weight-bold" style={{ color: "#00838f", fontSize: "0.95rem", margin: 0 }}>
                  🔍 เลือกรายชื่อผู้เข้าพักจากระบบ (ดึงข้อมูลอัตโนมัติ)
                </label>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setOnlyActiveOrFuture((prev) => !prev)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOnlyActiveOrFuture((prev) => !prev);
                    }
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "4px 10px",
                    borderRadius: "20px",
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.2s ease",
                    backgroundColor: onlyActiveOrFuture ? "#ecfdf5" : "#f1f5f9",
                    border: onlyActiveOrFuture ? "1.5px solid #10b981" : "1.5px solid #cbd5e1",
                    boxShadow: onlyActiveOrFuture ? "0 1px 4px rgba(16, 185, 129, 0.15)" : "none",
                    margin: 0
                  }}
                  title="คลิกเพื่อสลับการกรองรายชื่อ"
                >
                  <span
                    style={{
                      position: "relative",
                      display: "inline-block",
                      width: "32px",
                      height: "18px",
                      backgroundColor: onlyActiveOrFuture ? "#10b981" : "#cbd5e1",
                      borderRadius: "18px",
                      transition: "background-color 0.2s ease",
                      flexShrink: 0
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        top: "2px",
                        left: onlyActiveOrFuture ? "16px" : "2px",
                        width: "14px",
                        height: "14px",
                        backgroundColor: "#ffffff",
                        borderRadius: "50%",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                        transition: "left 0.2s ease"
                      }}
                    />
                  </span>
                  <input
                    type="checkbox"
                    id="filterActiveOnlyStudentView"
                    checked={onlyActiveOrFuture}
                    onChange={(e) => setOnlyActiveOrFuture(e.target.checked)}
                    style={{
                      position: "absolute",
                      opacity: 0,
                      width: 0,
                      height: 0,
                      margin: 0,
                      padding: 0,
                      pointerEvents: "none",
                      display: "none"
                    }}
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  <span
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      color: onlyActiveOrFuture ? "#047857" : "#64748b",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {onlyActiveOrFuture
                      ? "กรองเฉพาะคนที่อยู่ในช่วงเวลา & อนาคต"
                      : "แสดงทุกคน (รวมคนที่พ้นกำหนดแล้ว)"}
                  </span>
                </div>
              </div>

              <Select
                options={traineeCandidateOptions}
                value={selectedCandidateOption}
                onChange={(selected) => {
                  if (!selected) {
                    handleSelectTraineeCandidate("");
                  } else {
                    handleSelectTraineeCandidate(selected.value);
                  }
                }}
                placeholder="🔍 พิมพ์เพื่อค้นหาชื่อแพทย์/นักศึกษา, สถาบัน หรือเลือกจากรายการ..."
                isClearable
                noOptionsMessage={() => "ไม่พบรายชื่อผู้เข้าพักที่ตรงกับคำค้นหา (หรือทุกคนมีห้องพักแล้ว)"}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    backgroundColor: "#f0fdfa",
                    borderColor: state.isFocused ? "#00838f" : "#5eead4",
                    boxShadow: state.isFocused ? "0 0 0 1px #00838f" : "none",
                    fontWeight: "600",
                    fontSize: "0.9rem"
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999
                  }),
                  groupHeading: (base) => ({
                    ...base,
                    fontWeight: "bold",
                    color: "#00695c",
                    fontSize: "0.85rem"
                  }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: "0.88rem",
                    backgroundColor: state.isFocused ? "#e0f2fe" : "#ffffff",
                    color: "#1e293b",
                    cursor: "pointer"
                  })
                }}
              />
              <small className="text-info font-weight-bold d-block mt-1" style={{ fontSize: "0.78rem" }}>
                * สามารถพิมพ์ค้นหาชื่อได้ทันที และระบบซ่อนคนที่มีห้องพักอยู่แล้ว (ทั้งปัจจุบันและอนาคต) เพื่อป้องกันการจัดห้องซ้ำ
              </small>

              {/* Info summary card if candidate picked */}
              {checkInForm.selectedCandidate && (
                <div
                  className="p-2 px-3 mt-2 mb-1 rounded d-flex justify-content-between align-items-center flex-wrap"
                  style={{ backgroundColor: "#e6fffa", border: "1px solid #99f6e4", color: "#115e59", fontSize: "0.86rem" }}
                >
                  <div>
                    <strong>💡 ข้อมูลจากระบบ:</strong> {checkInForm.selectedCandidate.name} ({checkInForm.selectedCandidate.position || "-"}) | 
                    สถาบัน: {checkInForm.selectedCandidate.institution || "-"} | 
                    ช่วงเวลา: {checkInForm.selectedCandidate.dateRange || `${checkInForm.selectedCandidate.startDate || ""} ถึง ${checkInForm.selectedCandidate.endDate || ""}`}
                  </div>
                  <Button
                    size="sm"
                    color="outline-secondary"
                    className="py-0 px-2 font-weight-bold ml-auto"
                    style={{ fontSize: "0.75rem" }}
                    onClick={() => handleSelectTraineeCandidate("")}
                  >
                    ✕ ล้างการเลือก
                  </Button>
                </div>
              )}
            </div>

            <div className="col-md-6 mb-2">
              <label className="form-label font-weight-bold">
                ชื่อ-สกุล ผู้เข้าพัก *
                {selectedCandidateId && selectedCandidateId !== "CUSTOM" && (
                  <span className="badge badge-success ml-2" style={{ fontSize: "0.72rem" }}>
                    ✓ ดึงข้อมูลแล้ว
                  </span>
                )}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="เช่น พญ.เกศราภรณ์ เสาะหายิ่ง"
                value={checkInForm.traineeName}
                onChange={(e) => setCheckInForm({ ...checkInForm, traineeName: e.target.value })}
                required
              />
            </div>

            <div className="col-md-6 mb-2">
              <label className="form-label font-weight-bold">ประเภทผู้ฝึกอบรม</label>
              <select
                className="form-control"
                value={checkInForm.traineeType || "DOCTOR"}
                onChange={(e) => setCheckInForm({ ...checkInForm, traineeType: e.target.value })}
              >
                <option value="DOCTOR">👨‍⚕️ แพทย์ฝึกอบรม / ดูงาน</option>
                <option value="STUDENT">🎓 นักศึกษาฝึกงาน</option>
              </select>
            </div>

            <div className="col-md-6 mb-2">
              <label className="form-label font-weight-bold">ตำแหน่ง / สาขาวิชา</label>
              <input
                type="text"
                className="form-control"
                placeholder="เช่น นายแพทย์ปฏิบัติงาน หรือ นศ.เภสัชฯ"
                value={checkInForm.positionText}
                onChange={(e) => setCheckInForm({ ...checkInForm, positionText: e.target.value })}
              />
            </div>

            <div className="col-md-6 mb-2">
              <label className="form-label font-weight-bold">สถาบัน / ต้นสังกัด</label>
              <input
                type="text"
                className="form-control"
                placeholder="เช่น โรงพยาบาลบุรีรัมย์"
                value={checkInForm.departmentName}
                onChange={(e) => setCheckInForm({ ...checkInForm, departmentName: e.target.value })}
              />
            </div>

            {/* วันที่เริ่มเข้าพัก และ วันที่สิ้นสุด (พ.ศ.) */}
            <div className="col-md-6 mb-2">
              <div className="d-flex justify-content-between align-items-center">
                <label className="form-label font-weight-bold">
                  วันที่เริ่มเข้าพัก * (พ.ศ.)
                </label>
                {checkInForm.checkInDate && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger p-0"
                    style={{ fontSize: "0.74rem", textDecoration: "underline" }}
                    onClick={() => {
                      const autoRange = formatThaiDateRangeText("", checkInForm.expectedCheckOutDate);
                      setCheckInForm({ ...checkInForm, checkInDate: "", dateRange: autoRange });
                    }}
                  >
                    ล้างวันที่
                  </button>
                )}
              </div>
              <Datetime
                value={checkInForm.checkInDate && moment(checkInForm.checkInDate).isValid() ? moment(checkInForm.checkInDate) : null}
                renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                inputProps={{
                  placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                  readOnly: "readonly",
                  style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold" },
                  className: "form-control",
                  value: checkInForm.checkInDate && moment(checkInForm.checkInDate).isValid() ? moment(checkInForm.checkInDate).format("D MMMM BBBB") : ""
                }}
                closeOnSelect={true}
                closeOnTab={true}
                dateFormat="D MMMM BBBB"
                timeFormat={false}
                onChange={(val) => {
                  let dStr = "";
                  if (moment.isMoment(val) && val.isValid()) dStr = val.format("YYYY-MM-DD");
                  else if (val && typeof val === "string" && moment(val).isValid()) dStr = moment(val).format("YYYY-MM-DD");
                  const autoRange = formatThaiDateRangeText(dStr, checkInForm.expectedCheckOutDate);
                  setCheckInForm({ ...checkInForm, checkInDate: dStr, dateRange: autoRange });
                }}
              />
            </div>

            <div className="col-md-6 mb-2">
              <div className="d-flex justify-content-between align-items-center">
                <label className="form-label font-weight-bold">
                  วันที่สิ้นสุดการเข้าพัก (พ.ศ.)
                </label>
                {checkInForm.expectedCheckOutDate && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger p-0"
                    style={{ fontSize: "0.74rem", textDecoration: "underline" }}
                    onClick={() => {
                      const autoRange = formatThaiDateRangeText(checkInForm.checkInDate, "");
                      setCheckInForm({ ...checkInForm, expectedCheckOutDate: "", dateRange: autoRange });
                    }}
                  >
                    ล้างวันที่
                  </button>
                )}
              </div>
              <Datetime
                value={checkInForm.expectedCheckOutDate && moment(checkInForm.expectedCheckOutDate).isValid() ? moment(checkInForm.expectedCheckOutDate) : null}
                renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                inputProps={{
                  placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                  readOnly: "readonly",
                  style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold" },
                  className: "form-control",
                  value: checkInForm.expectedCheckOutDate && moment(checkInForm.expectedCheckOutDate).isValid() ? moment(checkInForm.expectedCheckOutDate).format("D MMMM BBBB") : ""
                }}
                closeOnSelect={true}
                closeOnTab={true}
                dateFormat="D MMMM BBBB"
                timeFormat={false}
                onChange={(val) => {
                  let dStr = "";
                  if (moment.isMoment(val) && val.isValid()) dStr = val.format("YYYY-MM-DD");
                  else if (val && typeof val === "string" && moment(val).isValid()) dStr = moment(val).format("YYYY-MM-DD");
                  const autoRange = formatThaiDateRangeText(checkInForm.checkInDate, dStr);
                  setCheckInForm({ ...checkInForm, expectedCheckOutDate: dStr, dateRange: autoRange });
                }}
              />
            </div>

            <div className="col-md-12 mb-2">
              <label className="form-label font-weight-bold" style={{ fontSize: "0.85rem", color: "#475569" }}>
                ช่วงเวลาเข้าพัก (จัดรูปแบบข้อความอัตโนมัติ)
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="เช่น 3 พ.ย. - 16 พ.ย. 2568"
                value={checkInForm.dateRange}
                onChange={(e) => setCheckInForm({ ...checkInForm, dateRange: e.target.value })}
              />
              <small className="text-muted" style={{ fontSize: "0.78rem" }}>
                * ระบบจัดข้อความ พ.ศ. ให้อัตโนมัติตามวันที่เลือก หรือปรับแต่งข้อความเพิ่มเติมได้
              </small>
            </div>

            {/* แจ้งเตือนเมื่อเป็นการเพิ่มคนเข้าพักในอนาคต (ไม่ใช่ตอนนี้) */}
            {isStayFuture(checkInForm) && (
              <div className="col-md-12 mb-2">
                <div
                  style={{
                    backgroundColor: "#f0f9ff",
                    border: "1px solid #7dd3fc",
                    borderRadius: "6px",
                    padding: "10px 14px",
                    fontSize: "0.85rem",
                    color: "#0369a1",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}
                >
                  <span style={{ fontSize: "1.3rem" }}>⏳</span>
                  <div>
                    <strong>ผู้เข้าพักนี้เป็นช่วงเวลาในอนาคต (ไม่ใช่ตอนนี้):</strong>
                    <div style={{ fontSize: "0.8rem", color: "#0c4a6e", marginTop: "2px" }}>
                      ระบบอนุญาตให้ลงทะเบียนจองห้องพักได้ตามปกติ และจะยังไม่นับรวมในยอดผู้เข้าพักปัจจุบันของห้องพัก จนกว่าจะถึงช่วงวันเวลาที่เข้าพักจริง
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* กล่องแจ้งเตือนสถานะเตียงและความจุเตียง / ช่วงเวลาที่ชนกัน */}
            {checkInForm.roomNo && (
              <div className="col-md-12 mb-2">
                {overlappingOccupantsInfo.isFull ? (
                  <div
                    style={{
                      backgroundColor: "#fef2f2",
                      border: "2px solid #ef4444",
                      borderRadius: "8px",
                      padding: "14px 16px",
                      boxShadow: "0 2px 6px rgba(239, 68, 68, 0.12)"
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                      <div>
                        <div style={{ color: "#991b1b", fontWeight: "bold", fontSize: "1rem" }}>
                          ⛔ ห้อง {checkInForm.roomNo} ({checkInForm.building}) เตียงเต็มแล้ว! ({overlappingOccupantsInfo.count}/{overlappingOccupantsInfo.capacity} เตียง)
                        </div>
                        <div style={{ color: "#b91c1c", fontSize: "0.85rem", marginTop: "2px" }}>
                          ช่วงเวลาเข้าพักนี้ ชนกับผู้พักเดิมที่อยู่ในห้องนี้แล้ว {overlappingOccupantsInfo.count} คน (เต็มตามจำนวนเตียงที่กำหนด)
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-danger font-weight-bold"
                          style={{ fontSize: "0.82rem", padding: "5px 12px", borderRadius: "6px" }}
                          onClick={() => currentSelectedRoom && handleQuickAddOneBed(currentSelectedRoom)}
                          title="เพิ่มเตียงให้อีก 1 เตียงทันที"
                        >
                          ➕ เพิ่ม 1 เตียงทันที (เป็น {overlappingOccupantsInfo.capacity + 1} เตียง)
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger font-weight-bold"
                          style={{ fontSize: "0.82rem", padding: "5px 12px", borderRadius: "6px", backgroundColor: "#ffffff" }}
                          onClick={() => currentSelectedRoom && handleOpenManageBeds(currentSelectedRoom)}
                        >
                          🛏️ Manage จัดการเตียง...
                        </button>
                      </div>
                    </div>

                    <div style={{ fontSize: "0.84rem", color: "#7f1d1d", fontWeight: "bold", marginBottom: "4px" }}>
                      รายชื่อผู้พักที่ช่วงเวลาซ้อนทับกัน ({overlappingOccupantsInfo.count} คน):
                    </div>
                    <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "0.83rem", color: "#991b1b" }}>
                      {overlappingOccupantsInfo.overlaps.map((s) => (
                        <li key={s.id || s.StayID || s.stayID} style={{ marginBottom: "2px" }}>
                          <strong>{s.traineeName || s.TraineeName}</strong> — {s.institution || s.departmentName || s.DepartmentName || s.position || s.PositionText || "-"} (ช่วงวันที่: {s.stayDateRangeText || s.dateRange || `${s.checkInDate || s.CheckInDate} ถึง ${s.expectedCheckOutDate || s.checkOutDate || s.CheckOutDate}`})
                        </li>
                      ))}
                    </ul>
                    <div style={{ marginTop: "8px", fontSize: "0.8rem", color: "#dc2626", fontWeight: "600" }}>
                      ⚠️ ไม่สามารถเพิ่มคนที่ {overlappingOccupantsInfo.count + 1} ได้ เนื่องจากเตียงเต็ม จะเพิ่มได้เมื่อกดปุ่ม "Manage จัดการเตียงเพิ่ม" ด้านบน หรือเลือกห้องพักอื่น
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #86efac",
                      borderRadius: "6px",
                      padding: "8px 14px",
                      fontSize: "0.86rem",
                      color: "#166534",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "8px"
                    }}
                  >
                    <div>
                      <strong>🟢 สถานะเตียงห้อง {checkInForm.roomNo}:</strong> ว่างพร้อมจัดเข้าพัก ({overlappingOccupantsInfo.count}/{overlappingOccupantsInfo.capacity} เตียง)
                      {overlappingOccupantsInfo.count > 0 && (
                        <span style={{ fontSize: "0.8rem", color: "#15803d", marginLeft: "6px" }}>
                          (มีผู้พักร่วมในช่วงเวลานี้: {overlappingOccupantsInfo.overlaps.map((o) => o.traineeName || o.TraineeName).join(", ")})
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-success font-weight-bold"
                      style={{ fontSize: "0.8rem", textDecoration: "underline" }}
                      onClick={() => currentSelectedRoom && handleOpenManageBeds(currentSelectedRoom)}
                    >
                      🛏️ จัดการเตียง ({overlappingOccupantsInfo.capacity} เตียง)
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="col-md-4 mb-2">
              <label className="form-label font-weight-bold" style={{ color: "#1e293b" }}>
                อัตราค่าหอพัก (คำนวณตาม Master)
              </label>
              <div
                style={{
                  backgroundColor: "#f8fafc",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  padding: "7px 12px",
                  fontSize: "0.88rem",
                  fontWeight: "bold",
                  color: "#0f766e",
                  display: "flex",
                  alignItems: "center",
                  minHeight: "38px"
                }}
                title="ระบบคิดราคาตาม Master Data: คิดอัตราต่อคน หากอยู่คนเดียวคิด 500 บาท/คน"
              >
                💰 {autoMasterFeeInfo.text}
              </div>
              <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                * คิดอัตราต่อคน (หากอยู่คนเดียวก็คิด 500 บาท/คน แยกตามผู้เข้าพัก)
              </small>
            </div>

            <div className="col-md-4 mb-2">
              <label className="form-label font-weight-bold">สถานะการชำระเงิน</label>
              <select
                className="form-control"
                value={checkInForm.paymentStatus || "PAID"}
                onChange={(e) => setCheckInForm({ ...checkInForm, paymentStatus: e.target.value })}
              >
                <option value="PAID">✅ ชำระเรียบร้อยแล้ว (PAID)</option>
                <option value="PENDING">⏳ รอชำระ (PENDING)</option>
                <option value="PARTIAL">⚠️ ชำระบางส่วน / จ่ายไม่ครบ (PARTIAL)</option>
                <option value="OVERDUE">❌ ค้างชำระ (OVERDUE)</option>
                <option value="FREE">🆓 ฟรี / ยกเว้นค่าธรรมเนียม (FREE)</option>
              </select>
            </div>

            <div className="col-md-4 mb-2">
              <label className="form-label">เบอร์โทรศัพท์ติดต่อ</label>
              <input
                type="text"
                className="form-control"
                placeholder="เช่น 089-111-2233"
                value={checkInForm.phone}
                onChange={(e) => setCheckInForm({ ...checkInForm, phone: e.target.value })}
              />
            </div>

            {/* สิทธิ์หน่วยค่าไฟใช้ฟรี */}
            <div className="col-md-12 mb-2">
              <div
                style={{
                  backgroundColor: "#f0fdf4",
                  border: "1.5px solid #86efac",
                  borderRadius: "12px",
                  padding: "14px 16px"
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div style={{ fontWeight: "800", color: "#166534", fontSize: "0.95rem" }}>
                    ⚡ สิทธิ์หน่วยค่าไฟใช้ฟรี (หน่วย/เดือน)
                  </div>
                  <span
                    style={{
                      backgroundColor: "#dcfce7",
                      color: "#15803d",
                      border: "1px solid #86efac",
                      padding: "2px 8px",
                      borderRadius: "6px",
                      fontSize: "0.76rem",
                      fontWeight: "800"
                    }}
                  >
                    สวัสดิการค่าไฟ
                  </span>
                </div>
                <div className="row align-items-center">
                  <div className="col-sm-5 mb-2 mb-sm-0">
                    <div className="input-group">
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        className="form-control text-right"
                        style={{
                          fontWeight: "800",
                          fontSize: "1.1rem",
                          color: "#14532d",
                          border: "2px solid #86efac",
                          backgroundColor: "#ffffff",
                          borderRadius: "8px 0 0 8px"
                        }}
                        placeholder="0"
                        value={checkInForm.freeElectricityUnits ?? 0}
                        onChange={(e) => setCheckInForm({ ...checkInForm, freeElectricityUnits: Number(e.target.value) || 0 })}
                      />
                      <div className="input-group-append">
                        <span className="input-group-text" style={{ fontWeight: "700", backgroundColor: "#dcfce7", color: "#166534", border: "2px solid #86efac", borderLeft: 0, borderRadius: "0 8px 8px 0" }}>
                          หน่วย / เดือน
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-7">
                    <div style={{ fontSize: "0.82rem", color: "#374151", lineHeight: "1.4" }}>
                      💡 <b>กติกาส่วนลด:</b> หากห้องมีผู้พักตั้งแต่ 2 คนขึ้นไป ระบบจะรวมสิทธิ์ฟรีของทุกคนเข้าด้วยกันตอนคิดค่าไฟ หากยอดใช้จริงไม่เกินสิทธิ์รวม ค่าไฟจะเป็น <b>0 บาท</b>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-12 mb-2">
              <label className="form-label">หมายเหตุการพัก</label>
              <input
                type="text"
                className="form-control"
                placeholder="เช่น ย้ายห้อง, เลื่อนวัน"
                value={checkInForm.remarks}
                onChange={(e) => setCheckInForm({ ...checkInForm, remarks: e.target.value })}
              />
            </div>
          </div>
        )}

            <div className="text-right pt-3 border-top mt-3">
              <Button
                color="secondary"
                className="mr-2"
                onClick={() => {
                  setSelectedCandidateId("");
                  setCheckInModal({ open: false, defaultRoomId: 0 });
                }}
              >
                ยกเลิก
              </Button>
              <Button
                color={checkInForm.residentCategory === "STUDENT" && overlappingOccupantsInfo.isFull ? "danger" : "primary"}
                type="submit"
                disabled={checkInForm.residentCategory === "STUDENT" && overlappingOccupantsInfo.isFull}
                style={{ fontWeight: "bold" }}
              >
                {checkInForm.residentCategory === "STUDENT" && overlappingOccupantsInfo.isFull
                  ? "⛔ เตียงเต็ม (ไม่สามารถบันทึกได้)"
                  : checkInForm.residentCategory === "STUDENT"
                  ? "บันทึกข้อมูลการเข้าพัก"
                  : "บันทึกการจัดคนเข้าพัก"}
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      {/* ========================================================================= */}
      {/* MODAL: EDIT STAY DETAILS (แก้ไขข้อมูลการเข้าพัก)                          */}
      {/* ========================================================================= */}
      <Modal
        isOpen={editStayModal.open}
        size="lg"
        toggle={() => setEditStayModal((prev) => ({ ...prev, open: false }))}
      >
        <ModalHeader toggle={() => setEditStayModal((prev) => ({ ...prev, open: false }))}>
          <div className="d-flex align-items-center" style={{ gap: "8px" }}>
            <span style={{ fontSize: "1.2rem" }}>✏️</span>
            <span className="font-weight-bold" style={{ color: "#0f172a" }}>แก้ไขข้อมูลการเข้าพัก</span>
          </div>
        </ModalHeader>
        <ModalBody>
          {editStayModal.open && (
            <form onSubmit={handleEditStaySubmit}>
              {/* Resident & Room Header Card */}
              <div
                className="p-3 rounded mb-3 d-flex align-items-center justify-content-between flex-wrap"
                style={{
                  backgroundColor: "#f8fafc",
                  border: "1.5px solid #e2e8f0"
                }}
              >
                <div>
                  <div className="h5 font-weight-bold text-dark mb-1">
                    👤 {editStayModal.residentName}
                  </div>
                  <div className="text-muted small">
                    {editStayModal.positionText && (
                      <span className="badge badge-light border mr-2" style={{ fontSize: "0.78rem" }}>
                        {editStayModal.positionText}
                      </span>
                    )}
                    {editStayModal.departmentName && (
                      <span className="text-muted mr-2">สังกัด: {editStayModal.departmentName}</span>
                    )}
                  </div>
                </div>
                <div className="text-right mt-2 mt-md-0">
                  <span
                    className="badge badge-primary px-3 py-2"
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: "800",
                      borderRadius: "8px",
                      backgroundColor: "#1e293b"
                    }}
                  >
                    🏢 {editStayModal.buildingName || "อาคารหอพัก"} • ห้อง {editStayModal.roomNumber}
                  </span>
                </div>
              </div>

              {/* Dates Row (รูปแบบ พ.ศ. ตามระบบ) */}
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label className="font-weight-bold">วันที่เข้าพัก (Check-in Date) (พ.ศ.) *</Label>
                    <Datetime
                      value={editStayModal.checkInDate && moment(editStayModal.checkInDate).isValid() ? moment(editStayModal.checkInDate) : null}
                      renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                      inputProps={{
                        placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                        readOnly: "readonly",
                        style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold", border: "1.5px solid #94a3b8", borderRadius: "10px" },
                        className: "form-control",
                        value: editStayModal.checkInDate && moment(editStayModal.checkInDate).isValid() ? moment(editStayModal.checkInDate).format("D MMMM BBBB") : ""
                      }}
                      closeOnSelect={true}
                      closeOnTab={true}
                      dateFormat="D MMMM BBBB"
                      timeFormat={false}
                      onChange={(val) => {
                        let dStr = "";
                        if (moment.isMoment(val) && val.isValid()) dStr = val.format("YYYY-MM-DD");
                        else if (val && typeof val === "string" && moment(val).isValid()) dStr = moment(val).format("YYYY-MM-DD");
                        setEditStayModal((prev) => ({ ...prev, checkInDate: dStr }));
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <div className="d-flex justify-content-between align-items-center">
                      <Label className="font-weight-bold">วันที่คาดว่าจะย้ายออก (ถ้ามี) (พ.ศ.)</Label>
                      {editStayModal.expectedCheckOutDate && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger p-0"
                          style={{ fontSize: "0.74rem", textDecoration: "underline" }}
                          onClick={() => setEditStayModal((prev) => ({ ...prev, expectedCheckOutDate: "" }))}
                        >
                          ล้างวันที่
                        </button>
                      )}
                    </div>
                    <Datetime
                      value={editStayModal.expectedCheckOutDate && moment(editStayModal.expectedCheckOutDate).isValid() ? moment(editStayModal.expectedCheckOutDate) : null}
                      renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                      inputProps={{
                        placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                        readOnly: "readonly",
                        style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold", border: "1.5px solid #94a3b8", borderRadius: "10px" },
                        className: "form-control",
                        value: editStayModal.expectedCheckOutDate && moment(editStayModal.expectedCheckOutDate).isValid() ? moment(editStayModal.expectedCheckOutDate).format("D MMMM BBBB") : ""
                      }}
                      closeOnSelect={true}
                      closeOnTab={true}
                      dateFormat="D MMMM BBBB"
                      timeFormat={false}
                      onChange={(val) => {
                        let dStr = "";
                        if (moment.isMoment(val) && val.isValid()) dStr = val.format("YYYY-MM-DD");
                        else if (val && typeof val === "string" && moment(val).isValid()) dStr = moment(val).format("YYYY-MM-DD");
                        setEditStayModal((prev) => ({ ...prev, expectedCheckOutDate: dStr }));
                      }}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Phone & Fee Type Row */}
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label className="font-weight-bold">เบอร์โทรศัพท์ติดต่อ</Label>
                    <Input
                      placeholder="08X-XXX-XXXX"
                      value={editStayModal.phone}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditStayModal((prev) => ({ ...prev, phone: val }));
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label className="font-weight-bold">รูปแบบค่าธรรมเนียม</Label>
                    <Input
                      type="select"
                      value={editStayModal.feeRateType}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditStayModal((prev) => ({ ...prev, feeRateType: val }));
                      }}
                    >
                      <option value="MONTHLY">รายเดือน (ตามอัตราของตึก/ห้อง)</option>
                      <option value="DAILY">รายวัน</option>
                      <option value="FREE">ฟรีสวัสดิการ (ไม่มีค่าห้อง)</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              {/* สิทธิ์หน่วยค่าไฟใช้ฟรี (สวัสดิการเจ้าหน้าที่) */}
              <div
                style={{
                  backgroundColor: "#f0fdf4",
                  border: "1.5px solid #86efac",
                  borderRadius: "12px",
                  padding: "14px 16px",
                  marginBottom: "16px"
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div style={{ fontWeight: "800", color: "#166534", fontSize: "0.95rem" }}>
                    ⚡ สิทธิ์หน่วยค่าไฟใช้ฟรี (หน่วย/เดือน)
                  </div>
                  <span
                    style={{
                      backgroundColor: "#dcfce7",
                      color: "#15803d",
                      border: "1px solid #86efac",
                      padding: "2px 8px",
                      borderRadius: "6px",
                      fontSize: "0.76rem",
                      fontWeight: "800"
                    }}
                  >
                    สวัสดิการค่าไฟ
                  </span>
                </div>
                <div className="row align-items-center">
                  <div className="col-sm-5 mb-2 mb-sm-0">
                    <div className="input-group">
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        className="form-control text-right"
                        style={{
                          fontWeight: "800",
                          fontSize: "1.1rem",
                          color: "#14532d",
                          border: "2px solid #86efac",
                          backgroundColor: "#ffffff",
                          borderRadius: "8px 0 0 8px"
                        }}
                        placeholder="0"
                        value={editStayModal.freeElectricityUnits ?? 0}
                        onChange={(e) => {
                          const val = Number(e.target.value) || 0;
                          setEditStayModal((prev) => ({ ...prev, freeElectricityUnits: val }));
                        }}
                      />
                      <div className="input-group-append">
                        <span className="input-group-text" style={{ fontWeight: "700", backgroundColor: "#dcfce7", color: "#166534", border: "2px solid #86efac", borderLeft: 0, borderRadius: "0 8px 8px 0" }}>
                          หน่วย / เดือน
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-7">
                    <div style={{ fontSize: "0.82rem", color: "#374151", lineHeight: "1.4" }}>
                      💡 <b>กติกาส่วนลด:</b> หากห้องมีเจ้าหน้าที่พักตั้งแต่ 2 คนขึ้นไป ระบบจะรวมสิทธิ์ฟรีของทุกคนเข้าด้วยกันตอนคิดค่าไฟ หากยอดใช้จริงไม่เกินสิทธิ์รวม ค่าไฟจะเป็น <b>0 บาท</b> (หากเกินคิดเฉพาะส่วนต่าง)
                    </div>
                  </div>
                </div>
              </div>

              {/* Co-Residents / Family Members */}
              <FormGroup>
                <Label className="font-weight-bold">
                  ข้อมูลผู้พักร่วม / สมาชิกครอบครัว (ถ้ามี)
                </Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={editStayModal.coResidents}
                  placeholder="เช่น คู่สมรส (นาย...), บุตร 2 คน (ด.ช...., ด.ญ....)"
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditStayModal((prev) => ({ ...prev, coResidents: val }));
                  }}
                />
                <small className="text-muted">
                  ใส่ชื่อสมาชิกครอบครัวคั่นด้วยเครื่องหมายจุลภาค (,) หรือขึ้นบรรทัดใหม่ ระบบจะนำไปแสดงในรายการผู้พักและหน้าต่างห้องพัก
                </small>
              </FormGroup>

              {/* Remarks */}
              <FormGroup>
                <Label className="font-weight-bold">หมายเหตุ</Label>
                <Input
                  type="textarea"
                  rows={2}
                  placeholder="ระบุหมายเหตุเพิ่มเติม (ถ้ามี)..."
                  value={editStayModal.remarks}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditStayModal((prev) => ({ ...prev, remarks: val }));
                  }}
                />
              </FormGroup>

              {/* Action Buttons */}
              <div className="text-right pt-3 border-top d-flex justify-content-end" style={{ gap: "8px" }}>
                <Button
                  color="secondary"
                  onClick={() => setEditStayModal((prev) => ({ ...prev, open: false }))}
                  disabled={editStayModal.isSubmitting}
                >
                  ยกเลิก
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  disabled={editStayModal.isSubmitting}
                  style={{ fontWeight: "700", minWidth: "140px" }}
                >
                  {editStayModal.isSubmitting ? "⏳ กำลังบันทึก..." : "💾 บันทึกการแก้ไข"}
                </Button>
              </div>
            </form>
          )}
        </ModalBody>
      </Modal>

      {/* ========================================================================= */}
      {/* MODAL: CHECK-OUT (แจ้งย้ายออก)                                            */}
      {/* ========================================================================= */}
      <Modal isOpen={checkOutModal.open} toggle={() => setCheckOutModal({ open: false, stay: null })}>
        <ModalHeader toggle={() => setCheckOutModal({ open: false, stay: null })}>
          แจ้งย้ายออกจากห้องพัก (Check-out)
        </ModalHeader>
        <ModalBody>
          {checkOutModal.stay && (
            <form onSubmit={handleCheckOutSubmit}>
              <div className="p-3 bg-light rounded mb-3">
                <div className="h5 font-weight-bold text-dark mb-1">{checkOutModal.stay.TraineeName}</div>
                <div className="text-muted small">
                  ห้อง {checkOutModal.stay.RoomNumber} • เข้าพักเมื่อ:{" "}
                  {moment(checkOutModal.stay.CheckInDate).format("D MMMM BBBB")}
                </div>
              </div>

              <FormGroup>
                <Label className="font-weight-bold">วันที่ย้ายออกจริง (พ.ศ.) *</Label>
                <Datetime
                  value={checkOutForm.checkOutDate && moment(checkOutForm.checkOutDate).isValid() ? moment(checkOutForm.checkOutDate) : null}
                  renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                  inputProps={{
                    placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                    readOnly: "readonly",
                    style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold", border: "1.5px solid #94a3b8", borderRadius: "10px" },
                    className: "form-control",
                    value: checkOutForm.checkOutDate && moment(checkOutForm.checkOutDate).isValid() ? moment(checkOutForm.checkOutDate).format("D MMMM BBBB") : ""
                  }}
                  closeOnSelect={true}
                  closeOnTab={true}
                  dateFormat="D MMMM BBBB"
                  timeFormat={false}
                  onChange={(val) => {
                    let dStr = "";
                    if (moment.isMoment(val) && val.isValid()) dStr = val.format("YYYY-MM-DD");
                    else if (val && typeof val === "string" && moment(val).isValid()) dStr = moment(val).format("YYYY-MM-DD");
                    setCheckOutForm((prev) => ({ ...prev, checkOutDate: dStr }));
                  }}
                />
              </FormGroup>

              <FormGroup>
                <Label className="font-weight-bold">หมายเหตุการย้ายออก</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={checkOutForm.remarks}
                  onChange={(e) => setCheckOutForm({ ...checkOutForm, remarks: e.target.value })}
                />
                <small className="text-muted">เช่น สภาพห้องเรียบร้อย, ตรวจมิเตอร์แล้ว, คืนกุญแจแล้ว</small>
              </FormGroup>

              <div className="text-right pt-3 border-top">
                <Button color="secondary" className="mr-2" onClick={() => setCheckOutModal({ open: false, stay: null })}>
                  ยกเลิก
                </Button>
                <Button color="danger" type="submit">
                  ยืนยันการย้ายออก
                </Button>
              </div>
            </form>
          )}
        </ModalBody>
      </Modal>

      {/* ========================================================================= */}
      {/* MODAL: PRINT ELECTRICITY BILL                                             */}
      {/* ========================================================================= */}
      <Modal isOpen={billPrintModal.open} size="md" toggle={() => setBillPrintModal({ open: false, bill: null })}>
        <ModalHeader toggle={() => setBillPrintModal({ open: false, bill: null })}>
          ใบแจ้งค่ากระแสไฟฟ้าหอพัก
        </ModalHeader>
        <ModalBody>
          {billPrintModal.bill && (
            <div id="print-bill-area" className="p-3 border rounded bg-white text-dark">
              <div className="text-center mb-3">
                <h5 className="font-weight-bold mb-1">โรงพยาบาลโรคผิวหนังเขตร้อนภาคใต้ จังหวัดตรัง</h5>
                <h6 className="font-weight-bold text-muted mb-0">ใบแจ้งค่ากระแสไฟฟ้าหอพัก</h6>
                <div className="small text-muted">
                  ประจำเดือน {billPrintModal.bill.monthName} พ.ศ. {billPrintModal.bill.yearTH}
                </div>
              </div>
              <hr />

              <Row className="mb-2">
                <Col xs={6}>
                  <strong>ห้องพัก:</strong> ห้อง {billPrintModal.bill.roomNumber}
                </Col>
                <Col xs={6} className="text-right">
                  <strong>อาคาร:</strong> {billPrintModal.bill.buildingName}
                </Col>
              </Row>
              <div className="mb-3">
                <strong>ผู้พักอาศัย:</strong> {billPrintModal.bill.residentName}
              </div>

              <Table bordered size="sm" className="mb-3 text-center">
                <thead className="bg-light">
                  <tr>
                    <th>มิเตอร์ก่อนวัด</th>
                    <th>มิเตอร์ขณะวัด</th>
                    <th>หน่วยที่ใช้จริง</th>
                    <th>สิทธิ์ใช้ไฟฟรี</th>
                    <th>หน่วยสุทธิคิดเงิน</th>
                    <th>อัตรา/หน่วย</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{billPrintModal.bill.previousMeter}</td>
                    <td>{billPrintModal.bill.currentMeter}</td>
                    <td className="font-weight-bold">{billPrintModal.bill.totalUnits}</td>
                    <td className="font-weight-bold text-success">
                      {(Number(billPrintModal.bill.discountUnits) || 0) > 0
                        ? `${billPrintModal.bill.discountUnits} หน่วย`
                        : "0 หน่วย"}
                    </td>
                    <td className="font-weight-bold text-primary">
                      {billPrintModal.bill.netUnits !== undefined
                        ? billPrintModal.bill.netUnits
                        : Math.max(0, (billPrintModal.bill.totalUnits || 0) - (billPrintModal.bill.discountUnits || 0))}
                    </td>
                    <td>{billPrintModal.bill.ratePerUnit} ฿</td>
                  </tr>
                </tbody>
              </Table>

              <div className="d-flex justify-content-between h5 font-weight-bold p-2 bg-light rounded align-items-center">
                <span>รวมเป็นเงินทั้งสิ้น:</span>
                <span className={billPrintModal.bill.totalAmount === 0 ? "text-success" : "text-primary"}>
                  {billPrintModal.bill.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })} บาท
                  {billPrintModal.bill.totalAmount === 0 && (
                    <small className="ml-2 badge badge-success" style={{ fontSize: "0.75rem", verticalAlign: "middle" }}>
                      อยู่ในสิทธิ์ใช้ไฟฟรี
                    </small>
                  )}
                </span>
              </div>

              <div className="mt-4 pt-3 d-flex justify-content-around text-center small text-muted">
                <div>
                  <br /><br />
                  (........................................)<br />
                  ผู้จดมิเตอร์
                </div>
                <div>
                  <br /><br />
                  (........................................)<br />
                  ผู้รับเงิน / เจ้าหน้าที่
                </div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setBillPrintModal({ open: false, bill: null })}>
            ปิด
          </Button>
          <Button color="primary" onClick={() => window.print()}>
            <PrintIcon style={{ fontSize: "16px", marginRight: "4px" }} />
            พิมพ์เอกสาร (Print)
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
